import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useSidebar } from '../contexts/SidebarContext';
import { detectGPU } from '../utils/photorealistic/gpuDetection';
import { processImage } from '../utils/photorealistic/inferenceEngine';
import {
  MAX_CANVAS_SIZE,
  MODEL_PATH,
  aiInferenceDone,
  DRAFT_AUTOSAVE_DELAY_MS,
} from '../utils/photorealistic/photorealisticConstants';
import { polygonFromMergeResult, normalizePolygonToRings, itemToInteriorFormat } from '../utils/photorealistic/polygonFormat';
import {
  getInteriorNumericConfidence,
  isLowConfidenceInterior,
  getInteriorStableKey,
  getLabelForInterior,
  groupObjectList,
  groupPopupListByLabel,
  getInteriorIdFromSaved,
  buildPreselectedSetsFromPolygons,
  isInteriorTypeDeletionBlocked,
  INTERIOR_TYPE_DELETION_BLOCKED_MESSAGE,
} from '../utils/photorealistic/interiorListHelpers';
import { serializeDraftState, isDraftPayloadValid } from '../utils/photorealistic/photorealisticDraft';
import {
  loadCuratedModelsForRoom,
  saveCuratedModelsForRoom,
} from '../utils/photorealistic/curatedModelsStorage';
import {
  CURATED_MODEL_DRAG_MIME,
  loadImageAspectRatio,
  loadPlacedCuratedModelsForRoom,
  normalizePlacedCuratedModels,
  parseCuratedModelDragPayload,
  savePlacedCuratedModelsForRoom,
} from '../utils/photorealistic/placedCuratedModelsStorage';
import { combineCanvasWithCuratedModels, canvasToPngFile } from '../utils/photorealistic/combineCanvasWithCuratedModels';
import { submitPhotorealisticImagePrompt } from '../utils/photorealistic/photorealisticPromptInference';
import { runGenerateMaskForRoomImage } from '../utils/photorealistic/generateMaskInference';
import {
  buildRenderHistoryRequestBody,
  DEFAULT_RENDER_PREVIEW_RESOLUTION_ID,
  extractRenderSettingsFromLocalRenderItem,
  formatRenderCameraLabel,
  RENDER_LIGHTING_OPTIONS,
  RENDER_RESOLUTION_OPTIONS,
  submitRoomImageRender,
  submitRoomImageRenderPreview,
} from '../utils/photorealistic/roomImageRenderInference';
import {
  enrichRenderHistoryItems,
  enrichLocalRenderResults,
  MAX_CONCURRENT_ROOM_IMAGE_RENDER_TASKS,
  SERVER_RENDER_HISTORY_SAVE_LIMIT,
  sortRenderHistoryNewestFirst,
} from '../utils/photorealistic/renderHistoryDisplay';
import {
  clampFlythroughRoi,
  FLYTHROUGH_DURATION_DEFAULT_SECONDS,
  FLYTHROUGH_MOTION_MODES,
  resolveFlythroughDurationForCameraAngleSequence,
  resolveFlythroughFinalDurationSeconds,
  FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_PREVIEW_DISABLED_TITLE,
  isFlythroughPreviewAllowedForCameraAngleSequence,
  FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT,
  FLYTHROUGH_ROI_DEFAULT,
  FLYTHROUGH_ROI_FULL_SCREEN,
  formatFlythroughRoiLabel,
  getFlythroughCameraPresetLabel,
  getFlythroughFrameRateLabel,
  getFlythroughResolutionLabel,
  MAX_CONCURRENT_FLYTHROUGH_VIDEO_TASKS,
} from '../utils/photorealistic/flythroughVideoConfig';
import { FLYTHROUGH_CANONICAL_CAMERA_ANGLE } from '../utils/photorealistic/flythroughCameraAngles';
import {
  submitFlythroughVideoFinal,
  submitFlythroughVideoPreview,
  buildFlythroughRenderHistoryRequestBody,
  extractFlythroughSettingsFromLocalItem,
} from '../utils/photorealistic/flythroughVideoInference';
import { downloadFlythroughVideo } from '../utils/photorealistic/downloadFlythroughVideo';
import {
  enrichLocalFlythroughResults,
  enrichFlythroughRenderHistoryItems,
  dedupeFlythroughHistoryById,
  isFlythroughResultAlreadyInHistory,
  loadFlythroughRenderHistoryEntries,
  rememberFlythroughPlaybackUrl,
  resolveFlythroughItemDisplayUrls,
} from '../utils/photorealistic/flythroughHistoryDisplay';
import {
  appendLocalFlythroughResult,
  loadLocalFlythroughResults,
  removeLocalFlythroughResult,
} from '../utils/photorealistic/localFlythroughResultsStorage';
import {
  appendLocalRenderResult,
  loadLocalRenderResults,
  removeLocalRenderResult,
} from '../utils/photorealistic/localRenderResultsStorage';
import {
  clearRoomImageRenderQueue,
  enqueueRoomImageRenderWork,
} from '../utils/photorealistic/roomImageRenderTaskQueue';
import {
  applyThumbMapToCuratedItems,
  applyThumbMapToPlacedModels,
  collectRoomObjectIdsFromStudioState,
  fetchRoomObjectThumbMap,
  resolvePlacementImageSrc,
  resolveRoomObjectThumbSrcAsync,
} from '../utils/photorealistic/roomObjectThumbnails';
import {
  clientPointToImageCoords,
  defaultCuratedPlacementLogicalWidth,
  defaultCuratedPlacementScreenWidth,
} from '../utils/photorealistic/canvasImageCoords';
import PlacedCuratedModelsOverlay from './PlacedCuratedModelsOverlay';
import { intersectMaterialListsBySupplierId } from '../utils/photorealistic/intersectMaterialListsBySupplierId';
import { runInference as runMaskRefinement } from '../ai/sam/inference';
import { SAM_STATUS, MAX_SAM_PROMPT_POINTS } from '../ai/config';
import { runAiInference, getAiInferencePresignedUrl, cancelAiInferenceTask } from '../api/aiInference';
import { addTask, cancelTask, removeTask } from '../services/taskResolver';
import ImageWithPolygons from './ImageWithPolygons';
import EditorActionToolbar from './EditorActionToolbar';
import DetectedObjectsPanel from './DetectedObjectsPanel';
import MaterialsPanel from './MaterialsPanel';
import RoomHistoryPanel from './RoomHistoryPanel';
import ModelStudioPanel from './ModelStudioPanel';
import PromptPanel from './PromptPanel';
import RenderPanel from './RenderPanel';
import ExportImagePanel from './ExportImagePanel';
import FlythroughVideoPanel from './FlythroughVideoPanel';
import FlythroughExportVideoPanel from './FlythroughExportVideoPanel';
import FlythroughRoiCropOverlay from './FlythroughRoiCropOverlay';
import SavedRenderHistoryStrip from './SavedRenderHistoryStrip';
import SavedFlythroughHistoryStrip from './SavedFlythroughHistoryStrip';
import RenderPreviewModal from './RenderPreviewModal';
import RenderCompareModal from './RenderCompareModal';
import ModelLibraryModal from './ModelLibraryModal';
import TransformModelsPanel from './TransformModelsPanel';
import ImageSelectionPanel from './ImageSelectionPanel';
import PhotorealisticCanvasToolAside from './PhotorealisticCanvasToolAside';
import ObjectChoiceModal, {
  CuratedPlacementConfirmModal,
  DeleteRenderConfirmModal,
  FlythroughGenerationFailedModal,
  RemoveObjectConfirmModal,
} from './ObjectChoiceModal';
import {
  mergePolygonWithStrokes,
  mergeMultiRingPolygonWithStrokes,
  mergeMultiRingPolygonWithStrokesRespectHoles,
  computePolygonBbox,
} from '../utils/polygonMerge';
import {
  saveInteriors,
  updateInteriors,
  deleteInterior,
  restoreInterior,
  createRoomObject,
  deleteRoomObject,
  getRoom,
  getRoomImages,
  getRoomObjects,
  getInteriors,
  applyImageToRoom,
  saveRenderHistory,
  getRenderHistory,
  deleteRenderHistory,
  uploadRoomImage,
  uploadRoomChildImage,
  updateRoom,
  parseRoomImageDimensionsFromApi,
} from '../api/rooms';
import { getAllInteriorTypes, getMaterialsByInteriorType } from '../api/supplierInteriors';
import { PhotorealisticHelpfulTipsPanel } from '../content/photorealisticHelpfulTips';
import {
  fetchUrlAsPngFileForRoomObject,
  parseRoomObjectIdFromApiResponse,
  pickRoomObjectImageUrl,
  resolveRoomObjectImageSrcForDisplay,
} from '../utils/roomObjectImage';

/** Guards generate_mask bootstrap from duplicate launches per room image (e.g. StrictMode double-effect). */
const generateMaskBootstrapInFlight = new Set();

/** Session row for Model Library "Detected Models" — must match `uploadSource: 'isolated'` shape used by isolated uploads. */
function buildDetectedModelsSessionItemFromCanvasCrop({ file, displayName, roomObjectId }) {
  const previewUrl = URL.createObjectURL(file);
  const id = `canvas-crop-${roomObjectId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const defaultName = file.name.replace(/\.[^/.]+$/, '');
  return {
    id,
    name: file.name,
    detailsName: (displayName && String(displayName).trim()) || defaultName,
    detailsCategory: 'Other',
    file,
    size: file.size,
    previewUrl,
    progress: 100,
    status: 'completed',
    uploadSource: 'isolated',
    needsBackgroundRemoval: false,
    backgroundRemovalStatus: 'completed',
    backgroundRemovalError: '',
    saveStatus: 'saved',
    saveError: '',
    roomObjectId,
  };
}

/** Snapshot before Model Library `generate_mask` replaces polygons (captured only for the first mask in a session). */
function captureUploadModelMaskPreFlowSnapshot({
  polygons,
  detectedObjectsFromAPI,
  userDefinedObjects,
  selectedPolygonIndex,
  multiSelectedPolygonIndices,
  detectedObjectMultiSelectActive,
  interiorsFromExisting,
  interiorsSavedOnce,
  selectedObjectIndices,
  selectedUserDefinedIndices,
  roomImageDimensions,
}) {
  const clone = (value) => {
    try {
      return structuredClone(value);
    } catch {
      return JSON.parse(JSON.stringify(value));
    }
  };
  const rw = Number(roomImageDimensions?.width);
  const rh = Number(roomImageDimensions?.height);
  const snapshotDims =
    roomImageDimensions &&
    typeof roomImageDimensions === 'object' &&
    rw > 0 &&
    rh > 0
      ? { width: rw, height: rh }
      : null;
  return {
    polygons: clone(Array.isArray(polygons) ? polygons : []),
    detectedObjectsFromAPI: clone(Array.isArray(detectedObjectsFromAPI) ? detectedObjectsFromAPI : []),
    userDefinedObjects: clone(Array.isArray(userDefinedObjects) ? userDefinedObjects : []),
    selectedPolygonIndex,
    multiSelectedPolygonIndices: [...(multiSelectedPolygonIndices ?? [])],
    detectedObjectMultiSelectActive: Boolean(detectedObjectMultiSelectActive),
    interiorsFromExisting: Boolean(interiorsFromExisting),
    interiorsSavedOnce: Boolean(interiorsSavedOnce),
    selectedObjectIndices: [...(selectedObjectIndices ?? [])],
    selectedUserDefinedIndices: [...(selectedUserDefinedIndices ?? [])],
    roomImageDimensions: snapshotDims,
  };
}

/** Same shape as ModelLibraryModal — normalize generate_mask API responses to a polygon list. */
function extractGenerateMaskPolygonList(data) {
  if (data == null) return [];
  const tr = data.taskResult;
  if (Array.isArray(tr)) return tr;
  if (Array.isArray(data.polygons)) return data.polygons;
  if (Array.isArray(data)) return data;
  return [];
}

async function resolveRoomObjectDisplayImageUrl(roomId, roomObjectId) {
  if (roomId == null || roomObjectId == null) return '';
  try {
    const objects = await getRoomObjects(roomId);
    const found = objects.find((o) => Number(o.id) === Number(roomObjectId));
    const raw = pickRoomObjectImageUrl(found);
    if (!raw || !String(raw).trim()) return '';
    return resolveRoomObjectImageSrcForDisplay(String(raw).trim());
  } catch {
    return '';
  }
}

function resolveRoomImageUrl(item) {
  // Prefer direct object URLs (AWS/CDN) before links that may force file download.
  return item?.url ?? item?.imageKey ?? item?.s3Key ?? null;
}

/** Offset + size of the fitted bitmap when aspect-ratio content is `object-fit: contain` in a WxH box. */
function getObjectFitContainBox(containerW, containerH, contentW, contentH) {
  if (!(containerW > 0) || !(containerH > 0) || !(contentW > 0) || !(contentH > 0)) {
    return { ox: 0, oy: 0, rw: Math.max(0, containerW), rh: Math.max(0, containerH) };
  }
  const scale = Math.min(containerW / contentW, containerH / contentH);
  const rw = contentW * scale;
  const rh = contentH * scale;
  const ox = (containerW - rw) / 2;
  const oy = (containerH - rh) / 2;
  return { ox, oy, rw, rh };
}

const Photorealistic = () => {
  const {
    photorealisticImage,
    setPhotorealisticImage,
    setCurrentView,
    roomId,
    roomImageId,
    setRoomImageId,
    roomImageDimensions,
    setRoomImageDimensions,
  } = useSidebar();
  const [image, setImage] = useState(null);
  const [imageRestoreLoading, setImageRestoreLoading] = useState(false);
  const [imageRestoreRetryKey, setImageRestoreRetryKey] = useState(0);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gpuInfo, setGpuInfo] = useState(null);
  const [executionTime, setExecutionTime] = useState(null);
  const [error, setError] = useState(null);
  const [swapped, setSwapped] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [polygons, setPolygons] = useState(null);
  const [polygonsLoading, setPolygonsLoading] = useState(false);
  const [materialTransferLoading, setMaterialTransferLoading] = useState(false);
  /** After a failed material_transfer, show "Regenerate" on the materials panel confirm button. */
  const [materialTransferLastFailed, setMaterialTransferLastFailed] = useState(false);
  const [removeObjectLoading, setRemoveObjectLoading] = useState(false);
  const [aiNotification, setAiNotification] = useState(null); // { task, status, message }
  const [removeObjectMode, setRemoveObjectMode] = useState(false);
  const [operationTypeForApply, setOperationTypeForApply] = useState('material_transfer');
  const [lastMaterialTransferResultKey, setLastMaterialTransferResultKey] = useState(null);
  const [resultImageElement, setResultImageElement] = useState(null);
  const [resultImageLoadError, setResultImageLoadError] = useState(false);
  const [basedImageLoadError, setBasedImageLoadError] = useState(false);
  const [showCompareSlider, setShowCompareSlider] = useState(false);
  const [compareSliderPosition, setCompareSliderPosition] = useState(50);
  const [isCompareDragging, setIsCompareDragging] = useState(false);
  const compareCanvasRef = useRef(null);
  const compareBaseImageRef = useRef(null);
  /** Compare slider/drag mapped to this rect so handles stay inside object-fit content (not letterbox). */
  const [compareFitRect, setCompareFitRect] = useState({ ox: 0, oy: 0, rw: 0, rh: 0 });
  const [scanTimeoutOrError, setScanTimeoutOrError] = useState(false);
  const [scanNoDetections, setScanNoDetections] = useState(false);
  const [isGenerateMaskTaskActive, setIsGenerateMaskTaskActive] = useState(false);
  const [generateMaskConflictRetrySeconds, setGenerateMaskConflictRetrySeconds] = useState(null);
  const [inferenceRetryKey, setInferenceRetryKey] = useState(0);
  const scanTimeoutRef = useRef(null);
  const forceGenerateMaskRef = useRef(false);
  /** Invalidate in-flight remove_object / material_transfer UI when user cancels or starts a new run. */
  const removeObjectRunIdRef = useRef(0);
  const removeObjectTaskIdRef = useRef(null);
  const materialTransferRunIdRef = useRef(0);
  const materialTransferTaskIdRef = useRef(null);
  const promptPreviewResultKeyRef = useRef(null);
  const promptApplyMaskInFlightRef = useRef(false);
  const changeObjectAngleRunIdRef = useRef(0);
  const changeObjectAngleTaskIdRef = useRef(null);
  const [showPolygonsOverlay, setShowPolygonsOverlay] = useState(false);
  const [editingLayerIndex, setEditingLayerIndex] = useState(null);
  const [editorState, setEditorState] = useState({
    activeTool: 'brush',
    magicMode: 'add',
    brushSize: 26,
    zoom: 1,
    pan: { x: 0, y: 0 },
    strokes: [],
    undoStack: [],
    clickPoints: [],
    polygonUndoStack: [],
    polygonRedoStack: [],
  });
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;
  const polygonAtEditStartRef = useRef(null);
  /** While Space is held for pan, previous tool to restore on keyup. */
  const spacePanPriorToolRef = useRef(null);
  const fileInputRef = useRef(null);
  const [interiorTypesList, setInteriorTypesList] = useState([]);
  const [interiorTypesLoading, setInteriorTypesLoading] = useState(false);
  const [expandedGroupLabel, setExpandedGroupLabel] = useState(null);
  const [pendingDetectedObjects, setPendingDetectedObjects] = useState(null);
  const [objectChoiceStep, setObjectChoiceStep] = useState('initial');
  /** Keys from `getInteriorStableKey` for low-confidence items the user chose "Ignore" on (show badge in choose list). */
  const [ignoredLowConfidenceKeys, setIgnoredLowConfidenceKeys] = useState(() => new Set());
  const [hideLowConfidenceBanner, setHideLowConfidenceBanner] = useState(false);
  const lowConfidenceReviewReturnStepRef = useRef('initial');
  const [selectedObjectIndices, setSelectedObjectIndices] = useState(() => new Set());
  const [deleteDetectionMode, setDeleteDetectionMode] = useState(false);
  const [addingNewObjectFromPopup, setAddingNewObjectFromPopup] = useState(false);
  const [isFirstMask, setIsFirstMask] = useState(false);
  const [newObjectLabel, setNewObjectLabel] = useState('');
  const [newObjectLabelDropdownOpen, setNewObjectLabelDropdownOpen] = useState(false);
  const [detectedObjectsFromAPI, setDetectedObjectsFromAPI] = useState([]);
  const [userDefinedObjects, setUserDefinedObjects] = useState([]);
  const [selectedUserDefinedIndices, setSelectedUserDefinedIndices] = useState(() => new Set());
  const [objectBeingLabeled, setObjectBeingLabeled] = useState(null);
  const [objectRenameIndex, setObjectRenameIndex] = useState(null);
  const [reviewLowConfidenceEditIndex, setReviewLowConfidenceEditIndex] = useState(null);
  const [reviewLowConfidenceSource, setReviewLowConfidenceSource] = useState(null);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [showModelStudioPanel, setShowModelStudioPanel] = useState(false);
  const [showPromptPanel, setShowPromptPanel] = useState(false);
  const [showRenderPanel, setShowRenderPanel] = useState(false);
  const [showExportImagePanel, setShowExportImagePanel] = useState(false);
  const [showFlythroughPanel, setShowFlythroughPanel] = useState(false);
  const [flythroughRoiMode, setFlythroughRoiMode] = useState('full_screen');
  const [flythroughRoi, setFlythroughRoi] = useState(FLYTHROUGH_ROI_FULL_SCREEN);
  const [flythroughCameraPreset, setFlythroughCameraPreset] = useState('dolly_in');
  const [flythroughMotionMode, setFlythroughMotionMode] = useState(
    FLYTHROUGH_MOTION_MODES.CAMERA_PRESET,
  );
  const [flythroughCameraAngleSequence, setFlythroughCameraAngleSequence] = useState([
    FLYTHROUGH_CANONICAL_CAMERA_ANGLE,
  ]);
  const [flythroughDraftCameraAngle, setFlythroughDraftCameraAngle] = useState(
    FLYTHROUGH_CANONICAL_CAMERA_ANGLE,
  );
  const [flythroughMotionPromptFields, setFlythroughMotionPromptFields] = useState(
    () => ({ ...FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT }),
  );
  const [flythroughDurationSeconds, setFlythroughDurationSeconds] = useState(
    FLYTHROUGH_DURATION_DEFAULT_SECONDS,
  );
  const [flythroughRenderResolution, setFlythroughRenderResolution] = useState('432p');
  const [flythroughFrameRate, setFlythroughFrameRate] = useState('24');
  const [flythroughSelectedRenderIds, setFlythroughSelectedRenderIds] = useState([]);
  const [flythroughExportVideoPanelCollapsed, setFlythroughExportVideoPanelCollapsed] =
    useState(false);
  const [flythroughVideoPreviewSrc, setFlythroughVideoPreviewSrc] = useState('');
  const [flythroughGeneratedVideoSrc, setFlythroughGeneratedVideoSrc] = useState('');
  const [flythroughGeneratedThumbnailSrc, setFlythroughGeneratedThumbnailSrc] = useState('');
  const [flythroughGeneratedVideoS3Key, setFlythroughGeneratedVideoS3Key] = useState('');
  const [flythroughGeneratedThumbnailS3Key, setFlythroughGeneratedThumbnailS3Key] = useState('');
  const [flythroughGeneratedSavedSettings, setFlythroughGeneratedSavedSettings] = useState(null);
  const [flythroughThumbnailSrc, setFlythroughThumbnailSrc] = useState('');
  const [flythroughRoiPreviewImage, setFlythroughRoiPreviewImage] = useState(null);
  const [pendingFlythroughVideoJobs, setPendingFlythroughVideoJobs] = useState([]);
  const [localRecentFlythroughs, setLocalRecentFlythroughs] = useState([]);
  const [localRecentFlythroughsLoading, setLocalRecentFlythroughsLoading] = useState(false);
  const [flythroughVideoLightboxSrc, setFlythroughVideoLightboxSrc] = useState('');
  const [flythroughDeleteConfirmItem, setFlythroughDeleteConfirmItem] = useState(null);
  const [flythroughGenerationFailedMode, setFlythroughGenerationFailedMode] = useState(null);
  const [deletingFlythroughId, setDeletingFlythroughId] = useState(null);
  const [downloadingFlythroughId, setDownloadingFlythroughId] = useState(null);
  const [downloadingGeneratedFlythrough, setDownloadingGeneratedFlythrough] = useState(false);
  const [savingFlythroughHistoryItemId, setSavingFlythroughHistoryItemId] = useState(null);
  const [flythroughLibraryHistory, setFlythroughLibraryHistory] = useState([]);
  const [flythroughLibraryLoading, setFlythroughLibraryLoading] = useState(false);
  const [flythroughLibraryError, setFlythroughLibraryError] = useState(null);
  const localRecentFlythroughsRefreshIdRef = useRef(0);
  const flythroughLibraryRefreshIdRef = useRef(0);
  const [flythroughPreviewVideoLoading, setFlythroughPreviewVideoLoading] = useState(false);
  const [flythroughPreviewStatusLabel, setFlythroughPreviewStatusLabel] = useState('');
  const flythroughPreviewRunIdRef = useRef(0);
  const flythroughPreviewTaskIdRef = useRef(null);
  const flythroughCancelledJobIdsRef = useRef(new Set());
  const [exportImagePanelCollapsed, setExportImagePanelCollapsed] = useState(false);
  const [exportPreviewUrl, setExportPreviewUrl] = useState('');
  const [exportPreviewResultKey, setExportPreviewResultKey] = useState('');
  const [exportPreviewGeneratedAt, setExportPreviewGeneratedAt] = useState(null);
  const [exportResultResolutionId, setExportResultResolutionId] = useState(null);
  const [exportPreviewSavedConfig, setExportPreviewSavedConfig] = useState(null);
  const [renderLibraryHistory, setRenderLibraryHistory] = useState([]);
  const [renderLibraryLoading, setRenderLibraryLoading] = useState(false);
  const [renderLibraryError, setRenderLibraryError] = useState(null);
  const [localRecentRenders, setLocalRecentRenders] = useState([]);
  const [localRecentRendersLoading, setLocalRecentRendersLoading] = useState(false);
  const [savedRenderLightboxUrl, setSavedRenderLightboxUrl] = useState('');
  const [savedRenderLightboxItemId, setSavedRenderLightboxItemId] = useState(null);
  const [savedRenderCompareActive, setSavedRenderCompareActive] = useState(false);
  const [savedRenderCompareSelection, setSavedRenderCompareSelection] = useState([]);
  const [savedRenderCompareModalOpen, setSavedRenderCompareModalOpen] = useState(false);
  const [deletingRenderHistoryId, setDeletingRenderHistoryId] = useState(null);
  const [savingRenderHistoryItemId, setSavingRenderHistoryItemId] = useState(null);
  const [renderDeleteConfirmItem, setRenderDeleteConfirmItem] = useState(null);
  const [exportPreviewLoading, setExportPreviewLoading] = useState(false);
  const [exportPreviewStatusLabel, setExportPreviewStatusLabel] = useState('');
  const [exportPreviewSaveLoading, setExportPreviewSaveLoading] = useState(false);
  const [pendingExportRenderJobs, setPendingExportRenderJobs] = useState([]);
  const [renderResolution, setRenderResolution] = useState('1k');
  const [renderLightingPreset, setRenderLightingPreset] = useState('default');
  const [renderCameraAngle, setRenderCameraAngle] = useState({
    azimuth: 0,
    elevation: 0,
    distance: 1,
  });
  const exportPreviewRunIdRef = useRef(0);
  const localRecentRendersRefreshIdRef = useRef(0);
  const renderLibraryRefreshIdRef = useRef(0);
  const prevRoomImageIdForRenderQueueRef = useRef(roomImageId);
  const [showTransformModelsPanel, setShowTransformModelsPanel] = useState(false);
  const [historyImages, setHistoryImages] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyPreviewImageId, setHistoryPreviewImageId] = useState(null);
  const [showMaterialsPanel, setShowMaterialsPanel] = useState(false);
  const [materialsForInterior, setMaterialsForInterior] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materialsError, setMaterialsError] = useState(null);
  const [materialsInteriorTypeId, setMaterialsInteriorTypeId] = useState(null);
  const [materialsTargetInteriorId, setMaterialsTargetInteriorId] = useState(null);
  /** When 2+ polygons are selected (multi-select), material_transfer uses `room_interior_ids`. */
  const [materialsTargetInteriorIds, setMaterialsTargetInteriorIds] = useState(null);
  /** Selected material card in panel; Confirm triggers material_transfer. */
  const [pendingMaterialSelection, setPendingMaterialSelection] = useState(null);
  /** Search query for filtering materials in the side panel. */
  const [materialsSearchQuery, setMaterialsSearchQuery] = useState('');
  /** While material_transfer result is on canvas, hide grid until user clicks "Change Material". */
  const [showMaterialsGridDuringAiResult, setShowMaterialsGridDuringAiResult] = useState(false);
  /** In-panel editor shown after clicking "Modify Materials / Finishes". */
  const [showMaterialModifyPanel, setShowMaterialModifyPanel] = useState(false);
  const [uploadModelMaskResults, setUploadModelMaskResults] = useState([]);
  const [uploadModelMaskFlowActive, setUploadModelMaskFlowActive] = useState(false);
  const [selectedUploadModelMaskRoomObjectId, setSelectedUploadModelMaskRoomObjectId] = useState(null);
  const [saveSelectedObjectLoading, setSaveSelectedObjectLoading] = useState(false);
  /** Model Library / Upload Models session items + modal (lifted so popup can open while Detected Objects panel is visible). */
  const [studioModels, setStudioModels] = useState([]);
  /** Room objects confirmed from Model Library “My Library” → shown under Model Studio Curated Models. */
  const [studioCuratedLibraryItems, setStudioCuratedLibraryItems] = useState(() =>
    roomId != null ? loadCuratedModelsForRoom(roomId) : [],
  );
  /** Curated models dragged onto the room image (image pixel coordinates). */
  const [placedCuratedModels, setPlacedCuratedModels] = useState(() =>
    roomId != null ? loadPlacedCuratedModelsForRoom(roomId) : [],
  );
  /** Uncommitted placement after drag-drop onto canvas (Update saves, Cancel discards). */
  const [pendingCuratedPlacement, setPendingCuratedPlacement] = useState(null);
  const [curatedPlacementConfirmOpen, setCuratedPlacementConfirmOpen] = useState(false);
  const [curatedCompositeLoading, setCuratedCompositeLoading] = useState(false);
  const [showCuratedSavePromptTip, setShowCuratedSavePromptTip] = useState(false);
  const [curatedSavePromptTipEpoch, setCuratedSavePromptTipEpoch] = useState(0);
  const [workspaceOverlayMessage, setWorkspaceOverlayMessage] = useState(null);
  const [selectedPlacedCuratedId, setSelectedPlacedCuratedId] = useState(null);
  const [canvasCuratedDropActive, setCanvasCuratedDropActive] = useState(false);
  const [mainCanvasLayoutSize, setMainCanvasLayoutSize] = useState({ w: 0, h: 0 });
  const mainCanvasContainerRef = useRef(null);
  const placedCuratedHydratedForRoomRef = useRef(null);
  /** Skip one save after hydrating from localStorage so we do not overwrite with []. */
  const placedCuratedSkipSaveRef = useRef(true);
  const curatedHydratedForRoomRef = useRef(roomId != null ? roomId : null);
  /** Skip one save after hydrating curated list from localStorage so we do not overwrite with []. */
  const curatedSkipSaveRef = useRef(true);
  const [modelLibraryModalOpen, setModelLibraryModalOpen] = useState(false);
  const [modelLibraryInitialStep, setModelLibraryInitialStep] = useState('library');
  const [modelLibraryModalKey, setModelLibraryModalKey] = useState(0);
  /** When non-null, Detected Models step opens with exactly these row ids selected (canvas Save as Object). */
  const [modelLibraryDetectedSelectionIds, setModelLibraryDetectedSelectionIds] = useState(null);
  const uploadModelMaskPreviousImageUrlRef = useRef(null);
  /** Polygons / API list / selection from before the first `generate_mask` in the current upload-model image-selection session. */
  const uploadModelMaskPreFlowSnapshotRef = useRef(null);
  /** True after at least one generation from Modify panel for current selection. */
  const [materialModifyGeneratedOnce, setMaterialModifyGeneratedOnce] = useState(false);
  const [materialModifyRotation, setMaterialModifyRotation] = useState(0);
  const [materialModifyScale, setMaterialModifyScale] = useState(100);
  const [materialModifyFlipVertical, setMaterialModifyFlipVertical] = useState(false);
  const [materialModifyFlipHorizontal, setMaterialModifyFlipHorizontal] = useState(false);
  const [interiorsFromExisting, setInteriorsFromExisting] = useState(false);
  const [interiorsSavedOnce, setInteriorsSavedOnce] = useState(false);
  const [selectedPolygonIndex, setSelectedPolygonIndex] = useState(null);
  const [showAllPolygonsOnCanvas, setShowAllPolygonsOnCanvas] = useState(false);
  /** Collapse the right prompt / objects / history panel to give more canvas space */
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  /** Collapse the materials strip (beside canvas) while keeping the tab visible */
  const [materialsPanelCollapsed, setMaterialsPanelCollapsed] = useState(false);
  /** Collapse the transform-models strip (beside canvas) while keeping the tab visible */
  const [transformModelsPanelCollapsed, setTransformModelsPanelCollapsed] = useState(false);
  /** change_object_angle: preview URL in Transform Models panel (+ main canvas when applied). */
  const [transformModelsOutputUrl, setTransformModelsOutputUrl] = useState('');
  const [transformModelsOutputResultKey, setTransformModelsOutputResultKey] = useState('');
  const [changeObjectAngleLoading, setChangeObjectAngleLoading] = useState(false);
  const [transformModelsPlaceLoading, setTransformModelsPlaceLoading] = useState(false);
  /** Collapse the image-selection strip (right of transform models) */
  const [imageSelectionPanelCollapsed, setImageSelectionPanelCollapsed] = useState(false);
  /** 0–1: overlay alpha for selected / visible polygons on canvas (not used while editing a layer). */
  const [polygonOverlayOpacity, setPolygonOverlayOpacity] = useState(1);
  /**
   * Bottom canvas actions (opacity, Apply Finish, …) only after the floating edit control on canvas;
   * hidden when choosing from the detected-objects list or by clicking the polygon on canvas (unless multi-select).
   */
  const [polygonBottomActionsOpen, setPolygonBottomActionsOpen] = useState(false);
  /** Image-local % position (centroid) where brush/edit control sits — used to align the actions strip. */
  const [polygonBottomActionsAnchor, setPolygonBottomActionsAnchor] = useState(null);
  /** When true, row/canvas clicks toggle multi-selection; canvas shows all selected polygons. */
  const [detectedObjectMultiSelectActive, setDetectedObjectMultiSelectActive] = useState(false);
  const [multiSelectedPolygonIndices, setMultiSelectedPolygonIndices] = useState(() => new Set());
  /** After clicking the Objects “Add” icon, show Add another / Confirm / Cancel under the toolbar row. */
  const [addObjectActionsVisible, setAddObjectActionsVisible] = useState(false);
  /** After clicking Remove object, show Confirm / Cancel before enabling remove mode. */
  const [removeObjectActionsVisible, setRemoveObjectActionsVisible] = useState(false);
  /** Second-step modal before POST remove_object (anchor polygon index). */
  const [removeObjectConfirmOpen, setRemoveObjectConfirmOpen] = useState(false);
  const [removeObjectConfirmIndex, setRemoveObjectConfirmIndex] = useState(null);
  /** True only when user clicked "Refine" from remove-object result. */
  const [removeObjectRefinePending, setRemoveObjectRefinePending] = useState(false);
  /** Which detected-object row has the ⋮ overflow menu open (index into `polygons`) */
  const [objectListOverflowMenuIndex, setObjectListOverflowMenuIndex] = useState(null);
  /** Brush tips should appear only after explicit brush icon click (not just default tool). */
  const [hasExplicitBrushToolSelection, setHasExplicitBrushToolSelection] = useState(false);
  /** Zoom & Pan tips after user clicks toolbar zoom +/− while editing (still on brush/eraser/etc.). */
  const [hasTriggeredZoomToolbarTips, setHasTriggeredZoomToolbarTips] = useState(false);
  /** Bumps on each zoom +/− click while editing so dismissed zoom tips can show again. */
  const [zoomToolbarTipsEpoch, setZoomToolbarTipsEpoch] = useState(0);
  /** Bumps when user selects Move while editing so dismissed pan tips can show again. */
  const [panToolbarTipsEpoch, setPanToolbarTipsEpoch] = useState(0);
  const [deletedObjectUndo, setDeletedObjectUndo] = useState(null);
  const selectObjectsSnapshotRef = useRef(null);
  const selectedObjectBeforeAddRef = useRef(null);
  const existingObjectIdsBeforeAddRef = useRef(null);
  /** After Add New → label step, return to `choose` ("focus on?") vs `initial` ("Show detected objects"). */
  const addObjectLabelReturnStepRef = useRef('initial');
  /** Last material_transfer material or remove_object polygon index — for "Regenerate" on the result preview. */
  const lastAiInferenceRegenerateContextRef = useRef(null);
  /** Base image snapshot before entering remove-result refine (used to restore on Cancel). */
  const removeResultRefineOriginImageRef = useRef(null);
  const removeResultRefineOriginUrlRef = useRef(null);
  const removeResultRefineOriginPolygonsRef = useRef(null);
  /** Polygon indices used in the latest remove_object run (preserve multi-select through Refine). */
  const removeObjectLastRunIndicesRef = useRef([]);
  /** Scroll container for the materials grid (scroll to top after "Change material"). */
  const materialsPanelListScrollRef = useRef(null);
  const [samStatus, setSamStatus] = useState(null);
  const [samError, setSamError] = useState(null);
  const noImageFileInputRef = useRef(null);
  const [noImageUploadLoading, setNoImageUploadLoading] = useState(false);
  const [noImageUploadError, setNoImageUploadError] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle | saving | saved | recovered | synced | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [saveStatusNow, setSaveStatusNow] = useState(Date.now());
  const [showSessionRecoveryModal, setShowSessionRecoveryModal] = useState(false);
  const [recoveryDraftPayload, setRecoveryDraftPayload] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const skipNextAutosaveRef = useRef(false);
  const latestDraftPayloadRef = useRef(null);
  /** Skip one recovery-modal check after intentional base-image switches (e.g. History click). */
  const suppressNextRecoveryModalRef = useRef(false);

  const draftStorageKey = useMemo(() => {
    if (roomId == null || roomImageId == null) return null;
    return `photorealistic:draft:${roomId}:${roomImageId}`;
  }, [roomId, roomImageId]);

  useEffect(() => {
    const timer = setInterval(() => setSaveStatusNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const openModelLibraryLibrary = useCallback(() => {
    setModelLibraryInitialStep('library');
    setModelLibraryDetectedSelectionIds(null);
    setModelLibraryModalKey((k) => k + 1);
    setModelLibraryModalOpen(true);
  }, []);

  const openModelLibraryUpload = useCallback(() => {
    setModelLibraryInitialStep('upload');
    setModelLibraryDetectedSelectionIds(null);
    setModelLibraryModalKey((k) => k + 1);
    setModelLibraryModalOpen(true);
  }, []);

  const handleConfirmLibraryCuratedSelection = useCallback(async (items) => {
    if (!Array.isArray(items)) return;
    let next = items;
    if (roomId != null) {
      try {
        const ids = items.map((item) => item?.id).filter((id) => id != null);
        const thumbMap = await fetchRoomObjectThumbMap(roomId, ids);
        next = applyThumbMapToCuratedItems(items, thumbMap);
      } catch (err) {
        console.warn('Failed to load thumbnails for curated selection:', err);
      }
      saveCuratedModelsForRoom(roomId, next);
      curatedHydratedForRoomRef.current = roomId;
      curatedSkipSaveRef.current = true;
    }
    setStudioCuratedLibraryItems(next);
  }, [roomId]);

  const handleCuratedLibraryModelsChange = useCallback((items) => {
    if (!Array.isArray(items)) return;
    setStudioCuratedLibraryItems(items);
    if (roomId != null) {
      curatedHydratedForRoomRef.current = roomId;
      curatedSkipSaveRef.current = true;
    }
  }, [roomId]);

  const handleRemoveCuratedModel = useCallback((objectId) => {
    if (objectId == null) return;
    const idStr = String(objectId);
    setStudioCuratedLibraryItems((prev) => {
      const next = prev.filter((item) => String(item.id) !== idStr);
      if (roomId != null) {
        saveCuratedModelsForRoom(roomId, next);
        curatedHydratedForRoomRef.current = roomId;
        curatedSkipSaveRef.current = true;
      }
      return next;
    });
  }, [roomId]);

  useEffect(() => {
    if (roomId == null) {
      setStudioCuratedLibraryItems([]);
      curatedHydratedForRoomRef.current = null;
      curatedSkipSaveRef.current = true;
      return;
    }
    if (curatedHydratedForRoomRef.current === roomId) return;
    setStudioCuratedLibraryItems(loadCuratedModelsForRoom(roomId));
    curatedHydratedForRoomRef.current = roomId;
    curatedSkipSaveRef.current = true;
  }, [roomId]);

  useEffect(() => {
    if (roomId == null || curatedHydratedForRoomRef.current !== roomId) return;
    if (curatedSkipSaveRef.current) {
      curatedSkipSaveRef.current = false;
      return;
    }
    saveCuratedModelsForRoom(roomId, studioCuratedLibraryItems);
  }, [roomId, studioCuratedLibraryItems]);

  useEffect(() => {
    if (roomId == null) {
      setPlacedCuratedModels([]);
      setPendingCuratedPlacement(null);
      setSelectedPlacedCuratedId(null);
      placedCuratedHydratedForRoomRef.current = null;
      placedCuratedSkipSaveRef.current = true;
      return;
    }
    if (placedCuratedHydratedForRoomRef.current === roomId) return;
    setPlacedCuratedModels(loadPlacedCuratedModelsForRoom(roomId));
    setPendingCuratedPlacement(null);
    setSelectedPlacedCuratedId(null);
    placedCuratedHydratedForRoomRef.current = roomId;
    placedCuratedSkipSaveRef.current = true;
  }, [roomId]);

  useEffect(() => {
    if (roomId == null || placedCuratedHydratedForRoomRef.current !== roomId) return;
    if (placedCuratedSkipSaveRef.current) {
      placedCuratedSkipSaveRef.current = false;
      return;
    }
    savePlacedCuratedModelsForRoom(roomId, placedCuratedModels);
  }, [roomId, placedCuratedModels]);

  const curatedLibrarySelectionIds = useMemo(
    () => studioCuratedLibraryItems.map((x) => x.id).filter((id) => id != null),
    [studioCuratedLibraryItems],
  );

  /** Hide these room objects in Model Library “My Library” (mask upload + active isolated session rows). */
  const librarySessionHiddenRoomObjectIds = useMemo(() => {
    const ids = new Set();
    uploadModelMaskResults.forEach((r) => {
      const n = Number(r?.roomObjectId);
      if (Number.isFinite(n)) ids.add(n);
    });
    studioModels.forEach((item) => {
      if (item?.uploadSource === 'isolated' && item?.roomObjectId != null) {
        const n = Number(item.roomObjectId);
        if (Number.isFinite(n)) ids.add(n);
      }
    });
    return [...ids];
  }, [uploadModelMaskResults, studioModels]);

  const studioRoomObjectIdsKey = useMemo(() => {
    const placementsForIds = pendingCuratedPlacement
      ? [...placedCuratedModels, pendingCuratedPlacement]
      : placedCuratedModels;
    return collectRoomObjectIdsFromStudioState(studioCuratedLibraryItems, placementsForIds).join(',');
  }, [studioCuratedLibraryItems, placedCuratedModels, pendingCuratedPlacement]);

  const refreshStudioRoomObjectThumbs = useCallback(async () => {
    if (roomId == null) return;
    const ids = studioRoomObjectIdsKey ? studioRoomObjectIdsKey.split(',').filter(Boolean) : [];
    if (ids.length === 0) return;
    try {
      const thumbMap = await fetchRoomObjectThumbMap(roomId, ids);
      setStudioCuratedLibraryItems((prev) => applyThumbMapToCuratedItems(prev, thumbMap));
      setPlacedCuratedModels((prev) => applyThumbMapToPlacedModels(prev, thumbMap));
      setPendingCuratedPlacement((pending) => {
        if (!pending?.roomObjectId) return pending;
        const meta = thumbMap.get(String(pending.roomObjectId));
        if (!meta) return pending;
        return {
          ...pending,
          thumbSrc: meta.thumbSrc || pending.thumbSrc || '',
          name: meta.name ?? pending.name,
          category: meta.category ?? pending.category,
        };
      });
    } catch (err) {
      console.warn('Failed to refresh room object thumbnails:', err);
    }
  }, [roomId, studioRoomObjectIdsKey]);

  /** Refresh curated + placed thumbnails from API (presigned URLs expire; do not persist thumb URLs). */
  useEffect(() => {
    if (roomId == null || !studioRoomObjectIdsKey) return;
    refreshStudioRoomObjectThumbs();
  }, [roomId, studioRoomObjectIdsKey, refreshStudioRoomObjectThumbs]);

  const visiblePolygonIndicesForCanvas = useMemo(() => {
    if (!detectedObjectMultiSelectActive || multiSelectedPolygonIndices.size === 0) return null;
    return [...multiSelectedPolygonIndices].sort((a, b) => a - b);
  }, [detectedObjectMultiSelectActive, multiSelectedPolygonIndices]);

  const getImagePixelSize = useCallback(
    (img) => {
      const aw = roomImageDimensions?.width ?? 0;
      const ah = roomImageDimensions?.height ?? 0;
      const useApi = aw > 0 && ah > 0;
      if (useApi) return { width: aw, height: ah };
      const nw = img ? img.naturalWidth || img.width || 0 : 0;
      const nh = img ? img.naturalHeight || img.height || 0 : 0;
      return { width: nw > 0 ? nw : 1, height: nh > 0 ? nh : 1 };
    },
    [roomImageDimensions],
  );

  const canPlaceCuratedOnCanvas = Boolean(
    image &&
    !swapped &&
    uploadModelMaskResults.length === 0 &&
    !uploadModelMaskFlowActive &&
    editingLayerIndex == null,
  );

  const showPlacedCuratedModelsOverlay = Boolean(
    image &&
    !swapped &&
    uploadModelMaskResults.length === 0 &&
    !uploadModelMaskFlowActive &&
    (placedCuratedModels.length > 0 || pendingCuratedPlacement),
  );

  const curatedPlacementsForOverlay = useMemo(() => {
    if (!pendingCuratedPlacement) return placedCuratedModels;
    return [...placedCuratedModels, pendingCuratedPlacement];
  }, [placedCuratedModels, pendingCuratedPlacement]);

  useLayoutEffect(() => {
    const el = mainCanvasContainerRef.current;
    if (!el || !canPlaceCuratedOnCanvas) {
      setMainCanvasLayoutSize({ w: 0, h: 0 });
      return undefined;
    }
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) setMainCanvasLayoutSize({ w, h });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [canPlaceCuratedOnCanvas, image?.src, roomImageDimensions]);

  const curatedDragPreviewWidthPx = useMemo(() => {
    const { width: logicalW, height: logicalH } = getImagePixelSize(image);
    return defaultCuratedPlacementScreenWidth(
      mainCanvasLayoutSize.w,
      mainCanvasLayoutSize.h,
      logicalW,
      logicalH,
    );
  }, [mainCanvasLayoutSize, image, getImagePixelSize, roomImageDimensions]);

  const handleCanvasCuratedDragOver = useCallback(
    (event) => {
      if (!canPlaceCuratedOnCanvas) return;
      const types = event.dataTransfer?.types;
      const hasCurated =
        types &&
        (types.includes?.(CURATED_MODEL_DRAG_MIME) ||
          types.includes?.('application/json'));
      if (!hasCurated) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      setCanvasCuratedDropActive(true);
    },
    [canPlaceCuratedOnCanvas],
  );

  const handleCanvasCuratedDragLeave = useCallback((event) => {
    const next = event.relatedTarget;
    if (next && mainCanvasContainerRef.current?.contains(next)) return;
    setCanvasCuratedDropActive(false);
  }, []);

  const handleCanvasCuratedDrop = useCallback(
    async (event) => {
      event.preventDefault();
      setCanvasCuratedDropActive(false);
      if (!canPlaceCuratedOnCanvas) return;
      const model = parseCuratedModelDragPayload(event.dataTransfer);
      if (!model) return;
      const container = mainCanvasContainerRef.current;
      const { width: logicalW, height: logicalH } = getImagePixelSize(image);
      const pt = clientPointToImageCoords(
        event.clientX,
        event.clientY,
        container,
        logicalW,
        logicalH,
      );
      if (!pt) return;
      let thumbSrc = model.thumbSrc;
      if (roomId != null && model.id != null) {
        const resolved = await resolvePlacementImageSrc(
          { roomObjectId: model.id, thumbSrc: model.thumbSrc },
          roomId,
        );
        if (resolved) thumbSrc = resolved;
      }
      const aspectRatio = await loadImageAspectRatio(thumbSrc);
      const defaultWidth = defaultCuratedPlacementLogicalWidth(logicalW);
      const placementId = `placed-${model.id}-${Date.now()}`;
      setPendingCuratedPlacement({
        placementId,
        roomObjectId: model.id,
        name: model.name,
        category: model.category,
        thumbSrc,
        x: pt.x,
        y: pt.y,
        width: defaultWidth,
        aspectRatio,
      });
      setSelectedPlacedCuratedId(placementId);
    },
    [canPlaceCuratedOnCanvas, image, getImagePixelSize, roomId],
  );

  const handleConfirmCuratedPlacement = useCallback(async () => {
    if (!image || !pendingCuratedPlacement || curatedCompositeLoading) return;
    if (roomId == null || roomImageId == null) {
      setAiNotification({
        task: 'curated_placement',
        status: 'Error',
        message: 'Missing room or active image id.',
      });
      return;
    }

    const placements = [...placedCuratedModels, pendingCuratedPlacement];
    const { width, height } = getImagePixelSize(image);
    const parentRoomImageId = roomImageId;

    setCuratedCompositeLoading(true);
    try {
      const { dataUrl, canvas } = await combineCanvasWithCuratedModels({
        baseImage: image,
        baseImageSrc: photorealisticImage || image.src,
        width,
        height,
        placements,
        roomId,
      });

      const imageFile = await canvasToPngFile(
        canvas,
        `curated-composite-${Date.now()}.png`,
      );
      const uploadResult = await uploadRoomChildImage(roomId, parentRoomImageId, imageFile);

      const newImageId =
        uploadResult?.id ?? uploadResult?.roomImageId ?? uploadResult?.imageId;
      if (newImageId != null) {
        setRoomImageId(newImageId);
        aiInferenceDone.add(newImageId);
      }

      const apiDims = parseRoomImageDimensionsFromApi(uploadResult);
      if (apiDims) setRoomImageDimensions(apiDims);

      const nextUrl = resolveRoomImageUrl(uploadResult) || dataUrl;
      setPhotorealisticImage(nextUrl);

      const nextImg = new Image();
      nextImg.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        nextImg.onload = () => resolve();
        nextImg.onerror = () => reject(new Error('Failed to load combined image'));
        nextImg.src = nextUrl;
      });
      setImage(nextImg);

      setPlacedCuratedModels([]);
      setPendingCuratedPlacement(null);
      setSelectedPlacedCuratedId(null);
      savePlacedCuratedModelsForRoom(roomId, []);

      setAiNotification({
        task: 'curated_placement',
        status: 'Success',
        message: 'Models combined and saved.',
      });

      setShowPolygonsOverlay(false);
      setShowHistoryPanel(false);
      setShowModelStudioPanel(false);
      setShowPromptPanel(true);
      setShowRenderPanel(false);
      setShowExportImagePanel(false);
      setRightPanelCollapsed(false);
      setShowCuratedSavePromptTip(true);
      setCuratedSavePromptTipEpoch((epoch) => epoch + 1);
    } catch (err) {
      console.error('Failed to combine canvas with curated models:', err);
      setAiNotification({
        task: 'curated_placement',
        status: 'Error',
        message: err?.message || 'Failed to combine models into the image.',
      });
    } finally {
      setCuratedCompositeLoading(false);
    }
  }, [
    image,
    pendingCuratedPlacement,
    placedCuratedModels,
    curatedCompositeLoading,
    getImagePixelSize,
    photorealisticImage,
    roomId,
    roomImageId,
    setPhotorealisticImage,
    setRoomImageId,
    setRoomImageDimensions,
  ]);

  const handleCancelCuratedPlacement = useCallback(() => {
    setPendingCuratedPlacement(null);
    setSelectedPlacedCuratedId(null);
  }, []);

  const handleMovePlacedCurated = useCallback((placementId, x, y) => {
    setPendingCuratedPlacement((pending) =>
      pending?.placementId === placementId ? { ...pending, x, y } : pending,
    );
    setPlacedCuratedModels((prev) =>
      prev.map((p) => (p.placementId === placementId ? { ...p, x, y } : p)),
    );
  }, []);

  const handleRemovePlacedCurated = useCallback((placementId) => {
    setPendingCuratedPlacement((pending) =>
      pending?.placementId === placementId ? null : pending,
    );
    setPlacedCuratedModels((prev) => prev.filter((p) => p.placementId !== placementId));
    setSelectedPlacedCuratedId((prev) => (prev === placementId ? null : prev));
  }, []);

  const handleReorderPlacedCurated = useCallback((orderedPlacements) => {
    if (!Array.isArray(orderedPlacements) || orderedPlacements.length === 0) return;
    setPlacedCuratedModels((prev) => {
      const byId = new Map(prev.map((p) => [p.placementId, p]));
      const next = orderedPlacements
        .map((p) => byId.get(p.placementId))
        .filter(Boolean);
      return next.length === prev.length ? next : prev;
    });
  }, []);

  const handleCanvasContainerPointerDown = useCallback((event) => {
    if (event.target.closest('[data-placed-curated-model]')) return;
    setSelectedPlacedCuratedId(null);
  }, []);

  const handleOpenTransformModelsPanel = useCallback(() => {
    setShowTransformModelsPanel(true);
    setTransformModelsPanelCollapsed(false);
    setShowHistoryPanel(false);
    setShowPolygonsOverlay(false);
    setShowMaterialsPanel(false);
    setShowRenderPanel(false);
    setShowExportImagePanel(false);
    setSelectedPolygonIndex(null);
    setShowAllPolygonsOnCanvas(false);
  }, []);

  const handleExportImageCancel = useCallback(() => {
    exportPreviewRunIdRef.current += 1;
    setShowExportImagePanel(false);
    setShowRenderPanel(false);
    setExportPreviewUrl('');
    setExportPreviewResultKey('');
    setExportPreviewGeneratedAt(null);
    setExportResultResolutionId(null);
    setExportPreviewSavedConfig(null);
    setExportPreviewLoading(false);
    setExportPreviewSaveLoading(false);
    setExportRenderLoading(false);
  }, []);

  const handleExportPreviewDiscard = useCallback(() => {
    exportPreviewRunIdRef.current += 1;
    setExportPreviewUrl('');
    setExportPreviewResultKey('');
    setExportPreviewGeneratedAt(null);
    setExportResultResolutionId(null);
    setExportPreviewSavedConfig(null);
    setExportPreviewLoading(false);
  }, []);

  const handleRenderCameraChange = useCallback((cameraAngle) => {
    setRenderCameraAngle({
      azimuth: Number(cameraAngle?.azimuth) || 0,
      elevation: Number(cameraAngle?.elevation) || 0,
      distance: Number(cameraAngle?.distance) || 1,
    });
  }, []);

  const loadRenderHistory = useCallback(async () => {
    if (roomId == null || roomImageId == null) {
      setRenderLibraryHistory([]);
      setRenderLibraryError(null);
      return;
    }

    const refreshId = renderLibraryRefreshIdRef.current + 1;
    renderLibraryRefreshIdRef.current = refreshId;
    setRenderLibraryLoading(true);
    setRenderLibraryError(null);
    try {
      const list = await getRenderHistory(roomId, roomImageId, { renderType: 'room_image' });
      const sorted = sortRenderHistoryNewestFirst(list);
      const enriched = await enrichRenderHistoryItems(sorted);
      if (renderLibraryRefreshIdRef.current !== refreshId) return;
      setRenderLibraryHistory(enriched);
    } catch (err) {
      if (renderLibraryRefreshIdRef.current !== refreshId) return;
      console.error('Failed to load render history:', err);
      setRenderLibraryHistory([]);
      setRenderLibraryError(err?.message || 'Failed to load saved renders.');
    } finally {
      if (renderLibraryRefreshIdRef.current === refreshId) {
        setRenderLibraryLoading(false);
      }
    }
  }, [roomId, roomImageId]);

  const refreshRenderLibraryImageUrls = useCallback(() => {
    if (roomId == null || roomImageId == null) return;

    setRenderLibraryHistory((prev) => {
      if (!prev.length) {
        void loadRenderHistory();
        return prev;
      }

      const refreshId = renderLibraryRefreshIdRef.current + 1;
      renderLibraryRefreshIdRef.current = refreshId;

      void enrichRenderHistoryItems(prev).then((enriched) => {
        if (renderLibraryRefreshIdRef.current !== refreshId) return;
        setRenderLibraryHistory(enriched);
      });
      return prev;
    });
  }, [loadRenderHistory, roomId, roomImageId]);

  const refreshLocalRecentRenders = useCallback(async () => {
    if (roomId == null || roomImageId == null) {
      setLocalRecentRenders([]);
      return;
    }

    const refreshId = localRecentRendersRefreshIdRef.current + 1;
    localRecentRendersRefreshIdRef.current = refreshId;
    setLocalRecentRendersLoading(true);
    try {
      const stored = loadLocalRenderResults(roomId, roomImageId);
      const enriched = await enrichLocalRenderResults(stored);
      if (localRecentRendersRefreshIdRef.current !== refreshId) return;
      setLocalRecentRenders(enriched);
    } catch (err) {
      if (localRecentRendersRefreshIdRef.current !== refreshId) return;
      console.error('Failed to load local render results:', err);
      setLocalRecentRenders([]);
    } finally {
      if (localRecentRendersRefreshIdRef.current === refreshId) {
        setLocalRecentRendersLoading(false);
      }
    }
  }, [roomId, roomImageId]);

  const refreshLocalRecentFlythroughs = useCallback(async () => {
    if (roomId == null || roomImageId == null) {
      setLocalRecentFlythroughs([]);
      return;
    }

    const refreshId = localRecentFlythroughsRefreshIdRef.current + 1;
    localRecentFlythroughsRefreshIdRef.current = refreshId;
    setLocalRecentFlythroughsLoading(true);
    try {
      const stored = loadLocalFlythroughResults(roomId, roomImageId);
      const enriched = await enrichLocalFlythroughResults(stored);
      if (localRecentFlythroughsRefreshIdRef.current !== refreshId) return;
      setLocalRecentFlythroughs(enriched);
    } catch (err) {
      if (localRecentFlythroughsRefreshIdRef.current !== refreshId) return;
      console.error('Failed to load local flythrough results:', err);
      setLocalRecentFlythroughs([]);
    } finally {
      if (localRecentFlythroughsRefreshIdRef.current === refreshId) {
        setLocalRecentFlythroughsLoading(false);
      }
    }
  }, [roomId, roomImageId]);

  const recentSavedRenderHistory = localRecentRenders;

  const pendingFlythroughVideoCount = pendingFlythroughVideoJobs.length;
  const flythroughGenerateVideoLoading = pendingFlythroughVideoCount > 0;
  const flythroughGenerateAtCapacity =
    pendingFlythroughVideoCount >= MAX_CONCURRENT_FLYTHROUGH_VIDEO_TASKS;
  const flythroughGenerateStatusLabel = useMemo(() => {
    const jobs = [...pendingFlythroughVideoJobs].sort(
      (a, b) => Number(b?.submittedAt ?? 0) - Number(a?.submittedAt ?? 0),
    );
    return jobs[0]?.statusLabel ?? '';
  }, [pendingFlythroughVideoJobs]);

  const pendingExportRenderCount = pendingExportRenderJobs.length;
  const exportRenderAtCapacity =
    pendingExportRenderCount >= MAX_CONCURRENT_ROOM_IMAGE_RENDER_TASKS;
  const exportRenderLoading = pendingExportRenderCount > 0;

  const renderHistorySaveLimitReached =
    renderLibraryHistory.length >= SERVER_RENDER_HISTORY_SAVE_LIMIT;

  const flythroughGeneratedAlreadySaved = useMemo(
    () =>
      isFlythroughResultAlreadyInHistory(
        { resultVideoS3Key: flythroughGeneratedVideoS3Key },
        flythroughLibraryHistory,
      ),
    [flythroughGeneratedVideoS3Key, flythroughLibraryHistory],
  );

  const loadFlythroughHistory = useCallback(async () => {
    if (roomId == null || roomImageId == null) {
      setFlythroughLibraryHistory([]);
      setFlythroughLibraryError(null);
      return;
    }

    const refreshId = flythroughLibraryRefreshIdRef.current + 1;
    flythroughLibraryRefreshIdRef.current = refreshId;
    setFlythroughLibraryLoading(true);
    setFlythroughLibraryError(null);
    try {
      let list = await loadFlythroughRenderHistoryEntries(roomId, roomImageId);
      const sorted = sortRenderHistoryNewestFirst(dedupeFlythroughHistoryById(list));
      const enriched = await enrichFlythroughRenderHistoryItems(sorted);
      if (flythroughLibraryRefreshIdRef.current !== refreshId) return;
      setFlythroughLibraryHistory(enriched);
    } catch (err) {
      if (flythroughLibraryRefreshIdRef.current !== refreshId) return;
      console.error('Failed to load flythrough history:', err);
      setFlythroughLibraryHistory([]);
      setFlythroughLibraryError(err?.message || 'Failed to load saved flythroughs.');
    } finally {
      if (flythroughLibraryRefreshIdRef.current === refreshId) {
        setFlythroughLibraryLoading(false);
      }
    }
  }, [roomId, roomImageId]);

  const handleSaveFlythroughToHistory = useCallback(
    async (item) => {
      if (savingFlythroughHistoryItemId != null) return;

      const flythroughS3Key =
        item?.resultVideoS3Key ??
        item?.result_video_s3_key ??
        item?.flythroughS3Key ??
        item?.flythrough_s3_key ??
        '';
      const thumbnailS3Key = item?.thumbnailS3Key ?? item?.thumbnail_s3_key ?? '';

      if (!flythroughS3Key || roomId == null || roomImageId == null) {
        setAiNotification({
          task: 'generate_flythrough',
          status: 'Failed',
          message: 'Missing room or flythrough result.',
        });
        return;
      }
      if (isFlythroughResultAlreadyInHistory(item, flythroughLibraryHistory)) return;

      const settings = extractFlythroughSettingsFromLocalItem(item);
      if (!settings) {
        setAiNotification({
          task: 'generate_flythrough',
          status: 'Failed',
          message: 'Missing flythrough configuration for this result.',
        });
        return;
      }

      const itemId = item?.id ?? `flythrough-${Date.now()}`;
      setSavingFlythroughHistoryItemId(itemId);
      try {
        setAiNotification({
          task: 'generate_flythrough',
          status: 'Pending',
          message: 'Saving flythrough to library…',
        });

        const body = buildFlythroughRenderHistoryRequestBody({
          flythroughS3Key,
          thumbnailS3Key,
          motionMode: settings.motionMode,
          cameraPreset: settings.cameraPreset,
          cameraAngleSequence: settings.cameraAngleSequence,
          motionPromptFields: settings.motionPromptFields,
          roiMode: settings.roiMode,
          roi: settings.roi,
          durationSeconds: settings.durationSeconds,
          fps: settings.frameRate,
          resolution: settings.renderResolution,
        });

        if (item?.videoUrl) {
          rememberFlythroughPlaybackUrl(flythroughS3Key, item.videoUrl);
        }

        await saveRenderHistory(roomId, roomImageId, body);
        const list = await loadFlythroughRenderHistoryEntries(roomId, roomImageId);
        const sorted = sortRenderHistoryNewestFirst(dedupeFlythroughHistoryById(list));
        const enriched = await enrichFlythroughRenderHistoryItems(sorted);
        setFlythroughLibraryHistory(enriched);

        setAiNotification({
          task: 'generate_flythrough',
          status: 'Success',
          message: 'Flythrough saved to library.',
        });
      } catch (err) {
        console.error('Failed to save flythrough to history:', err);
        setAiNotification({
          task: 'generate_flythrough',
          status: 'Failed',
          message: err?.message || 'Failed to save flythrough.',
        });
      } finally {
        setSavingFlythroughHistoryItemId(null);
      }
    },
    [
      flythroughLibraryHistory,
      loadFlythroughHistory,
      roomId,
      roomImageId,
      savingFlythroughHistoryItemId,
    ],
  );

  const handleFlythroughExportSaveToLibrary = useCallback(() => {
    if (!flythroughGeneratedVideoS3Key) return;
    void handleSaveFlythroughToHistory({
      id: 'export-generated',
      resultVideoS3Key: flythroughGeneratedVideoS3Key,
      thumbnailS3Key: flythroughGeneratedThumbnailS3Key,
      flythroughSettings: flythroughGeneratedSavedSettings,
    });
  }, [
    flythroughGeneratedSavedSettings,
    flythroughGeneratedThumbnailS3Key,
    flythroughGeneratedVideoS3Key,
    handleSaveFlythroughToHistory,
  ]);

  const handleSavedRenderSelect = useCallback((item) => {
    const url = item?.lightboxImageUrl ?? item?.imageUrl;
    if (typeof url === 'string' && url.trim()) {
      setSavedRenderLightboxItemId(item?.id ?? null);
      setSavedRenderLightboxUrl(url.trim());
    }
  }, []);

  const handleSavedRenderCompare = useCallback(() => {
    setSavedRenderCompareActive((active) => {
      if (active) {
        setSavedRenderCompareSelection([]);
        setSavedRenderCompareModalOpen(false);
      }
      return !active;
    });
  }, []);

  const getRenderCompareItemLabel = useCallback((item) => {
    const lightingId =
      item?.renderSettings?.lightingId ??
      item?.payload?.lighting ??
      item?.payload?.config?.lighting;
    if (lightingId == null || lightingId === 'default') {
      const option = RENDER_LIGHTING_OPTIONS.find((entry) => entry.apiValue == null);
      return option?.label ?? 'Default';
    }
    const option = RENDER_LIGHTING_OPTIONS.find(
      (entry) => entry.id === lightingId || entry.apiValue === lightingId,
    );
    return option?.label ?? String(lightingId);
  }, []);

  const handleCompareRenderToggle = useCallback((item) => {
    const itemId = item?.id;
    if (itemId == null) return;
    const id = String(itemId);
    setSavedRenderCompareSelection((prev) => {
      if (prev.includes(id)) {
        setSavedRenderCompareModalOpen(false);
        return prev.filter((entryId) => entryId !== id);
      }
      if (prev.length >= 2) return prev;
      const next = [...prev, id];
      if (next.length === 2) {
        setSavedRenderCompareModalOpen(true);
      }
      return next;
    });
  }, []);

  const savedRenderComparePair = useMemo(() => {
    if (savedRenderCompareSelection.length !== 2) return null;
    const [leftId, rightId] = savedRenderCompareSelection;
    const leftItem = recentSavedRenderHistory.find((entry) => String(entry?.id) === leftId);
    const rightItem = recentSavedRenderHistory.find((entry) => String(entry?.id) === rightId);
    const leftSrc = leftItem?.lightboxImageUrl ?? leftItem?.imageUrl;
    const rightSrc = rightItem?.lightboxImageUrl ?? rightItem?.imageUrl;
    if (!leftSrc || !rightSrc) return null;
    return {
      leftSrc,
      rightSrc,
      leftLabel: getRenderCompareItemLabel(leftItem),
      rightLabel: getRenderCompareItemLabel(rightItem),
    };
  }, [getRenderCompareItemLabel, recentSavedRenderHistory, savedRenderCompareSelection]);

  const handleRenderDeleteRequest = useCallback((item) => {
    if (item?.id == null) return;
    setRenderDeleteConfirmItem(item);
  }, []);

  const handleSaveLocalRenderToHistory = useCallback(
    async (item) => {
      if (savingRenderHistoryItemId != null) return;
      const resultS3Key = item?.resultS3Key ?? item?.result_s3_key;
      if (!resultS3Key || roomId == null || roomImageId == null) {
        setAiNotification({
          task: 'room_image_render',
          status: 'Failed',
          message: 'Missing room or render result.',
        });
        return;
      }
      if (renderHistorySaveLimitReached) return;

      const renderSettings = extractRenderSettingsFromLocalRenderItem(item);
      if (!renderSettings) {
        setAiNotification({
          task: 'room_image_render',
          status: 'Failed',
          message: 'Missing render configuration for this result.',
        });
        return;
      }

      setSavingRenderHistoryItemId(item.id);
      try {
        setAiNotification({
          task: 'room_image_render',
          status: 'Pending',
          message: 'Saving render to library…',
        });

        const body = buildRenderHistoryRequestBody({
          ...renderSettings,
          resultS3Key,
        });

        await saveRenderHistory(roomId, roomImageId, body);
        await loadRenderHistory();

        setAiNotification({
          task: 'room_image_render',
          status: 'Success',
          message: 'Render saved to library.',
        });
      } catch (err) {
        console.error('Failed to save local render to history:', err);
        setAiNotification({
          task: 'room_image_render',
          status: 'Failed',
          message: err?.message || 'Failed to save render.',
        });
      } finally {
        setSavingRenderHistoryItemId(null);
      }
    },
    [
      loadRenderHistory,
      renderHistorySaveLimitReached,
      roomId,
      roomImageId,
      savingRenderHistoryItemId,
    ],
  );

  const handleRenderDeleteConfirmClose = useCallback(() => {
    if (deletingRenderHistoryId != null) return;
    setRenderDeleteConfirmItem(null);
  }, [deletingRenderHistoryId]);

  const handleDeleteRenderHistory = useCallback(async () => {
    const item = renderDeleteConfirmItem;
    const entryId = item?.id;
    if (roomId == null || roomImageId == null || entryId == null) return;
    if (deletingRenderHistoryId != null) return;

    setDeletingRenderHistoryId(entryId);
    try {
      if (item?.isLocal) {
        removeLocalRenderResult(roomId, roomImageId, entryId);
        setLocalRecentRenders((prev) =>
          prev.filter((entry) => String(entry?.id) !== String(entryId)),
        );
      } else {
        await deleteRenderHistory(roomId, roomImageId, entryId);
        setRenderLibraryHistory((prev) =>
          prev.filter((entry) => Number(entry?.id) !== Number(entryId)),
        );
      }

      if (String(savedRenderLightboxItemId) === String(entryId)) {
        setSavedRenderLightboxItemId(null);
        setSavedRenderLightboxUrl('');
      }
      setRenderDeleteConfirmItem(null);
    } catch (err) {
      console.error('Failed to delete render:', err);
      setAiNotification({
        task: 'room_image_render',
        status: 'Failed',
        message: err?.message || 'Failed to delete render.',
      });
    } finally {
      setDeletingRenderHistoryId(null);
    }
  }, [
    deletingRenderHistoryId,
    renderDeleteConfirmItem,
    roomId,
    roomImageId,
    savedRenderLightboxItemId,
  ]);

  useEffect(() => {
    if (roomImageId == null) return undefined;
    if (!showRenderPanel && !showExportImagePanel && !showFlythroughPanel && localRecentRenders.length === 0) {
      return undefined;
    }
    void loadRenderHistory();
  }, [
    showRenderPanel,
    showExportImagePanel,
    showFlythroughPanel,
    loadRenderHistory,
    roomImageId,
    localRecentRenders.length,
  ]);

  useEffect(() => {
    if (roomImageId == null || !showFlythroughPanel) return undefined;
    void loadFlythroughHistory();
  }, [showFlythroughPanel, loadFlythroughHistory, roomImageId]);

  useEffect(() => {
    void refreshLocalRecentRenders();
  }, [refreshLocalRecentRenders]);

  useEffect(() => {
    void refreshLocalRecentFlythroughs();
  }, [refreshLocalRecentFlythroughs]);

  useEffect(() => {
    const prevRoomImageId = prevRoomImageIdForRenderQueueRef.current;
    if (prevRoomImageId != null && prevRoomImageId !== roomImageId) {
      clearRoomImageRenderQueue(prevRoomImageId);
    }
    prevRoomImageIdForRenderQueueRef.current = roomImageId;

    if (roomImageId == null) {
      setRenderLibraryHistory([]);
      setRenderLibraryError(null);
      setLocalRecentRenders([]);
      setLocalRecentFlythroughs([]);
      setPendingFlythroughVideoJobs([]);
      setFlythroughGeneratedVideoSrc('');
      setFlythroughGeneratedThumbnailSrc('');
      setSavedRenderLightboxUrl('');
      setSavedRenderLightboxItemId(null);
    }
    clearRoomImageRenderQueue(roomImageId);
    setPendingExportRenderJobs([]);
    setSavedRenderCompareActive(false);
    setSavedRenderCompareSelection([]);
    setSavedRenderCompareModalOpen(false);
    setFlythroughRoiMode('full_screen');
    setFlythroughRoi(FLYTHROUGH_ROI_FULL_SCREEN);
    setFlythroughCameraPreset('dolly_in');
    setFlythroughMotionMode(FLYTHROUGH_MOTION_MODES.CAMERA_PRESET);
    setFlythroughCameraAngleSequence([FLYTHROUGH_CANONICAL_CAMERA_ANGLE]);
    setFlythroughDraftCameraAngle(FLYTHROUGH_CANONICAL_CAMERA_ANGLE);
    setFlythroughMotionPromptFields({ ...FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT });
    setFlythroughDurationSeconds(FLYTHROUGH_DURATION_DEFAULT_SECONDS);
    setFlythroughRenderResolution('432p');
    setFlythroughFrameRate('24');
    setFlythroughSelectedRenderIds([]);
    setFlythroughVideoPreviewSrc('');
    setFlythroughThumbnailSrc('');
    setFlythroughPreviewVideoLoading(false);
    setFlythroughPreviewStatusLabel('');
  }, [roomImageId]);

  const handleFlythroughMotionModeChange = useCallback((nextMode) => {
    setFlythroughMotionMode(nextMode);
    if (nextMode === FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE) {
      setFlythroughDurationSeconds(
        resolveFlythroughDurationForCameraAngleSequence(flythroughCameraAngleSequence.length),
      );
    }
  }, [flythroughCameraAngleSequence.length]);

  const handleFlythroughCameraAngleSequenceChange = useCallback((nextSequence) => {
    setFlythroughCameraAngleSequence(nextSequence);
    if (flythroughMotionMode === FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE) {
      setFlythroughDurationSeconds(
        resolveFlythroughDurationForCameraAngleSequence(nextSequence.length),
      );
    }
  }, [flythroughMotionMode]);

  const handleFlythroughMotionPromptFieldChange = useCallback((fieldId, value) => {
    setFlythroughMotionPromptFields((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const handleFlythroughRoiModeChange = useCallback((nextMode) => {
    setFlythroughRoiMode(nextMode);
    if (nextMode === 'full_screen') {
      setFlythroughRoi(FLYTHROUGH_ROI_FULL_SCREEN);
    } else if (nextMode === 'custom') {
      setFlythroughRoi((prev) => {
        const isFullScreen =
          Number(prev?.x) === 0 &&
          Number(prev?.y) === 0 &&
          Number(prev?.width) === 1 &&
          Number(prev?.height) === 1;
        return clampFlythroughRoi(isFullScreen ? FLYTHROUGH_ROI_DEFAULT : prev);
      });
    }
  }, []);

  const handleFlythroughRoiChange = useCallback((nextRoi) => {
    setFlythroughRoi(clampFlythroughRoi(nextRoi));
    setFlythroughRoiMode('custom');
  }, []);

  useEffect(() => {
    if (flythroughSelectedRenderIds.length >= 2) {
      setFlythroughMotionMode(FLYTHROUGH_MOTION_MODES.KEYFRAME_STITCHING);
      return;
    }
    setFlythroughMotionMode((current) =>
      current === FLYTHROUGH_MOTION_MODES.KEYFRAME_STITCHING
        ? FLYTHROUGH_MOTION_MODES.CAMERA_PRESET
        : current,
    );
  }, [flythroughSelectedRenderIds.length]);

  const flythroughPreviewDisabled = useMemo(() => {
    if (flythroughMotionMode !== FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE) return false;
    return !isFlythroughPreviewAllowedForCameraAngleSequence(flythroughCameraAngleSequence.length);
  }, [flythroughMotionMode, flythroughCameraAngleSequence.length]);

  const flythroughExportMetadata = useMemo(
    () => ({
      projectName: roomId != null ? `Room ${roomId}` : '—',
      renderHistoryId: flythroughSelectedRenderIds.length
        ? flythroughSelectedRenderIds.join(', ')
        : 'Current room image',
      cameraPreset: getFlythroughCameraPresetLabel(flythroughCameraPreset),
      flythroughRoi: formatFlythroughRoiLabel(flythroughRoiMode, flythroughRoi),
      resolution: getFlythroughResolutionLabel(flythroughRenderResolution),
      duration: `${flythroughDurationSeconds}s`,
    }),
    [
      roomId,
      flythroughSelectedRenderIds,
      flythroughCameraPreset,
      flythroughRoiMode,
      flythroughRoi,
      flythroughRenderResolution,
      flythroughDurationSeconds,
    ],
  );

  const flythroughRenderOutputSrc = useMemo(() => {
    if (!flythroughSelectedRenderIds.length) return '';
    const firstId = flythroughSelectedRenderIds[0];
    const item = renderLibraryHistory.find((entry) => String(entry?.id) === String(firstId));
    return item?.imageUrl ?? item?.lightboxImageUrl ?? '';
  }, [flythroughSelectedRenderIds, renderLibraryHistory]);

  const shouldUseFlythroughRoiPreviewImage =
    showFlythroughPanel &&
    flythroughRoiMode === 'custom' &&
    flythroughSelectedRenderIds.length === 1 &&
    !swapped;

  useEffect(() => {
    if (!shouldUseFlythroughRoiPreviewImage) {
      setFlythroughRoiPreviewImage(null);
      return;
    }

    const src = flythroughRenderOutputSrc?.trim();
    if (!src) {
      setFlythroughRoiPreviewImage(null);
      return;
    }

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (!cancelled) setFlythroughRoiPreviewImage(img);
    };
    img.onerror = () => {
      if (!cancelled) setFlythroughRoiPreviewImage(null);
    };
    img.src = src;

    return () => {
      cancelled = true;
    };
  }, [shouldUseFlythroughRoiPreviewImage, flythroughRenderOutputSrc]);

  const activeCanvasImage =
    shouldUseFlythroughRoiPreviewImage && flythroughRoiPreviewImage
      ? flythroughRoiPreviewImage
      : image;
  const canvasReferenceImagePixelSize = useMemo(() => {
    const rw = Number(roomImageDimensions?.width);
    const rh = Number(roomImageDimensions?.height);
    if (Number.isFinite(rw) && rw > 0 && Number.isFinite(rh) && rh > 0) {
      return { width: rw, height: rh };
    }
    const { width, height } = getImagePixelSize(image);
    if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
      return { width, height };
    }
    return null;
  }, [roomImageDimensions, image, getImagePixelSize]);

  const flythroughExportThumbnailSrc = useMemo(() => {
    if (flythroughThumbnailSrc?.trim()) return flythroughThumbnailSrc;
    if (flythroughRenderOutputSrc?.trim()) return flythroughRenderOutputSrc;
    return '';
  }, [flythroughThumbnailSrc, flythroughRenderOutputSrc]);

  const handleFlythroughCancelTasks = useCallback(async () => {
    if (!flythroughPreviewVideoLoading && !flythroughGenerateVideoLoading) return;

    const taskIds = new Set();
    if (flythroughPreviewTaskIdRef.current) {
      taskIds.add(flythroughPreviewTaskIdRef.current);
    }
    pendingFlythroughVideoJobs.forEach((job) => {
      if (job?.taskId) taskIds.add(String(job.taskId));
      if (job?.jobId) flythroughCancelledJobIdsRef.current.add(job.jobId);
    });

    flythroughPreviewRunIdRef.current += 1;
    flythroughPreviewTaskIdRef.current = null;

    await Promise.allSettled(
      [...taskIds].map((taskId) =>
        cancelAiInferenceTask(taskId).catch((err) => {
          console.warn(`Failed to cancel flythrough task ${taskId}:`, err);
        }),
      ),
    );

    [...taskIds].forEach((taskId) => {
      cancelTask(taskId, 'Flythrough cancelled.');
    });

    setPendingFlythroughVideoJobs([]);
    setFlythroughPreviewVideoLoading(false);
    setFlythroughPreviewStatusLabel('');
    setFlythroughVideoPreviewSrc('');
    setFlythroughThumbnailSrc('');
    setAiNotification({
      task: 'generate_flythrough',
      status: '',
      message: 'Flythrough cancelled.',
    });
  }, [
    flythroughPreviewVideoLoading,
    flythroughGenerateVideoLoading,
    pendingFlythroughVideoJobs,
  ]);

  const handleFlythroughPreviewVideo = useCallback(async () => {
    if (flythroughPreviewVideoLoading) return;
    if (flythroughPreviewDisabled) return;
    if (roomImageId == null) {
      setAiNotification({
        task: 'generate_flythrough',
        status: 'Failed',
        message: 'No room image loaded.',
      });
      return;
    }

    flythroughPreviewRunIdRef.current += 1;
    const runId = flythroughPreviewRunIdRef.current;
    flythroughPreviewTaskIdRef.current = null;
    setFlythroughPreviewVideoLoading(true);
    setFlythroughPreviewStatusLabel('on queue');

    try {
      setFlythroughVideoPreviewSrc('');
      setFlythroughThumbnailSrc('');
      setAiNotification({
        task: 'generate_flythrough',
        status: 'Pending',
        message: 'Generating flythrough preview…',
      });

      const result = await submitFlythroughVideoPreview(roomImageId, {
        renderHistoryIds: flythroughSelectedRenderIds,
        motionMode: flythroughMotionMode,
        cameraPreset: flythroughCameraPreset,
        cameraAngleSequence: flythroughCameraAngleSequence,
        motionPromptFields: flythroughMotionPromptFields,
        roiMode: flythroughRoiMode,
        roi: flythroughRoi,
        durationSeconds: flythroughDurationSeconds,
        onStatusChange: setFlythroughPreviewStatusLabel,
        onTaskStarted: (taskId) => {
          if (flythroughPreviewRunIdRef.current !== runId) {
            void cancelAiInferenceTask(taskId).catch((err) => {
              console.warn(`Failed to cancel flythrough preview task ${taskId}:`, err);
            });
            cancelTask(taskId, 'Flythrough cancelled.');
            return;
          }
          flythroughPreviewTaskIdRef.current = taskId;
        },
      });

      if (flythroughPreviewRunIdRef.current !== runId) return;

      setFlythroughVideoPreviewSrc(result.videoUrl);
      setFlythroughThumbnailSrc(result.thumbnailUrl ?? '');
      setAiNotification({
        task: 'generate_flythrough',
        status: 'Success',
        message: 'Flythrough preview ready.',
      });
    } catch (err) {
      if (flythroughPreviewRunIdRef.current !== runId) return;
      if (err?.cancelled) {
        return;
      }
      console.error('Failed to run generate_flythrough preview:', err);
      setFlythroughGenerationFailedMode('preview');
    } finally {
      if (flythroughPreviewRunIdRef.current === runId) {
        flythroughPreviewTaskIdRef.current = null;
        setFlythroughPreviewVideoLoading(false);
        setFlythroughPreviewStatusLabel('');
      }
    }
  }, [
    flythroughPreviewVideoLoading,
    roomImageId,
    flythroughSelectedRenderIds,
    flythroughMotionMode,
    flythroughCameraPreset,
    flythroughCameraAngleSequence,
    flythroughMotionPromptFields,
    flythroughRoiMode,
    flythroughRoi,
    flythroughDurationSeconds,
    flythroughPreviewDisabled,
  ]);

  const handleFlythroughGenerateVideo = useCallback(() => {
    if (flythroughPreviewVideoLoading) return;
    if (flythroughGenerateAtCapacity) {
      setAiNotification({
        task: 'generate_flythrough',
        status: 'Failed',
        message: `Maximum ${MAX_CONCURRENT_FLYTHROUGH_VIDEO_TASKS} flythrough videos queued. Wait for one to finish before starting another.`,
      });
      return;
    }
    if (roomImageId == null) {
      setAiNotification({
        task: 'generate_flythrough',
        status: 'Failed',
        message: 'No room image loaded.',
      });
      return;
    }

    const jobId = `flythrough-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const submittedAt = Date.now();
    const resolvedDurationSeconds = resolveFlythroughFinalDurationSeconds({
      motionMode: flythroughMotionMode,
      durationSeconds: flythroughDurationSeconds,
      cameraAngleSequence: flythroughCameraAngleSequence,
    });
    const flythroughSettings = {
      motionMode: flythroughMotionMode,
      cameraPreset: flythroughCameraPreset,
      cameraAngleSequence: flythroughCameraAngleSequence,
      motionPromptFields: flythroughMotionPromptFields,
      roiMode: flythroughRoiMode,
      roi: flythroughRoi,
      durationSeconds: resolvedDurationSeconds,
      renderResolution: flythroughRenderResolution,
      frameRate: flythroughFrameRate,
      renderHistoryIds: [...flythroughSelectedRenderIds],
    };

    setPendingFlythroughVideoJobs((prev) => [
      ...prev,
      { jobId, statusLabel: 'on queue', submittedAt },
    ]);
    setAiNotification({
      task: 'generate_flythrough',
      status: 'Pending',
      message: 'Generating flythrough video…',
    });

    void (async () => {
      try {
        const result = await submitFlythroughVideoFinal(roomImageId, {
          renderHistoryIds: flythroughSelectedRenderIds,
          motionMode: flythroughMotionMode,
          cameraPreset: flythroughCameraPreset,
          cameraAngleSequence: flythroughCameraAngleSequence,
          motionPromptFields: flythroughMotionPromptFields,
          roiMode: flythroughRoiMode,
          roi: flythroughRoi,
          durationSeconds: resolvedDurationSeconds,
          fps: flythroughFrameRate,
          resolution: flythroughRenderResolution,
          onStatusChange: (statusLabel) => {
            setPendingFlythroughVideoJobs((prev) =>
              prev.map((job) => (job.jobId === jobId ? { ...job, statusLabel } : job)),
            );
          },
          onTaskStarted: (taskId) => {
            if (flythroughCancelledJobIdsRef.current.has(jobId)) {
              flythroughCancelledJobIdsRef.current.delete(jobId);
              void cancelAiInferenceTask(taskId).catch((err) => {
                console.warn(`Failed to cancel flythrough task ${taskId}:`, err);
              });
              cancelTask(taskId, 'Flythrough cancelled.');
              return;
            }
            setPendingFlythroughVideoJobs((prev) =>
              prev.map((job) => (job.jobId === jobId ? { ...job, taskId } : job)),
            );
          },
        });

        if (roomId != null) {
          appendLocalFlythroughResult(roomId, roomImageId, {
            id: result.taskId ?? result.inferenceId ?? `local-${jobId}`,
            taskId: result.taskId,
            inferenceId: result.inferenceId,
            resultVideoS3Key: result.resultVideoS3Key,
            thumbnailS3Key: result.thumbnailS3Key,
            payload: result.payload,
            submittedAt,
            createdAt: result.createdAt,
            startedAt: result.startedAt,
            completedAt: result.completedAt,
            flythroughSettings,
          });
          await refreshLocalRecentFlythroughs();
        }

        setAiNotification({
          task: 'generate_flythrough',
          status: 'Success',
          message: 'Flythrough video ready.',
        });

        setFlythroughGeneratedVideoSrc(result.videoUrl);
        setFlythroughGeneratedThumbnailSrc(result.thumbnailUrl ?? '');
        setFlythroughGeneratedVideoS3Key(result.resultVideoS3Key ?? '');
        setFlythroughGeneratedThumbnailS3Key(result.thumbnailS3Key ?? '');
        setFlythroughGeneratedSavedSettings(flythroughSettings);
      } catch (err) {
        if (err?.cancelled) return;
        console.error('Failed to run generate_flythrough final:', err);
        setFlythroughGenerationFailedMode('generate');
      } finally {
        flythroughCancelledJobIdsRef.current.delete(jobId);
        setPendingFlythroughVideoJobs((prev) => prev.filter((job) => job.jobId !== jobId));
      }
    })();
  }, [
    flythroughPreviewVideoLoading,
    flythroughGenerateAtCapacity,
    roomImageId,
    roomId,
    flythroughSelectedRenderIds,
    flythroughMotionMode,
    flythroughCameraPreset,
    flythroughCameraAngleSequence,
    flythroughMotionPromptFields,
    flythroughRoiMode,
    flythroughRoi,
    flythroughDurationSeconds,
    flythroughRenderResolution,
    flythroughFrameRate,
    refreshLocalRecentFlythroughs,
  ]);

  const handleFlythroughDeleteGenerated = useCallback(() => {
    setFlythroughGeneratedVideoSrc('');
    setFlythroughGeneratedThumbnailSrc('');
    setFlythroughGeneratedVideoS3Key('');
    setFlythroughGeneratedThumbnailS3Key('');
    setFlythroughGeneratedSavedSettings(null);
  }, []);

  const handleFlythroughGenerationFailedClose = useCallback(() => {
    setFlythroughGenerationFailedMode(null);
  }, []);

  const handleFlythroughGenerationFailedRetry = useCallback(() => {
    const mode = flythroughGenerationFailedMode;
    setFlythroughGenerationFailedMode(null);
    if (mode === 'preview') {
      void handleFlythroughPreviewVideo();
      return;
    }
    if (mode === 'generate') {
      handleFlythroughGenerateVideo();
    }
  }, [
    flythroughGenerationFailedMode,
    handleFlythroughGenerateVideo,
    handleFlythroughPreviewVideo,
  ]);

  const handleFlythroughVideoSelect = useCallback((item) => {
    const url = item?.videoUrl;
    if (typeof url === 'string' && url.trim()) {
      setFlythroughVideoLightboxSrc(url.trim());
    }
  }, []);

  const handleFlythroughDownloadVideo = useCallback(async (itemOrUrl, { filenameBase = 'flythrough', itemId = null } = {}) => {
    const setDownloading =
      itemId != null
        ? () => setDownloadingFlythroughId(itemId)
        : () => setDownloadingGeneratedFlythrough(true);
    const clearDownloading =
      itemId != null
        ? () => setDownloadingFlythroughId(null)
        : () => setDownloadingGeneratedFlythrough(false);

    let videoUrl = typeof itemOrUrl === 'string' ? itemOrUrl : itemOrUrl?.videoUrl;
    if ((!videoUrl || !String(videoUrl).trim()) && itemOrUrl && typeof itemOrUrl === 'object') {
      const resolved = await resolveFlythroughItemDisplayUrls(itemOrUrl);
      videoUrl = resolved.videoUrl;
    }

    if (!videoUrl || !String(videoUrl).trim()) {
      setAiNotification({
        task: 'generate_flythrough',
        status: 'Failed',
        message: 'Video download URL unavailable.',
      });
      return;
    }

    setDownloading();
    try {
      await downloadFlythroughVideo(String(videoUrl).trim(), filenameBase);
    } catch (err) {
      console.error('Failed to download flythrough video:', err);
      setAiNotification({
        task: 'generate_flythrough',
        status: 'Failed',
        message: 'Could not download the flythrough video.',
      });
    } finally {
      clearDownloading();
    }
  }, []);

  const handleFlythroughDownloadGeneratedVideo = useCallback(() => {
    if (!flythroughGeneratedVideoSrc?.trim()) return;
    void handleFlythroughDownloadVideo(flythroughGeneratedVideoSrc, {
      filenameBase: 'flythrough',
    });
  }, [flythroughGeneratedVideoSrc, handleFlythroughDownloadVideo]);

  const handleLibraryFlythroughDownload = useCallback((item) => {
    const itemId = item?.id ?? item?.resultVideoS3Key ?? `flythrough-${Date.now()}`;
    const filenameBase = item?.id != null ? `flythrough-${item.id}` : 'flythrough';
    void handleFlythroughDownloadVideo(item, { filenameBase, itemId });
  }, [handleFlythroughDownloadVideo]);

  const handleFlythroughDeleteRequest = useCallback((item) => {
    if (item?.id == null) return;
    setFlythroughDeleteConfirmItem(item);
  }, []);

  const handleFlythroughDeleteConfirmClose = useCallback(() => {
    if (deletingFlythroughId != null) return;
    setFlythroughDeleteConfirmItem(null);
  }, [deletingFlythroughId]);

  const handleDeleteFlythroughLocal = useCallback(async () => {
    const item = flythroughDeleteConfirmItem;
    const entryId = item?.id;
    if (roomId == null || roomImageId == null || entryId == null) return;
    if (deletingFlythroughId != null) return;

    setDeletingFlythroughId(entryId);
    try {
      if (item?.isLocal) {
        removeLocalFlythroughResult(roomId, roomImageId, entryId);
        setLocalRecentFlythroughs((prev) =>
          prev.filter((entry) => String(entry?.id) !== String(entryId)),
        );
      } else {
        await deleteRenderHistory(roomId, roomImageId, entryId);
        setFlythroughLibraryHistory((prev) =>
          prev.filter((entry) => Number(entry?.id) !== Number(entryId)),
        );
      }

      if (flythroughVideoLightboxSrc && String(item?.videoUrl) === flythroughVideoLightboxSrc) {
        setFlythroughVideoLightboxSrc('');
      }
      setFlythroughDeleteConfirmItem(null);
    } catch (err) {
      console.error('Failed to delete flythrough video:', err);
      setAiNotification({
        task: 'generate_flythrough',
        status: 'Failed',
        message: err?.message || 'Failed to delete flythrough video.',
      });
    } finally {
      setDeletingFlythroughId(null);
    }
  }, [
    deletingFlythroughId,
    flythroughDeleteConfirmItem,
    flythroughVideoLightboxSrc,
    roomId,
    roomImageId,
  ]);

  const exportCanvasImageSrc = useMemo(() => {
    if (swapped) {
      if (resultImageElement?.src) return resultImageElement.src;
      if (typeof preview === 'string' && preview.trim()) return preview.trim();
    }
    if (image?.src) return image.src;
    if (typeof photorealisticImage === 'string' && photorealisticImage.trim()) {
      return photorealisticImage.trim();
    }
    return '';
  }, [swapped, resultImageElement, preview, image, photorealisticImage]);

  const exportRenderSettingsMetadata = useMemo(
    () => ({
      camera: formatRenderCameraLabel(renderCameraAngle),
      resolution:
        RENDER_RESOLUTION_OPTIONS.find((item) => item.id === renderResolution)?.label ?? '—',
      lighting:
        RENDER_LIGHTING_OPTIONS.find((item) => item.id === renderLightingPreset)?.label ?? '—',
    }),
    [renderCameraAngle, renderLightingPreset, renderResolution],
  );

  const handleExportGeneratePreview = useCallback(async () => {
    if (exportPreviewLoading) return;
    if (roomImageId == null) {
      setAiNotification({
        task: 'room_image_render',
        status: 'Failed',
        message: 'No room image loaded.',
      });
      return;
    }

    exportPreviewRunIdRef.current += 1;
    const runId = exportPreviewRunIdRef.current;

    try {
      setExportPreviewLoading(true);
      setExportPreviewStatusLabel('on queue');
      setExportPreviewUrl('');
      setExportPreviewResultKey('');
      setExportPreviewGeneratedAt(null);
      setExportResultResolutionId(null);
    setExportPreviewSavedConfig(null);
      setAiNotification({
        task: 'room_image_render',
        status: 'Pending',
        message: 'Generating render preview…',
      });

      const result = await enqueueRoomImageRenderWork(roomImageId, () =>
        submitRoomImageRenderPreview(roomImageId, {
          resolutionId: DEFAULT_RENDER_PREVIEW_RESOLUTION_ID,
          lightingId: renderLightingPreset,
          cameraAngle: renderCameraAngle,
          onStatusChange: setExportPreviewStatusLabel,
        }),
      );

      if (exportPreviewRunIdRef.current !== runId) return;

      setExportPreviewUrl(result.imageUrl);
      setExportPreviewResultKey(result.resultS3Key ?? '');
      setExportPreviewGeneratedAt(new Date());
      setExportResultResolutionId(DEFAULT_RENDER_PREVIEW_RESOLUTION_ID);
      setExportPreviewSavedConfig({
        resolutionId: DEFAULT_RENDER_PREVIEW_RESOLUTION_ID,
        lightingId: renderLightingPreset,
        cameraAngle: { ...renderCameraAngle },
      });
      setAiNotification({
        task: 'room_image_render',
        status: 'Success',
        message: 'Render preview ready.',
      });
    } catch (err) {
      if (exportPreviewRunIdRef.current !== runId) return;
      console.error('Failed to run room_image_render:', err);
      const msg =
        err?.status === 503
          ? 'Service is not available, please try again later.'
          : err?.message || 'Render preview failed.';
      setAiNotification({
        task: 'room_image_render',
        status: 'Failed',
        message: msg,
      });
    } finally {
      if (exportPreviewRunIdRef.current === runId) {
        setExportPreviewLoading(false);
        setExportPreviewStatusLabel('');
      }
    }
  }, [
    exportPreviewLoading,
    roomImageId,
    renderCameraAngle,
    renderLightingPreset,
  ]);

  const handleExportRender = useCallback(() => {
    if (exportPreviewLoading) return;
    if (exportRenderAtCapacity) {
      setAiNotification({
        task: 'room_image_render',
        status: 'Failed',
        message: `Maximum ${MAX_CONCURRENT_ROOM_IMAGE_RENDER_TASKS} renders queued. Wait for one to finish before starting another.`,
      });
      return;
    }
    if (roomImageId == null) {
      setAiNotification({
        task: 'room_image_render',
        status: 'Failed',
        message: 'No room image loaded.',
      });
      return;
    }

    const jobId = `render-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const submittedAt = Date.now();
    const jobSettings = {
      resolutionId: renderResolution,
      lightingId: renderLightingPreset,
      cameraAngle: { ...renderCameraAngle },
    };

    setPendingExportRenderJobs((prev) => [
      ...prev,
      { jobId, statusLabel: 'on queue', submittedAt },
    ]);
    setAiNotification({
      task: 'room_image_render',
      status: 'Pending',
      message: 'Rendering image…',
    });

    void enqueueRoomImageRenderWork(roomImageId, async () => {
      const result = await submitRoomImageRender(roomImageId, {
        ...jobSettings,
        onStatusChange: (statusLabel) => {
          setPendingExportRenderJobs((prev) =>
            prev.map((job) =>
              job.jobId === jobId ? { ...job, statusLabel } : job,
            ),
          );
        },
      });

      if (roomId != null) {
        appendLocalRenderResult(roomId, roomImageId, {
          id: result.taskId ?? result.inferenceId ?? `local-${jobId}`,
          taskId: result.taskId,
          inferenceId: result.inferenceId,
          resultS3Key: result.resultS3Key,
          payload: result.payload,
          submittedAt,
          createdAt: result.createdAt,
          startedAt: result.startedAt,
          completedAt: result.completedAt,
          renderSettings: jobSettings,
        });
        await refreshLocalRecentRenders();
      }

      setAiNotification({
        task: 'room_image_render',
        status: 'Success',
        message: 'Render ready.',
      });

      return result;
    })
      .catch((err) => {
        if (err?.message === 'Render queue cleared') return;
        console.error('Failed to run room_image_render:', err);
        const msg =
          err?.status === 503
            ? 'Service is not available, please try again later.'
            : err?.status === 409
              ? 'Another render is already in progress. Please wait and try again.'
              : err?.message || 'Render failed.';
        setAiNotification({
          task: 'room_image_render',
          status: 'Failed',
          message: msg,
        });
      })
      .finally(() => {
        setPendingExportRenderJobs((prev) => prev.filter((job) => job.jobId !== jobId));
      });
  }, [
    exportPreviewLoading,
    exportRenderAtCapacity,
    roomId,
    roomImageId,
    refreshLocalRecentRenders,
    renderCameraAngle,
    renderLightingPreset,
    renderResolution,
  ]);

  const selectedCuratedPlacement = useMemo(() => {
    if (!selectedPlacedCuratedId) return null;
    if (pendingCuratedPlacement?.placementId === selectedPlacedCuratedId) {
      return pendingCuratedPlacement;
    }
    return placedCuratedModels.find((p) => p.placementId === selectedPlacedCuratedId) ?? null;
  }, [placedCuratedModels, pendingCuratedPlacement, selectedPlacedCuratedId]);

  useEffect(() => {
    setTransformModelsOutputUrl('');
    setTransformModelsOutputResultKey('');
  }, [selectedPlacedCuratedId]);

  const handleTransformModelsCancelPreview = useCallback(() => {
    setTransformModelsOutputUrl('');
    setTransformModelsOutputResultKey('');
  }, []);

  const handleTransformModelsGenerate = useCallback(
    async ({ azimuth, elevation, distance }) => {
      const roomObjectId = Number(selectedCuratedPlacement?.roomObjectId);
      if (!Number.isFinite(roomObjectId) || roomObjectId <= 0) {
        setAiNotification({
          task: 'change_object_angle',
          status: 'Failed',
          message: 'Select a placed model on the canvas first.',
        });
        return;
      }

      const applyAngleResultFromData = async (data, runId) => {
        const resultKey = data?.taskResult?.resultS3Key ?? data?.taskResult?.result_s3_key;
        if (!resultKey) {
          if (changeObjectAngleRunIdRef.current === runId) {
            setChangeObjectAngleLoading(false);
            setAiNotification({
              task: 'change_object_angle',
              status: 'Failed',
              message: 'View change completed without a result image.',
            });
          }
          changeObjectAngleTaskIdRef.current = null;
          return;
        }
        try {
          const presigned = await getAiInferencePresignedUrl(resultKey);
          if (changeObjectAngleRunIdRef.current !== runId) return;
          const rawUrl = presigned.url ?? presigned.presignedUrl;
          const url = rawUrl ? resolveRoomObjectImageSrcForDisplay(String(rawUrl).trim()) : '';
          if (url) {
            setTransformModelsOutputUrl(url);
            setTransformModelsOutputResultKey(resultKey);
            setAiNotification({
              task: 'change_object_angle',
              status: 'Success',
              message: 'Preview ready in Transform Models panel.',
            });
          }
        } catch (err) {
          console.error('Failed to fetch presigned URL for change_object_angle result:', err);
          if (changeObjectAngleRunIdRef.current === runId) {
            setAiNotification({
              task: 'change_object_angle',
              status: 'Failed',
              message: err?.message || 'Could not load the result image.',
            });
          }
        } finally {
          if (changeObjectAngleRunIdRef.current === runId) {
            setChangeObjectAngleLoading(false);
          }
          changeObjectAngleTaskIdRef.current = null;
        }
      };

      let runId = 0;
      try {
        changeObjectAngleRunIdRef.current += 1;
        runId = changeObjectAngleRunIdRef.current;
        setChangeObjectAngleLoading(true);
        setAiNotification({
          task: 'change_object_angle',
          status: 'Pending',
          message: 'Updating object view…',
        });

        const payload = {
          task_name: 'change_object_angle',
          room_object_id: roomObjectId,
          azimuth,
          elevation,
          distance,
        };
        console.log('POST /ai-inference change_object_angle payload:', payload);
        const res = await runAiInference(payload);
        if (changeObjectAngleRunIdRef.current !== runId) {
          setChangeObjectAngleLoading(false);
          return;
        }

        const taskId = res?.taskId ?? res?.aiTaskId ?? res?.id;
        const immediateKey = res?.taskResult?.resultS3Key ?? res?.taskResult?.result_s3_key;
        if (taskId != null) {
          changeObjectAngleTaskIdRef.current = taskId;
          addTask(taskId, {
            roomImageId,
            onResolve: async (data) => {
              if (changeObjectAngleRunIdRef.current !== runId) return;
              setAiNotification({
                task: 'change_object_angle',
                status: 'Started',
                message: 'Processing…',
              });
              await applyAngleResultFromData(data, runId);
            },
            onError: (err) => {
              if (changeObjectAngleRunIdRef.current !== runId) return;
              console.error('change_object_angle task failed:', err);
              setChangeObjectAngleLoading(false);
              changeObjectAngleTaskIdRef.current = null;
              setAiNotification({
                task: 'change_object_angle',
                status: 'Failed',
                message: err?.message || 'View update failed.',
              });
            },
          });
        } else if (immediateKey) {
          await applyAngleResultFromData(res, runId);
        } else {
          setChangeObjectAngleLoading(false);
          setAiNotification({
            task: 'change_object_angle',
            status: 'Failed',
            message: 'Could not start view update (missing task id).',
          });
        }
      } catch (err) {
        console.error('Failed to run change_object_angle:', err);
        if (changeObjectAngleRunIdRef.current === runId) {
          setChangeObjectAngleLoading(false);
          
          const msg = err?.status === 503
            ? 'Service is not available, please try again later.'
            : err?.message || 'View update failed to start.';
          
          setAiNotification({
            task: 'change_object_angle',
            status: 'Failed',
            message: msg,
          });
        }
      }
    },
    [selectedCuratedPlacement, roomImageId],
  );

  const handleTransformModelsApply = useCallback(async () => {
    const placementId = selectedPlacedCuratedId;
    const previewUrl = transformModelsOutputUrl?.trim();
    const resultKey = transformModelsOutputResultKey?.trim();
    const sourcePlacement = selectedCuratedPlacement;
    if (!placementId || !previewUrl || !sourcePlacement || roomId == null) {
      setAiNotification({
        task: 'change_object_angle',
        status: 'Failed',
        message: !placementId
          ? 'Select a placed model on the canvas first.'
          : 'Generate a preview before applying.',
      });
      return;
    }

    const name = sourcePlacement.name ?? 'Model';
    const category = sourcePlacement.category ?? 'Other';

    setTransformModelsPlaceLoading(true);
    try {
      const aspectRatio = await loadImageAspectRatio(previewUrl);
      let uploadUrl = previewUrl;
      if (resultKey) {
        try {
          const presigned = await getAiInferencePresignedUrl(resultKey);
          const raw = presigned?.url ?? presigned?.presignedUrl;
          if (raw) uploadUrl = raw;
        } catch (err) {
          console.warn('Using preview URL for upload; presigned fetch failed:', err);
        }
      }
      const imageFile = await fetchUrlAsPngFileForRoomObject(uploadUrl, name);
      const createRes = await createRoomObject(roomId, {
        name,
        category,
        image: imageFile,
      });
      const newRoomObjectId = parseRoomObjectIdFromApiResponse(createRes);
      if (newRoomObjectId == null) {
        throw new Error('Saved model did not return a room object id.');
      }

      let thumbSrc = await resolveRoomObjectThumbSrcAsync(createRes);
      if (!thumbSrc) {
        try {
          const objects = await getRoomObjects(roomId);
          const created = objects.find((o) => Number(o.id) === newRoomObjectId);
          if (created) {
            thumbSrc = await resolveRoomObjectThumbSrcAsync(created);
          }
        } catch (err) {
          console.warn('Failed to resolve thumbnail for new room object:', err);
        }
      }
      if (!thumbSrc) {
        thumbSrc = previewUrl;
      }

      setPlacedCuratedModels((prev) =>
        prev.map((p) =>
          p.placementId === placementId
            ? {
                ...p,
                roomObjectId: newRoomObjectId,
                name,
                category,
                thumbSrc,
                aspectRatio,
              }
            : p,
        ),
      );

      setStudioCuratedLibraryItems((prev) => {
        const idStr = String(newRoomObjectId);
        const nextItem = { id: newRoomObjectId, name, category, thumbSrc };
        if (prev.some((x) => String(x.id) === idStr)) {
          return prev.map((x) => (String(x.id) === idStr ? { ...x, ...nextItem } : x));
        }
        return [...prev, nextItem];
      });

      setTransformModelsOutputUrl('');
      setTransformModelsOutputResultKey('');

      setAiNotification({
        task: 'change_object_angle',
        status: 'Success',
        message: 'New model saved, added to Curated Models',
      });
    } catch (err) {
      console.error('Failed to apply transformed model:', err);
      setAiNotification({
        task: 'change_object_angle',
        status: 'Failed',
        message: err?.message || 'Could not save and apply the new model.',
      });
    } finally {
      setTransformModelsPlaceLoading(false);
    }
  }, [
    selectedPlacedCuratedId,
    selectedCuratedPlacement,
    transformModelsOutputUrl,
    transformModelsOutputResultKey,
    roomId,
  ]);

  const canvasLogicalSize = useMemo(
    () => getImagePixelSize(image),
    [image, getImagePixelSize],
  );

  const handleResizePlacedCurated = useCallback(
    (placementId, width) => {
      const w = Number(width);
      if (!Number.isFinite(w)) return;
      const minW = 32;
      const maxW = Math.max(minW, canvasLogicalSize.width * 0.6);
      const nextW = Math.min(maxW, Math.max(minW, w));
      setPendingCuratedPlacement((pending) =>
        pending?.placementId === placementId ? { ...pending, width: nextW } : pending,
      );
      setPlacedCuratedModels((prev) =>
        prev.map((p) => (p.placementId === placementId ? { ...p, width: nextW } : p)),
      );
    },
    [canvasLogicalSize.width],
  );

  /** Same cw×ch as ImageWithPolygons draw + MAX_CANVAS_SIZE so compare matches main canvas. */
  const compareViewLayoutSize = useMemo(() => {
    const img = image;
    if (!img?.src) return { cw: 0, ch: 0 };
    const aw = roomImageDimensions?.width ?? 0;
    const ah = roomImageDimensions?.height ?? 0;
    const useApi = aw > 0 && ah > 0;
    const nw = useApi ? aw : img.naturalWidth || img.width || 0;
    const nh = useApi ? ah : img.naturalHeight || img.height || 0;
    if (!nw || !nh) return { cw: 0, ch: 0 };
    let cw = nw;
    let ch = nh;
    if (Math.max(cw, ch) > MAX_CANVAS_SIZE) {
      const ratio = MAX_CANVAS_SIZE / Math.max(cw, ch);
      cw = Math.round(nw * ratio);
      ch = Math.round(nh * ratio);
    }
    return { cw, ch };
  }, [image, roomImageDimensions]);

  useLayoutEffect(() => {
    const el = compareCanvasRef.current;
    if (!el || !showCompareSlider || !preview || !image?.src) {
      setCompareFitRect({ ox: 0, oy: 0, rw: 0, rh: 0 });
      return undefined;
    }
    const measure = () => {
      const W = el.clientWidth;
      const H = el.clientHeight;
      const { width: iw, height: ih } = getImagePixelSize(image);
      if (!(W > 0) || !(H > 0) || !(iw > 0) || !(ih > 0)) return;
      setCompareFitRect(getObjectFitContainBox(W, H, iw, ih));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [
    showCompareSlider,
    preview,
    image,
    roomImageDimensions,
    compareViewLayoutSize.cw,
    compareViewLayoutSize.ch,
    getImagePixelSize,
  ]);

  /** Interior DB ids for material_transfer / remove_object: all multi-selected, or one index. */
  const resolveInteriorIdsForSelectedObjects = useCallback(
    (polygonIndex) => {
      const useMulti =
        detectedObjectMultiSelectActive && multiSelectedPolygonIndices.size > 1;
      const indices =
        useMulti
          ? [...multiSelectedPolygonIndices].sort((a, b) => a - b)
          : polygonIndex != null
            ? [polygonIndex]
            : [];
      const out = [];
      for (const i of indices) {
        const item = polygons?.[i];
        const id = item?.id ?? item?.interior_id;
        if (id != null) out.push(Number(id));
      }
      return [...new Set(out)];
    },
    [detectedObjectMultiSelectActive, multiSelectedPolygonIndices, polygons]
  );

  /** Bumps when object selection (or multi-select membership) changes so materials panel can refetch. */
  const materialsPanelSelectionKey = useMemo(() => {
    if (detectedObjectMultiSelectActive && multiSelectedPolygonIndices.size > 1) {
      return [...multiSelectedPolygonIndices].sort((a, b) => a - b).join(',');
    }
    return selectedPolygonIndex != null ? `i:${selectedPolygonIndex}` : '';
  }, [detectedObjectMultiSelectActive, multiSelectedPolygonIndices, selectedPolygonIndex]);

  const filteredMaterialsForInterior = useMemo(() => {
    const query = materialsSearchQuery.trim().toLowerCase();
    if (!query) return materialsForInterior;
    return materialsForInterior.filter((mat) => {
      const label = String(mat?.materialType ?? '').toLowerCase();
      const supplier = String(mat?.supplierName ?? '').toLowerCase();
      return label.includes(query) || supplier.includes(query);
    });
  }, [materialsForInterior, materialsSearchQuery]);

  const interiorsListForLowConfidencePopup = useMemo(() => {
    if (Array.isArray(detectedObjectsFromAPI) && detectedObjectsFromAPI.length > 0) {
      return detectedObjectsFromAPI;
    }
    return Array.isArray(pendingDetectedObjects) ? pendingDetectedObjects : [];
  }, [detectedObjectsFromAPI, pendingDetectedObjects]);

  const hasLowConfidenceInPopupList = useMemo(
    () => interiorsListForLowConfidencePopup.some(isLowConfidenceInterior),
    [interiorsListForLowConfidencePopup]
  );
  const lastSavedAgoText = useMemo(() => {
    if (lastSavedAt == null) return null;
    const sec = Math.max(0, Math.floor((saveStatusNow - lastSavedAt) / 1000));
    return `Last saved ${sec} sec ago`;
  }, [lastSavedAt, saveStatusNow]);

  const handleLowConfidenceIgnore = useCallback(() => {
    const keys = new Set();
    interiorsListForLowConfidencePopup.forEach((item, i) => {
      if (isLowConfidenceInterior(item)) keys.add(getInteriorStableKey(item, i));
    });
    setIgnoredLowConfidenceKeys(keys);
    setHideLowConfidenceBanner(true);
  }, [interiorsListForLowConfidencePopup]);

  const handleLowConfidenceReview = useCallback((returnStep) => {
    lowConfidenceReviewReturnStepRef.current = returnStep;
    setObjectChoiceStep('reviewLowConfidence');
  }, []);

  /**
   * Sync ignored-key + banner state from a concrete interiors array (avoids stale closure when opening
   * Select Objects before React re-renders interiorsListForLowConfidencePopup).
   */
  const applyLowConfidenceStateForObjectList = useCallback((arr) => {
    if (!Array.isArray(arr) || arr.length === 0) {
      setIgnoredLowConfidenceKeys(new Set());
      setHideLowConfidenceBanner(false);
      return;
    }
    if (arr.some(isLowConfidenceInterior)) {
      const keys = new Set();
      arr.forEach((item, i) => {
        if (isLowConfidenceInterior(item)) keys.add(getInteriorStableKey(item, i));
      });
      setIgnoredLowConfidenceKeys(keys);
      setHideLowConfidenceBanner(true);
    } else {
      setIgnoredLowConfidenceKeys(new Set());
      setHideLowConfidenceBanner(false);
    }
  }, []);

  /** Choosing Auto-detect or Choose object implies acknowledging low-confidence regions like "Ignore". */
  const openChooseObjectPopup = useCallback(() => {
    if (hasLowConfidenceInPopupList) {
      handleLowConfidenceIgnore();
    }
    setObjectChoiceStep('choose');
    const { detectedSet, userSet } = buildPreselectedSetsFromPolygons(
      polygons,
      detectedObjectsFromAPI,
      userDefinedObjects
    );
    setSelectedObjectIndices(detectedSet);
    setSelectedUserDefinedIndices(userSet);
  }, [hasLowConfidenceInPopupList, handleLowConfidenceIgnore, polygons, detectedObjectsFromAPI, userDefinedObjects]);

  const clearAutoSavedDraft = useCallback(() => {
    if (!draftStorageKey) return;
    try {
      localStorage.removeItem(draftStorageKey);
      latestDraftPayloadRef.current = null;
      skipNextAutosaveRef.current = true;
      setAutoSaveStatus('synced');
      setLastSavedAt(Date.now());
    } catch (err) {
      console.error('Failed to clear auto-save draft:', err);
    }
  }, [draftStorageKey]);

  useEffect(() => {
    if (!draftStorageKey) return;
    if (suppressNextRecoveryModalRef.current) {
      suppressNextRecoveryModalRef.current = false;
      return;
    }
    try {
      const raw = localStorage.getItem(draftStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!isDraftPayloadValid(parsed)) return;
      setRecoveryDraftPayload(parsed);
      setShowSessionRecoveryModal(true);
    } catch (err) {
      console.error('Failed to load draft for recovery:', err);
    }
  }, [draftStorageKey]);

  useEffect(() => {
    if (!draftStorageKey) return undefined;
    if (showSessionRecoveryModal) return undefined;
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return undefined;
    }
    const payload = serializeDraftState({
      polygons,
      detectedObjectsFromAPI,
      pendingDetectedObjects,
      objectChoiceStep,
      selectedPolygonIndex,
      editingLayerIndex,
      showPolygonsOverlay,
      showModelStudioPanel,
      showHistoryPanel,
      showPromptPanel,
      showRenderPanel,
      showExportImagePanel,
      showFlythroughPanel,
      exportImagePanelCollapsed,
      rightPanelCollapsed,
      userDefinedObjects,
      selectedObjectIndices,
      selectedUserDefinedIndices,
      reviewLowConfidenceEditIndex,
      reviewLowConfidenceSource,
      ignoredLowConfidenceKeys,
      hideLowConfidenceBanner,
      interiorsFromExisting,
      interiorsSavedOnce,
      swapped,
      preview,
      operationTypeForApply,
      lastMaterialTransferResultKey,
      uploadModelMaskResults,
      selectedUploadModelMaskRoomObjectId,
      imageSelectionPanelCollapsed,
      studioCuratedLibraryItems,
      placedCuratedModels,
      pendingCuratedPlacement,
      selectedPlacedCuratedId,
    });
    latestDraftPayloadRef.current = payload;
    setAutoSaveStatus('saving');
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(draftStorageKey, JSON.stringify(payload));
        setAutoSaveStatus('saved');
        setLastSavedAt(Date.now());
      } catch (err) {
        console.error('Failed to auto-save draft:', err);
        setAutoSaveStatus('error');
      }
    }, DRAFT_AUTOSAVE_DELAY_MS);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [
    draftStorageKey,
    polygons,
    detectedObjectsFromAPI,
    pendingDetectedObjects,
    objectChoiceStep,
    selectedPolygonIndex,
    editingLayerIndex,
    showPolygonsOverlay,
    showModelStudioPanel,
    showHistoryPanel,
    showPromptPanel,
    showRenderPanel,
    showExportImagePanel,
    showFlythroughPanel,
    exportImagePanelCollapsed,
    rightPanelCollapsed,
    userDefinedObjects,
    selectedObjectIndices,
    selectedUserDefinedIndices,
    reviewLowConfidenceEditIndex,
    reviewLowConfidenceSource,
    ignoredLowConfidenceKeys,
    hideLowConfidenceBanner,
    interiorsFromExisting,
    interiorsSavedOnce,
    swapped,
    preview,
    operationTypeForApply,
    lastMaterialTransferResultKey,
    uploadModelMaskResults,
    selectedUploadModelMaskRoomObjectId,
    imageSelectionPanelCollapsed,
    studioCuratedLibraryItems,
    placedCuratedModels,
    pendingCuratedPlacement,
    selectedPlacedCuratedId,
    showSessionRecoveryModal,
  ]);

  useEffect(() => {
    if (!draftStorageKey) return undefined;
    const flushDraftOnUnload = () => {
      if (!latestDraftPayloadRef.current) return;
      try {
        localStorage.setItem(draftStorageKey, JSON.stringify(latestDraftPayloadRef.current));
      } catch (err) {
        // Ignore unload-time storage errors.
      }
    };
    window.addEventListener('beforeunload', flushDraftOnUnload);
    return () => window.removeEventListener('beforeunload', flushDraftOnUnload);
  }, [draftStorageKey]);

  const handleRestoreDraft = useCallback(async () => {
    const draft = recoveryDraftPayload?.data;
    if (!draft) {
      setShowSessionRecoveryModal(false);
      return;
    }
    setPolygons(Array.isArray(draft.polygons) ? draft.polygons : null);
    setDetectedObjectsFromAPI(Array.isArray(draft.detectedObjectsFromAPI) ? draft.detectedObjectsFromAPI : []);
    setPendingDetectedObjects(Array.isArray(draft.pendingDetectedObjects) ? draft.pendingDetectedObjects : null);
    setObjectChoiceStep(draft.objectChoiceStep ?? 'initial');
    setSelectedPolygonIndex(typeof draft.selectedPolygonIndex === 'number' ? draft.selectedPolygonIndex : null);
    setEditingLayerIndex(typeof draft.editingLayerIndex === 'number' ? draft.editingLayerIndex : null);
    setShowPolygonsOverlay(Boolean(draft.showPolygonsOverlay));
    setShowModelStudioPanel(Boolean(draft.showModelStudioPanel));
    setShowHistoryPanel(Boolean(draft.showHistoryPanel));
    setShowPromptPanel(Boolean(draft.showPromptPanel));
    const renderPanelOpen = Boolean(draft.showRenderPanel);
    setShowRenderPanel(renderPanelOpen);
    setShowExportImagePanel(renderPanelOpen || Boolean(draft.showExportImagePanel));
    setShowFlythroughPanel(Boolean(draft.showFlythroughPanel));
    setExportImagePanelCollapsed(Boolean(draft.exportImagePanelCollapsed));
    setRightPanelCollapsed(Boolean(draft.rightPanelCollapsed));
    setUserDefinedObjects(Array.isArray(draft.userDefinedObjects) ? draft.userDefinedObjects : []);
    setSelectedObjectIndices(new Set(Array.isArray(draft.selectedObjectIndices) ? draft.selectedObjectIndices : []));
    setSelectedUserDefinedIndices(new Set(Array.isArray(draft.selectedUserDefinedIndices) ? draft.selectedUserDefinedIndices : []));
    setReviewLowConfidenceEditIndex(
      typeof draft.reviewLowConfidenceEditIndex === 'number' ? draft.reviewLowConfidenceEditIndex : null
    );
    setReviewLowConfidenceSource(Array.isArray(draft.reviewLowConfidenceSource) ? draft.reviewLowConfidenceSource : null);
    setIgnoredLowConfidenceKeys(new Set(Array.isArray(draft.ignoredLowConfidenceKeys) ? draft.ignoredLowConfidenceKeys : []));
    setHideLowConfidenceBanner(Boolean(draft.hideLowConfidenceBanner));
    setInteriorsFromExisting(Boolean(draft.interiorsFromExisting));
    setInteriorsSavedOnce(Boolean(draft.interiorsSavedOnce));
    const restoredOperationType =
      draft.operationTypeForApply === 'remove_object' ? 'remove_object' : 'material_transfer';
    setOperationTypeForApply(restoredOperationType);
    const restoredResultKey =
      typeof draft.lastMaterialTransferResultKey === 'string' && draft.lastMaterialTransferResultKey.trim()
        ? draft.lastMaterialTransferResultKey
        : null;
    setLastMaterialTransferResultKey(restoredResultKey);
    const restoredUploadMaskResults = Array.isArray(draft.uploadModelMaskResults)
      ? draft.uploadModelMaskResults
          .map((entry) => {
            const imageUrl = typeof entry?.imageUrl === 'string' ? entry.imageUrl.trim() : '';
            const roomObjectId = Number(entry?.roomObjectId);
            if (!imageUrl || !Number.isFinite(roomObjectId)) return null;
            return {
              roomObjectId,
              imageUrl,
              polygons: Array.isArray(entry?.polygons) ? entry.polygons : [],
            };
          })
          .filter(Boolean)
      : [];
    if (restoredUploadMaskResults.length === 0) {
      const legacyUrl =
        typeof draft.uploadModelMaskResultImageUrl === 'string'
          ? draft.uploadModelMaskResultImageUrl.trim()
          : '';
      if (legacyUrl) {
        restoredUploadMaskResults.push({
          roomObjectId: -1,
          imageUrl: legacyUrl,
          polygons: Array.isArray(draft.polygons) ? draft.polygons : [],
        });
      }
    }
    setUploadModelMaskResults(restoredUploadMaskResults);
    const restoredSelectedRoomObjectId = Number(draft.selectedUploadModelMaskRoomObjectId);
    setSelectedUploadModelMaskRoomObjectId(
      Number.isFinite(restoredSelectedRoomObjectId)
        ? restoredSelectedRoomObjectId
        : restoredUploadMaskResults[0]?.roomObjectId ?? null
    );
    setImageSelectionPanelCollapsed(
      restoredUploadMaskResults.length > 0 ? Boolean(draft.imageSelectionPanelCollapsed) : true
    );
    const restoredCurated = Array.isArray(draft.studioCuratedLibraryItems)
      ? draft.studioCuratedLibraryItems
      : [];
    if (restoredCurated.length > 0) {
      setStudioCuratedLibraryItems(restoredCurated);
      if (roomId != null) {
        saveCuratedModelsForRoom(roomId, restoredCurated);
        curatedHydratedForRoomRef.current = roomId;
        curatedSkipSaveRef.current = true;
      }
    }
    if (Array.isArray(draft.placedCuratedModels)) {
      const restoredPlaced = normalizePlacedCuratedModels(draft.placedCuratedModels).map((p) => ({
        ...p,
        thumbSrc: '',
      }));
      setPlacedCuratedModels(restoredPlaced);
      if (roomId != null) {
        savePlacedCuratedModelsForRoom(roomId, restoredPlaced);
        placedCuratedHydratedForRoomRef.current = roomId;
        placedCuratedSkipSaveRef.current = true;
      }
      if (typeof draft.selectedPlacedCuratedId === 'string' && draft.selectedPlacedCuratedId.trim()) {
        setSelectedPlacedCuratedId(draft.selectedPlacedCuratedId);
      } else if (restoredPlaced.length > 0) {
        setSelectedPlacedCuratedId(restoredPlaced.at(-1)?.placementId ?? null);
      }
    }
    if (draft.pendingCuratedPlacement && typeof draft.pendingCuratedPlacement === 'object') {
      const pending = normalizePlacedCuratedModels([draft.pendingCuratedPlacement])[0];
      if (pending) {
        setPendingCuratedPlacement({ ...pending, thumbSrc: '' });
        setSelectedPlacedCuratedId(pending.placementId);
      }
    }
    const restoredPlacedForThumbs = Array.isArray(draft.placedCuratedModels)
      ? normalizePlacedCuratedModels(draft.placedCuratedModels)
      : [];
    const restoredPendingForThumbs =
      draft.pendingCuratedPlacement && typeof draft.pendingCuratedPlacement === 'object'
        ? normalizePlacedCuratedModels([draft.pendingCuratedPlacement])[0]
        : null;
    const thumbIdList = [
      ...collectRoomObjectIdsFromStudioState(restoredCurated, restoredPlacedForThumbs),
      ...(restoredPendingForThumbs?.roomObjectId != null
        ? [String(restoredPendingForThumbs.roomObjectId)]
        : []),
    ];
    const uniqueThumbIds = [...new Set(thumbIdList)];
    if (roomId != null && uniqueThumbIds.length > 0) {
      try {
        const thumbMap = await fetchRoomObjectThumbMap(roomId, uniqueThumbIds);
        if (restoredCurated.length > 0) {
          setStudioCuratedLibraryItems((prev) =>
            applyThumbMapToCuratedItems(restoredCurated, thumbMap),
          );
        } else {
          setStudioCuratedLibraryItems((prev) => applyThumbMapToCuratedItems(prev, thumbMap));
        }
        if (restoredPlacedForThumbs.length > 0) {
          setPlacedCuratedModels(
            applyThumbMapToPlacedModels(
              restoredPlacedForThumbs.map((p) => ({ ...p, thumbSrc: '' })),
              thumbMap,
            ),
          );
        }
        if (restoredPendingForThumbs) {
          const meta = thumbMap.get(String(restoredPendingForThumbs.roomObjectId));
          setPendingCuratedPlacement({
            ...restoredPendingForThumbs,
            thumbSrc: meta?.thumbSrc || '',
            name: meta?.name ?? restoredPendingForThumbs.name,
            category: meta?.category ?? restoredPendingForThumbs.category,
          });
        }
      } catch (err) {
        console.warn('Failed to refresh thumbnails after session restore:', err);
      }
    }
    let restoredPreviewUrl =
      typeof draft.preview === 'string' && draft.preview.trim()
        ? draft.preview
        : null;
    if (restoredResultKey) {
      try {
        const presigned = await getAiInferencePresignedUrl(restoredResultKey);
        restoredPreviewUrl = presigned?.url ?? presigned?.presignedUrl ?? restoredPreviewUrl;
      } catch (err) {
        console.warn('Failed to refresh presigned URL while restoring draft preview:', err);
      }
    }
    setPreview(restoredPreviewUrl);
    setSwapped(Boolean(draft.swapped && restoredPreviewUrl));
    if (restoredPreviewUrl) {
      const resultImg = new Image();
      resultImg.crossOrigin = 'anonymous';
      resultImg.onload = () => {
        setResultImageElement(resultImg);
        setResultImageLoadError(false);
      };
      resultImg.onerror = () => {
        setResultImageElement(null);
        setResultImageLoadError(true);
      };
      resultImg.src = restoredPreviewUrl;
    } else {
      setResultImageElement(null);
      setResultImageLoadError(false);
    }
    setShowSessionRecoveryModal(false);
    setRecoveryDraftPayload(null);
    setAutoSaveStatus('recovered');
    setLastSavedAt(Date.now());
  }, [recoveryDraftPayload, roomId]);

  const restartRoomImageRestore = useCallback(() => {
    // Force a clean bootstrap from backend room image state.
    setImage(null);
    setError(null);
    setPhotorealisticImage(null);
    setRoomImageDimensions(null);
    setImageRestoreRetryKey((k) => k + 1);
  }, [setPhotorealisticImage, setRoomImageDimensions]);

  const handleDiscardDraft = useCallback(() => {
    if (draftStorageKey) {
      try {
        localStorage.removeItem(draftStorageKey);
      } catch (err) {
        console.error('Failed to discard draft:', err);
      }
    }
    setShowSessionRecoveryModal(false);
    setRecoveryDraftPayload(null);
    setAutoSaveStatus('idle');
    setPendingDetectedObjects(null);
    setDetectedObjectsFromAPI([]);
    setUserDefinedObjects([]);
    setSelectedObjectIndices(new Set());
    setSelectedUserDefinedIndices(new Set());
    setPolygons(null);
    setObjectChoiceStep('initial');
    if (roomImageId != null) {
      aiInferenceDone.delete(roomImageId);
    }
    setInferenceRetryKey((k) => k + 1);
    restartRoomImageRestore();
  }, [draftStorageKey, restartRoomImageRestore, roomImageId]);

  const handleUndoDeletedObject = useCallback(async () => {
    if (!deletedObjectUndo || deletedObjectUndo.restoring) return;
    const batchItems = Array.isArray(deletedObjectUndo.batchItems) ? deletedObjectUndo.batchItems : null;
    const { roomId: deletedRoomId, roomImageId: deletedRoomImageId, interiorId, item, index } = deletedObjectUndo;

    setDeletedObjectUndo((prev) => (prev ? { ...prev, restoring: true } : prev));
    try {
      if (batchItems && batchItems.length > 0) {
        const validBatch = batchItems.filter(
          (x) => x?.roomId != null && x?.roomImageId != null && x?.interiorId != null
        );
        for (const entry of validBatch) {
          await restoreInterior(entry.roomId, entry.roomImageId, entry.interiorId);
        }
        setPolygons((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          const sorted = [...batchItems].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
          for (const entry of sorted) {
            const insertAt = Math.max(0, Math.min(entry.index ?? list.length, list.length));
            list.splice(insertAt, 0, entry.item);
          }
          return list;
        });
        setDetectedObjectsFromAPI((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          const sorted = [...batchItems].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
          for (const entry of sorted) {
            const insertAt = Math.max(0, Math.min(entry.index ?? list.length, list.length));
            list.splice(insertAt, 0, entry.item);
          }
          return list;
        });
      } else {
        if (deletedRoomId == null || deletedRoomImageId == null || interiorId == null) return;
        await restoreInterior(deletedRoomId, deletedRoomImageId, interiorId);
        setPolygons((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          const insertAt = Math.max(0, Math.min(index ?? list.length, list.length));
          list.splice(insertAt, 0, item);
          return list;
        });
        setDetectedObjectsFromAPI((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          const insertAt = Math.max(0, Math.min(index ?? list.length, list.length));
          list.splice(insertAt, 0, item);
          return list;
        });
      }
      setError(null);
      setDeletedObjectUndo(null);
    } catch (err) {
      console.error('Failed to restore interior:', err);
      setError(err.message ?? 'Failed to restore deleted object');
      setDeletedObjectUndo((prev) => (prev ? { ...prev, restoring: false } : prev));
    }
  }, [deletedObjectUndo]);

  const handleDeleteSelectedDetectionsFromPopup = useCallback(async () => {
    const selectedIndices = [...selectedObjectIndices].sort((a, b) => a - b);
    if (selectedIndices.length === 0) return;
    const selectedItems = selectedIndices
      .map((idx) => ({ idx, item: detectedObjectsFromAPI[idx] }))
      .filter((x) => x.item != null);
    if (selectedItems.length === 0) return;

    const hasBlockedStructural = selectedItems.some(({ item, idx }) =>
      isInteriorTypeDeletionBlocked(item, interiorTypesList, idx)
    );
    if (hasBlockedStructural) {
      setAiNotification({
        task: 'delete_detection',
        status: '',
        message: INTERIOR_TYPE_DELETION_BLOCKED_MESSAGE,
      });
      return;
    }

    const confirmMessage =
      selectedItems.length === 1
        ? `Delete detection "${getLabelForInterior(selectedItems[0].item, interiorTypesList, selectedItems[0].idx)}"? This permanently removes it from the server.`
        : `Delete ${selectedItems.length} selected detections? This permanently removes them from the server.`;
    if (!window.confirm(confirmMessage)) return;

    const batchUndo = [];
    try {
      for (const { idx, item } of selectedItems) {
        const interiorId = item?.interior_id ?? item?.id;
        if (roomId != null && roomImageId != null && interiorId != null) {
          await deleteInterior(roomId, roomImageId, interiorId);
          batchUndo.push({
            label: getLabelForInterior(item, interiorTypesList, idx),
            roomId,
            roomImageId,
            interiorId,
            item: { ...item },
            index: idx,
          });
        }
      }

      const removedInteriorIds = new Set(
        selectedItems
          .map(({ item }) => item?.interior_id ?? item?.id)
          .filter((id) => id != null)
          .map((id) => Number(id))
      );
      setPolygons((prev) => {
        if (!Array.isArray(prev)) return prev;
        if (removedInteriorIds.size === 0) return prev;
        return prev.filter((p) => !removedInteriorIds.has(Number(p?.interior_id ?? p?.id)));
      });
      setDetectedObjectsFromAPI((prev) => {
        const removeSet = new Set(selectedIndices);
        return (Array.isArray(prev) ? prev : []).filter((_, idx) => !removeSet.has(idx));
      });
      setSelectedObjectIndices(new Set());
      setDeleteDetectionMode(false);
      setError(null);

      if (batchUndo.length > 0) {
        if (batchUndo.length === 1) {
          const single = batchUndo[0];
          setDeletedObjectUndo({ ...single, restoring: false });
        } else {
          setDeletedObjectUndo({
            label: `${batchUndo.length} detections`,
            batchItems: batchUndo,
            restoring: false,
          });
        }
      }
    } catch (err) {
      console.error('Failed to delete selected detections:', err);
      setError(err?.message ?? 'Failed to delete selected detections');
    }
  }, [
    selectedObjectIndices,
    detectedObjectsFromAPI,
    roomId,
    roomImageId,
    interiorTypesList,
  ]);

  const handleNoImageFileSelect = useCallback(
    async (file) => {
      if (!file) return;
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setNoImageUploadError('Please upload a JPG, PNG, or PDF file');
        return;
      }
      const maxSize = 8 * 1024 * 1024;
      if (file.size > maxSize) {
        setNoImageUploadError('File size must be less than 8MB');
        return;
      }
      if (!roomId) {
        setNoImageUploadError('Please click “Start Designing” first to create a room.');
        return;
      }

      setNoImageUploadLoading(true);
      setNoImageUploadError(null);
      try {
        const uploadResult = await uploadRoomImage(roomId, file);
        const imageId = uploadResult.id;
        if (imageId != null) setRoomImageId(imageId);
        const apiDims = parseRoomImageDimensionsFromApi(uploadResult);
        if (apiDims) setRoomImageDimensions(apiDims);
        else setRoomImageDimensions(null);
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target.result;
          setPhotorealisticImage(dataUrl);
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            setImage(img);
            setError(null);
          };
          img.onerror = () => setError('Failed to load image');
          img.src = dataUrl;
        };
        reader.readAsDataURL(file);
      } catch (err) {
        console.error('Upload from Photorealistic failed:', err);
        setNoImageUploadError(err.message || 'Failed to upload image');
      } finally {
        setNoImageUploadLoading(false);
      }
    },
    [roomId, setRoomImageId, setPhotorealisticImage, setRoomImageDimensions]
  );

  /** Load / refresh materials list for the given polygon row (does not open or close the panel). */
  const syncMaterialsListForPolygonIndex = useCallback(
    async (polygonIndex) => {
      const useMulti =
        detectedObjectMultiSelectActive && multiSelectedPolygonIndices.size > 1;
      const indices =
        useMulti
          ? [...multiSelectedPolygonIndices].sort((a, b) => a - b)
          : polygonIndex != null
            ? [polygonIndex]
            : [];

      const interiorIds = resolveInteriorIdsForSelectedObjects(polygonIndex);

      if (indices.length === 0) {
        setMaterialsForInterior([]);
        setMaterialsInteriorTypeId(null);
        setMaterialsError('No object selected.');
        setMaterialsLoading(false);
        setMaterialsSearchQuery('');
        setShowMaterialModifyPanel(false);
        setPendingMaterialSelection(null);
        setMaterialsTargetInteriorId(null);
        setMaterialsTargetInteriorIds(null);
        return;
      }

      const typeIdsOrdered = [];
      for (const i of indices) {
        const row = polygons?.[i];
        const tid = row?.interior_type_id ?? row?.interiorTypeId ?? null;
        if (tid == null) {
          setMaterialsForInterior([]);
          setMaterialsInteriorTypeId(null);
          setMaterialsError(
            useMulti
              ? 'Every selected object must have an interior type so we can find shared materials.'
              : 'This object has no interior type configured.'
          );
          setMaterialsLoading(false);
          setMaterialsSearchQuery('');
          setShowMaterialModifyPanel(false);
          setPendingMaterialSelection(null);
          setMaterialsTargetInteriorId(null);
          setMaterialsTargetInteriorIds(null);
          return;
        }
        typeIdsOrdered.push(Number(tid));
      }

      const uniqueTypeIds = [...new Set(typeIdsOrdered)];
      setMaterialsInteriorTypeId(uniqueTypeIds.length === 1 ? uniqueTypeIds[0] : null);

      if (interiorIds.length > 1) {
        setMaterialsTargetInteriorId(null);
        setMaterialsTargetInteriorIds(interiorIds);
      } else {
        setMaterialsTargetInteriorId(interiorIds[0] ?? null);
        setMaterialsTargetInteriorIds(null);
      }

      setMaterialsLoading(true);
      setMaterialsError(null);
      setMaterialsSearchQuery('');
      setShowMaterialModifyPanel(false);
      setPendingMaterialSelection(null);
      try {
        const results = await Promise.all(
          uniqueTypeIds.map((tid) => getMaterialsByInteriorType(tid))
        );
        const lists = results.map((r) => (Array.isArray(r.materials) ? r.materials : []));
        let materials;
        if (uniqueTypeIds.length === 1) {
          materials = lists[0] ?? [];
        } else {
          materials = intersectMaterialListsBySupplierId(lists);
          if (materials.length === 0) {
            setMaterialsError(
              'No materials are listed for every selected object type. Select objects that share a category, or apply materials one object at a time.'
            );
          }
        }
        setMaterialsForInterior(materials);
      } catch (err) {
        console.error('Failed to load materials for interior type(s):', uniqueTypeIds, err);
        setMaterialsError(err?.message ?? 'Failed to load materials for this interior type.');
        setMaterialsForInterior([]);
      } finally {
        setMaterialsLoading(false);
      }
    },
    [
      polygons,
      resolveInteriorIdsForSelectedObjects,
      detectedObjectMultiSelectActive,
      multiSelectedPolygonIndices,
    ]
  );

  /** Open the materials panel; list is filled by syncMaterialsListForPolygonIndex (including via useEffect on selection change). */
  const showMaterialsForInterior = useCallback(() => {
    setShowMaterialsPanel(true);
    setMaterialsPanelCollapsed(false);
    setShowHistoryPanel(false);
  }, []);

  const handleApplyMaterialToPolygon = useCallback(
    async (material, modifyOptions = null, behaviorOptions = null) => {
      if (!roomImageId) {
        console.warn('Cannot apply material: roomImageId is missing');
        return;
      }
      const liveSelectedInteriorIds = resolveInteriorIdsForSelectedObjects(selectedPolygonIndex);
      const interiorIdsForApply =
        liveSelectedInteriorIds.length > 0
          ? liveSelectedInteriorIds
          : materialsTargetInteriorIds != null && materialsTargetInteriorIds.length > 0
            ? materialsTargetInteriorIds
            : materialsTargetInteriorId != null
              ? [materialsTargetInteriorId]
              : [];
      if (interiorIdsForApply.length === 0) {
        console.warn('Cannot apply material: room_interior_id(s) missing for selected polygon(s)');
        return;
      }
      if (!material?.id) {
        console.warn('Cannot apply material: supplier_material_id is missing on material object');
        return;
      }
      lastAiInferenceRegenerateContextRef.current = {
        type: 'material_transfer',
        material: {
          id: material.id,
          materialType: material.materialType,
          imageKey: material.imageKey,
          ...(modifyOptions && typeof modifyOptions === 'object'
            ? {
                rotation: modifyOptions.rotation,
                scale: modifyOptions.scale,
              }
            : {}),
        },
      };
      setShowMaterialsGridDuringAiResult(false);
      setPreview(null);
      setSwapped(false);
      setLastMaterialTransferResultKey(null);
      setResultImageElement(null);
      setOperationTypeForApply('material_transfer');
      const payloadModifyFields =
        modifyOptions && typeof modifyOptions === 'object'
          ? {
              rotation: Math.max(0, Math.min(360, Number(modifyOptions.rotation) || 0)),
              scale: Math.max(0, Math.min(2, Number(modifyOptions.scale) || 0)),
            }
          : {};
      const payload =
        interiorIdsForApply.length > 1
          ? {
              task_name: 'material_transfer',
              room_image_id: roomImageId,
              room_interior_ids: interiorIdsForApply,
              supplier_material_id: material.id,
              prompt: '',
              ...payloadModifyFields,
            }
          : {
              task_name: 'material_transfer',
              room_image_id: roomImageId,
              room_interior_id: interiorIdsForApply[0],
              supplier_material_id: material.id,
              prompt: '',
              ...payloadModifyFields,
            };
      console.log('POST /ai-inference material_transfer payload:', payload);
      let materialTransferRunId = 0;
      try {
        setMaterialTransferLastFailed(false);
        materialTransferRunIdRef.current += 1;
        materialTransferRunId = materialTransferRunIdRef.current;
        setMaterialTransferLoading(true);
        setAiNotification({
          task: 'material_transfer',
          status: 'Pending',
          message: 'Material transfer queued...',
        });
        const res = await runAiInference(payload);
        console.log('POST /ai-inference material_transfer response:', res);
        if (materialTransferRunId !== materialTransferRunIdRef.current) {
          setMaterialTransferLoading(false);
          return;
        }
        const taskId = res.taskId ?? res.aiTaskId ?? res.id;
        if (taskId != null) {
          materialTransferTaskIdRef.current = taskId;
          setAiNotification({
            task: 'material_transfer',
            status: 'Started',
            message: 'Material transfer in progress...',
          });
          addTask(taskId, {
            roomImageId,
            onResolve: async (data) => {
              if (materialTransferRunId !== materialTransferRunIdRef.current) return;
              console.log(`material_transfer task ${taskId} resolved:`, data);
              const resultKey =
                data?.taskResult?.resultS3Key ?? data?.taskResult?.result_s3_key;
              if (!resultKey) {
                console.warn(
                  'material_transfer completed without resultS3Key / result_s3_key in taskResult'
                );
                setMaterialTransferLastFailed(true);
                if (materialTransferRunId === materialTransferRunIdRef.current) {
                  setMaterialTransferLoading(false);
                  setAiNotification({
                    task: 'material_transfer',
                    status: 'Failed',
                    message: 'Material transfer completed without a result image.',
                  });
                }
                materialTransferTaskIdRef.current = null;
                return;
              }
              try {
                const presigned = await getAiInferencePresignedUrl(resultKey);
                if (materialTransferRunId !== materialTransferRunIdRef.current) return;
                const url = presigned.url ?? presigned.presignedUrl;
                if (url) {
                  console.log('material_transfer presigned URL:', url);
                  setPreview(url);
                  setSwapped(true);
                  setResultImageLoadError(false);
                  setLastMaterialTransferResultKey(resultKey);
                  setResultImageElement(null);
                  const resultImg = new Image();
                  resultImg.crossOrigin = 'anonymous';
                  resultImg.onload = () => {
                    if (materialTransferRunId !== materialTransferRunIdRef.current) return;
                    setResultImageElement(resultImg);
                    setResultImageLoadError(false);
                    setMaterialTransferLastFailed(false);
                    if (behaviorOptions?.keepModifyPanelOpen) {
                      setShowMaterialModifyPanel(true);
                      setShowMaterialsGridDuringAiResult(true);
                    } else {
                      setPendingMaterialSelection(null);
                      setShowMaterialsGridDuringAiResult(false);
                    }
                    setAiNotification({
                      task: 'material_transfer',
                      status: 'Success',
                      message: 'Material transfer completed. Review the result.',
                    });
                  };
                  resultImg.onerror = () => {
                    if (materialTransferRunId !== materialTransferRunIdRef.current) return;
                    setResultImageElement(null);
                    setResultImageLoadError(true);
                    setMaterialTransferLastFailed(true);
                    setAiNotification({
                      task: 'material_transfer',
                      status: 'Failed',
                      message: 'Could not load the result image.',
                    });
                  };
                  resultImg.src = url;
                } else {
                  console.warn('Presigned URL response missing url field:', presigned);
                  setMaterialTransferLastFailed(true);
                  if (materialTransferRunId === materialTransferRunIdRef.current) {
                    setAiNotification({
                      task: 'material_transfer',
                      status: 'Failed',
                      message: 'Material transfer result URL was not returned.',
                    });
                  }
                }
              } catch (err) {
                console.error('Failed to fetch presigned URL for material_transfer result:', err);
                setMaterialTransferLastFailed(true);
                if (materialTransferRunId === materialTransferRunIdRef.current) {
                  setAiNotification({
                    task: 'material_transfer',
                    status: 'Failed',
                    message: err?.message || 'Could not load the material transfer result.',
                  });
                }
              } finally {
                if (materialTransferRunId === materialTransferRunIdRef.current) {
                  setMaterialTransferLoading(false);
                }
                materialTransferTaskIdRef.current = null;
              }
            },
            onError: (err) => {
              if (materialTransferRunId !== materialTransferRunIdRef.current) return;
              console.error(`material_transfer task ${taskId} failed:`, err);
              setMaterialTransferLastFailed(true);
              setMaterialTransferLoading(false);
              materialTransferTaskIdRef.current = null;
              setAiNotification({
                task: 'material_transfer',
                status: 'Failed',
                message: err?.message || 'Material transfer failed.',
              });
            },
          });
        } else {
          console.warn('material_transfer response without taskId:', res);
          setMaterialTransferLastFailed(true);
          if (materialTransferRunId === materialTransferRunIdRef.current) {
            setMaterialTransferLoading(false);
            setAiNotification({
              task: 'material_transfer',
              status: 'Failed',
              message: 'Material transfer could not be started.',
            });
          }
        }
      } catch (err) {
        console.error('Failed to run material_transfer inference:', err);
        setMaterialTransferLastFailed(true);
        if (materialTransferRunIdRef.current === materialTransferRunId) {
          setMaterialTransferLoading(false);
          setAiNotification({
            task: 'material_transfer',
            status: 'Failed',
            message: err?.message || 'Material transfer failed to start.',
          });
        }
      }
    },
    [
      roomImageId,
      materialsTargetInteriorId,
      materialsTargetInteriorIds,
      resolveInteriorIdsForSelectedObjects,
      selectedPolygonIndex,
    ]
  );

  const executeRemoveObjectInference = useCallback(
    async (polygonIndex) => {
      if (!roomImageId) return;
      const useMulti =
        detectedObjectMultiSelectActive && multiSelectedPolygonIndices.size > 1;
      const blockedIndices = useMulti
        ? [...multiSelectedPolygonIndices].sort((a, b) => a - b)
        : polygonIndex != null
          ? [polygonIndex]
          : [];
      removeObjectLastRunIndicesRef.current = blockedIndices;
      for (const i of blockedIndices) {
        const pItem = polygons?.[i];
        if (pItem != null && isInteriorTypeDeletionBlocked(pItem, interiorTypesList, i)) {
          setAiNotification({
            task: 'remove_object',
            status: '',
            message: INTERIOR_TYPE_DELETION_BLOCKED_MESSAGE,
          });
          return;
        }
      }
      const interiorIdsForRemove = resolveInteriorIdsForSelectedObjects(polygonIndex);
      if (interiorIdsForRemove.length === 0) {
        console.warn('Cannot remove object: room_interior_id(s) missing for selected polygon(s)');
        setAiNotification({
          task: 'remove_object',
          status: 'Please select a region first',
          message: '',
        });
        return;
      }
      lastAiInferenceRegenerateContextRef.current = {
        type: 'remove_object',
        polygonIndex,
        interiorIds: interiorIdsForRemove,
        polygonIndices: blockedIndices,
      };
      setPreview(null);
      setSwapped(false);
      setLastMaterialTransferResultKey(null);
      setResultImageElement(null);
      setOperationTypeForApply('remove_object');
      const payload =
        interiorIdsForRemove.length > 1
          ? {
              task_name: 'remove_object',
              room_image_id: roomImageId,
              room_interior_ids: interiorIdsForRemove,
              prompt: '',
            }
          : {
              task_name: 'remove_object',
              room_image_id: roomImageId,
              room_interior_id: interiorIdsForRemove[0],
              prompt: '',
            };
      console.log('POST /ai-inference remove_object payload:', payload);
      let removeObjectRunId = 0;
      try {
        removeObjectRunIdRef.current += 1;
        removeObjectRunId = removeObjectRunIdRef.current;
        setRemoveObjectLoading(true);
        setAiNotification({
          task: 'remove_object',
          status: 'Pending',
          message: 'Object removal queued...',
        });
        const res = await runAiInference(payload);
        if (removeObjectRunId !== removeObjectRunIdRef.current) {
          setRemoveObjectLoading(false);
          return;
        }
        const taskId = res.taskId ?? res.aiTaskId ?? res.id;
        if (taskId != null) {
          removeObjectTaskIdRef.current = taskId;
          addTask(taskId, {
            roomImageId,
            onResolve: async (data) => {
              if (removeObjectRunId !== removeObjectRunIdRef.current) return;
              setAiNotification({
                task: 'remove_object',
                status: 'Started',
                message: 'Removing object...',
              });
              const resultKey =
                data?.taskResult?.resultS3Key ?? data?.taskResult?.result_s3_key;
              if (!resultKey) {
                if (removeObjectRunId === removeObjectRunIdRef.current) {
                  setRemoveObjectLoading(false);
                }
                removeObjectTaskIdRef.current = null;
                return;
              }
              try {
                const presigned = await getAiInferencePresignedUrl(resultKey);
                if (removeObjectRunId !== removeObjectRunIdRef.current) return;
                const url = presigned.url ?? presigned.presignedUrl;
                if (url) {
                  setPreview(url);
                  setSwapped(true);
                  setResultImageLoadError(false);
                  setLastMaterialTransferResultKey(resultKey);
                  setResultImageElement(null);
                  const resultImg = new Image();
                  resultImg.crossOrigin = 'anonymous';
                  resultImg.onload = () => {
                    if (removeObjectRunId !== removeObjectRunIdRef.current) return;
                    setResultImageElement(resultImg);
                    setResultImageLoadError(false);
                  };
                  resultImg.onerror = () => {
                    if (removeObjectRunId !== removeObjectRunIdRef.current) return;
                    setResultImageElement(null);
                    setResultImageLoadError(true);
                  };
                  resultImg.src = url;
                }
              } catch (err) {
                console.error('Failed to fetch presigned URL for remove_object result:', err);
              } finally {
                if (removeObjectRunId === removeObjectRunIdRef.current) {
                  setRemoveObjectLoading(false);
                  setAiNotification({
                    task: 'remove_object',
                    status: 'Success',
                    message: 'Object removal completed. Review the result.',
                  });
                }
                removeObjectTaskIdRef.current = null;
              }
            },
            onError: (err) => {
              if (removeObjectRunId !== removeObjectRunIdRef.current) return;
              console.error('remove_object task failed:', err);
              setRemoveObjectLoading(false);
              removeObjectTaskIdRef.current = null;
              setAiNotification({
                task: 'remove_object',
                status: 'Failed',
                message: err?.message || 'Object removal failed.',
              });
            },
          });
        } else {
          if (removeObjectRunId === removeObjectRunIdRef.current) {
            setRemoveObjectLoading(false);
            setAiNotification({
              task: 'remove_object',
              status: 'Failed',
              message: 'Object removal could not be started.',
            });
          }
        }
      } catch (err) {
        console.error('Failed to run remove_object inference:', err);
        if (removeObjectRunIdRef.current === removeObjectRunId) {
          setRemoveObjectLoading(false);
          setAiNotification({
            task: 'remove_object',
            status: 'Failed',
            message: err?.message || 'Object removal failed to start.',
          });
        }
      }
    },
    [
      roomImageId,
      resolveInteriorIdsForSelectedObjects,
      polygons,
      interiorTypesList,
      detectedObjectMultiSelectActive,
      multiSelectedPolygonIndices,
    ]
  );

  /** Stops waiting on the current AI task (client-side); server job may still complete. */
  const handleCancelGeneratingImageOverlay = useCallback(() => {
    if (removeObjectLoading) {
      removeObjectRunIdRef.current += 1;
      const tid = removeObjectTaskIdRef.current;
      removeObjectTaskIdRef.current = null;
      if (tid != null) removeTask(tid);
      setRemoveObjectLoading(false);
      setAiNotification({
        task: 'remove_object',
        status: '',
        message: 'Object removal cancelled.',
      });
      return;
    }
    if (materialTransferLoading) {
      materialTransferRunIdRef.current += 1;
      const tid = materialTransferTaskIdRef.current;
      materialTransferTaskIdRef.current = null;
      if (tid != null) removeTask(tid);
      setMaterialTransferLoading(false);
      setAiNotification({
        task: 'material_transfer',
        status: '',
        message: 'Material transfer cancelled.',
      });
      return;
    }
    if (changeObjectAngleLoading) {
      changeObjectAngleRunIdRef.current += 1;
      const tid = changeObjectAngleTaskIdRef.current;
      changeObjectAngleTaskIdRef.current = null;
      if (tid != null) removeTask(tid);
      setChangeObjectAngleLoading(false);
      setAiNotification({
        task: 'change_object_angle',
        status: '',
        message: 'View update cancelled.',
      });
    }
  }, [removeObjectLoading, materialTransferLoading, changeObjectAngleLoading]);

  const handleRemoveObjectConfirmModalClose = useCallback(() => {
    setRemoveObjectConfirmOpen(false);
    setRemoveObjectConfirmIndex(null);
  }, []);

  const handleRemoveObjectConfirmModalConfirm = useCallback(() => {
    const idx = removeObjectConfirmIndex;
    setRemoveObjectConfirmOpen(false);
    setRemoveObjectConfirmIndex(null);
    if (idx != null) void executeRemoveObjectInference(idx);
  }, [removeObjectConfirmIndex, executeRemoveObjectInference]);

  const handleRemoveObjectClick = useCallback(
    (polygonIndex) => {
      if (!roomImageId) return;
      const useMulti =
        detectedObjectMultiSelectActive && multiSelectedPolygonIndices.size > 1;
      const indices =
        useMulti
          ? [...multiSelectedPolygonIndices].sort((a, b) => a - b)
          : polygonIndex != null
            ? [polygonIndex]
            : [];
      for (const i of indices) {
        const item = polygons?.[i];
        if (item != null && isInteriorTypeDeletionBlocked(item, interiorTypesList, i)) {
          setAiNotification({
            task: 'remove_object',
            status: '',
            message: INTERIOR_TYPE_DELETION_BLOCKED_MESSAGE,
          });
          return;
        }
      }
      const interiorIdsForRemove = resolveInteriorIdsForSelectedObjects(polygonIndex);
      if (interiorIdsForRemove.length === 0) {
        console.warn('Cannot remove object: room_interior_id(s) missing for selected polygon(s)');
        setAiNotification({
          task: 'remove_object',
          status: 'Please select a region first',
          message: '',
        });
        return;
      }
      setRemoveObjectConfirmIndex(polygonIndex);
      setRemoveObjectConfirmOpen(true);
    },
    [
      roomImageId,
      resolveInteriorIdsForSelectedObjects,
      polygons,
      interiorTypesList,
      detectedObjectMultiSelectActive,
      multiSelectedPolygonIndices,
    ]
  );

  const handleRegenerateAiResult = useCallback(() => {
    const ctx = lastAiInferenceRegenerateContextRef.current;
    if (!ctx) return;
    if (ctx.type === 'material_transfer') {
      void handleApplyMaterialToPolygon(ctx.material);
    } else if (ctx.type === 'remove_object' && ctx.polygonIndex != null) {
      void executeRemoveObjectInference(ctx.polygonIndex);
    }
  }, [handleApplyMaterialToPolygon, executeRemoveObjectInference]);

  /** Show materials list again while keeping AI result preview on canvas (material_transfer only). */
  const handleChangeMaterialFromResult = useCallback(() => {
    if (operationTypeForApply !== 'material_transfer') return;
    setPendingMaterialSelection(null);
    setShowMaterialModifyPanel(false);
    setMaterialTransferLastFailed(false);
    setShowMaterialsGridDuringAiResult(true);
    setShowMaterialsPanel(true);
    setMaterialsPanelCollapsed(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        materialsPanelListScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }, [operationTypeForApply]);

  const handleStrokesChange = useCallback((action) => {
    if (action.type === 'stroke-end') {
      const strokeToAdd = action.stroke;
      const strokesToApply = [...editorState.strokes, strokeToAdd];
      if (editingLayerIndex != null && polygons && image && strokesToApply.length > 0) {
        const item = polygons[editingLayerIndex];
        const basePoly = item.polygon ?? item.polygons;
        let basePoints = null;
        let rings = null;
        if (basePoly?.points && Array.isArray(basePoly.points)) {
          basePoints = basePoly.points;
        } else if (Array.isArray(basePoly) && Array.isArray(basePoly[0]) && Array.isArray(basePoly[0][0])) {
          // Multi-ring polygon: choose ring based on where the stroke started.
          rings = basePoly;
        } else if (Array.isArray(basePoly) && basePoly.flat(2).length >= 4) {
          const flat = basePoly.flat(2);
          basePoints = [];
          for (let i = 0; i < flat.length - 1; i += 2) basePoints.push([Number(flat[i]), Number(flat[i + 1])]);
        }
        if ((!basePoints || basePoints.length < 3) && rings && Array.isArray(rings[0])) {
          basePoints = rings[0];
        }
        if (basePoints && basePoints.length >= 3) {
          const { width: nw, height: nh } = getImagePixelSize(image);
          const scale = Math.max(nw, nh) > MAX_CANVAS_SIZE ? MAX_CANVAS_SIZE / Math.max(nw, nh) : 1;
          const brushStrokes = [...(item.modifications?.brushAdditions ?? []), ...strokesToApply.filter((st) => st.tool === 'brush')];
          const eraserStrokes = [...(item.modifications?.eraserSubtractions ?? []), ...strokesToApply.filter((st) => st.tool === 'eraser')];

          if (rings && Array.isArray(rings)) {
            if (strokeToAdd.tool === 'eraser') {
              // Multi-ring eraser: apply the eraser to the combined polygon geometry.
              // This ensures subsequent eraser strokes expand/merge with previous erased areas.
              const mergedAll = mergeMultiRingPolygonWithStrokesRespectHoles(rings, [], eraserStrokes, scale);
              if (Array.isArray(mergedAll?.rings) && mergedAll.rings.length > 0) {
                const newPoly = polygonFromMergeResult(mergedAll);
                const bbox = computePolygonBbox(newPoly);
                const prevItem = { ...polygons[editingLayerIndex] };
                setPolygons((prev) =>
                  prev.map((p, i) =>
                    i !== editingLayerIndex
                      ? p
                      : { ...p, polygon: newPoly, bbox: bbox ?? p.bbox, modifications: undefined }
                  )
                );
                setEditorState((s) => ({
                  ...s,
                  strokes: [],
                  undoStack: [],
                  polygonUndoStack: [...(s.polygonUndoStack ?? []), prevItem],
                  polygonRedoStack: [],
                }));
                return;
              }
              // merge failed — fall through and keep stroke in editorState
            }
            // Brush: union ALL rings first, then strokes — avoids even-odd overlap holes between islands.
            const mergedAll = mergeMultiRingPolygonWithStrokes(rings, brushStrokes, eraserStrokes, scale);
            if (Array.isArray(mergedAll?.rings) && mergedAll.rings.length > 0) {
              const newPoly = polygonFromMergeResult(mergedAll);
              const bbox = computePolygonBbox(newPoly);
              const prevItem = { ...polygons[editingLayerIndex] };
              setPolygons((prev) =>
                prev.map((p, i) =>
                  i !== editingLayerIndex ? p : { ...p, polygon: newPoly, bbox: bbox ?? p.bbox, modifications: undefined }
                )
              );
              setEditorState((s) => ({
                ...s,
                strokes: [],
                undoStack: [],
                polygonUndoStack: [...(s.polygonUndoStack ?? []), prevItem],
                polygonRedoStack: [],
              }));
              return;
            }
            // merge failed — keep stroke in editorState (fall through)
          } else {
            const merged = mergePolygonWithStrokes(basePoints, brushStrokes, eraserStrokes, scale);
            if (Array.isArray(merged?.rings) && merged.rings.length > 0) {
              const nextPoly = polygonFromMergeResult(merged);
              const bbox = computePolygonBbox(nextPoly);
              const prevItem = { ...polygons[editingLayerIndex] };
              setPolygons((prev) =>
                prev.map((p, i) =>
                  i !== editingLayerIndex ? p : { ...p, polygon: nextPoly, bbox: bbox ?? p.bbox, modifications: undefined }
                )
              );
              setEditorState((s) => ({
                ...s,
                strokes: [],
                undoStack: [],
                polygonUndoStack: [...(s.polygonUndoStack ?? []), prevItem],
                polygonRedoStack: [],
              }));
              return;
            }
          }
        }
      }
      setEditorState((s) => ({
        ...s,
        strokes: [...s.strokes, strokeToAdd],
        undoStack: [],
      }));
    } else if (action.type === 'pan') {
      setEditorState((s) => ({
        ...s,
        pan: { x: s.pan.x + action.dx, y: s.pan.y + action.dy },
      }));
    }
  }, [editingLayerIndex, polygons, image, editorState.strokes, getImagePixelSize]);

  const applyLayerModifications = useCallback(() => {
    if (editingLayerIndex == null || !polygons || !image) return;
    const strokes = editorState.strokes;
    if (strokes.length === 0) return;
    const item = polygons[editingLayerIndex];
    const basePoly = item.polygon ?? item.polygons;
    let basePoints = null;
    let rings = null;
    if (basePoly?.points && Array.isArray(basePoly.points)) {
      basePoints = basePoly.points;
    } else if (Array.isArray(basePoly) && Array.isArray(basePoly[0]) && Array.isArray(basePoly[0][0])) {
      rings = basePoly;
    } else if (Array.isArray(basePoly) && basePoly.flat(2).length >= 4) {
      const flat = basePoly.flat(2);
      basePoints = [];
      for (let i = 0; i < flat.length - 1; i += 2) basePoints.push([Number(flat[i]), Number(flat[i + 1])]);
    }
    if ((!basePoints || basePoints.length < 3) && rings && Array.isArray(rings[0])) {
      basePoints = rings[0];
    }
    const { width: nw, height: nh } = getImagePixelSize(image);
    const scale = Math.max(nw, nh) > MAX_CANVAS_SIZE ? MAX_CANVAS_SIZE / Math.max(nw, nh) : 1;
    const brushStrokes = [...(item.modifications?.brushAdditions ?? []), ...strokes.filter((s) => s.tool === 'brush')];
    const eraserStrokes = [...(item.modifications?.eraserSubtractions ?? []), ...strokes.filter((s) => s.tool === 'eraser')];

    if (rings && Array.isArray(rings) && rings.some((r) => r?.length >= 3)) {
      const merged = mergeMultiRingPolygonWithStrokes(rings, brushStrokes, eraserStrokes, scale);
      if (!Array.isArray(merged?.rings) || merged.rings.length === 0) return;
      const poly = polygonFromMergeResult(merged);
      if (!poly) return;
      const bbox = computePolygonBbox(poly);
      const polyForLog = Array.isArray(poly?.[0]?.[0]) ? poly : [poly];
      console.log(JSON.stringify({
        interior_type_id: item.interior_type_id ?? 0,
        confidence: item.confidence ?? 0,
        bbox: bbox ?? item.bbox ?? [],
        polygon: polyForLog,
      }, null, 2));
      setPolygons((prev) =>
        prev.map((p, i) =>
          i !== editingLayerIndex ? p : { ...p, polygon: poly, bbox: bbox ?? p.bbox, modifications: undefined }
        )
      );
      return;
    }

    if (!basePoints || basePoints.length < 3) return;
    const merged = mergePolygonWithStrokes(basePoints, brushStrokes, eraserStrokes, scale);

    if (!Array.isArray(merged?.rings) || merged.rings.length === 0) return;
    const poly = polygonFromMergeResult(merged);
    if (!poly) return;
    const bbox = computePolygonBbox(poly);
    const polyForLog = Array.isArray(poly)
      ? [poly]
      : [poly.points, ...(poly.holes ?? [])];
    console.log(JSON.stringify({
      interior_type_id: item.interior_type_id ?? 0,
      confidence: item.confidence ?? 0,
      bbox: bbox ?? item.bbox ?? [],
      polygon: polyForLog,
    }, null, 2));
    setPolygons((prev) =>
      prev.map((p, i) =>
        i !== editingLayerIndex ? p : { ...p, polygon: poly, bbox: bbox ?? p.bbox, modifications: undefined }
      )
    );
  }, [editingLayerIndex, polygons, editorState.strokes, image, getImagePixelSize]);

  const handleUndo = useCallback(() => {
    const stack = editorState.polygonUndoStack;
    if (stack?.length > 0 && editingLayerIndex != null) {
      const prev = stack[stack.length - 1];
      const current = polygons?.[editingLayerIndex];
      setPolygons((p) =>
        p && editingLayerIndex < p.length ? p.map((item, i) => (i === editingLayerIndex ? prev : item)) : p
      );
      setEditorState((s) => ({
        ...s,
        polygonUndoStack: s.polygonUndoStack.slice(0, -1),
        polygonRedoStack: [...(s.polygonRedoStack ?? []), current],
      }));
      if (stack.length === 1) {
        setIsFirstMask(true);
      }
      return;
    }
    setEditorState((s) => {
      if (s.strokes.length === 0) return s;
      const last = s.strokes[s.strokes.length - 1];
      return { ...s, strokes: s.strokes.slice(0, -1), undoStack: [...s.undoStack, last] };
    });
  }, [editingLayerIndex, polygons, editorState.polygonUndoStack]);

  const handleRedo = useCallback(() => {
    const stack = editorState.polygonRedoStack;
    if (stack?.length > 0 && editingLayerIndex != null) {
      const next = stack[stack.length - 1];
      const current = polygons?.[editingLayerIndex];
      setPolygons((p) =>
        p && editingLayerIndex < p.length ? p.map((item, i) => (i === editingLayerIndex ? next : item)) : p
      );
      setEditorState((s) => ({
        ...s,
        polygonRedoStack: s.polygonRedoStack.slice(0, -1),
        polygonUndoStack: [...(s.polygonUndoStack ?? []), current],
      }));
      return;
    }
    setEditorState((s) => {
      if (s.undoStack.length === 0) return s;
      const last = s.undoStack[s.undoStack.length - 1];
      return { ...s, strokes: [...s.strokes, last], undoStack: s.undoStack.slice(0, -1) };
    });
  }, [editingLayerIndex, polygons, editorState.polygonRedoStack]);

  useEffect(() => {
    if (editingLayerIndex == null) return;
    const isTypingTarget = (el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.isContentEditable) return true;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    };

    const restoreSpacePanTool = () => {
      const prior = spacePanPriorToolRef.current;
      spacePanPriorToolRef.current = null;
      if (prior != null) {
        setEditorState((s) => ({ ...s, activeTool: prior }));
      }
    };

    const onKeyDown = (e) => {
      if (isTypingTarget(e.target)) return;

      const es = editorStateRef.current;
      const canUndo =
        es.strokes.length > 0 || (es.polygonUndoStack?.length ?? 0) > 0;
      const canRedo =
        es.undoStack.length > 0 || (es.polygonRedoStack?.length ?? 0) > 0;

      const key = e.key?.toLowerCase?.() ?? '';
      const mod = e.ctrlKey || e.metaKey;

      if (key === ' ' || e.code === 'Space') {
        if (e.repeat) return;
        e.preventDefault();
        if (spacePanPriorToolRef.current == null) {
          spacePanPriorToolRef.current = es.activeTool;
          setEditorState((s) => ({ ...s, activeTool: 'move' }));
          setPanToolbarTipsEpoch((n) => n + 1);
        }
        return;
      }

      if (mod) {
        if (key === 'z' && e.shiftKey && canRedo) {
          e.preventDefault();
          handleRedo();
          return;
        }
        if (key === 'z' && !e.shiftKey && canUndo) {
          e.preventDefault();
          handleUndo();
          return;
        }
        if (key === 'y' && canRedo) {
          e.preventDefault();
          handleRedo();
        }
        return;
      }

      if (key === 'b') {
        e.preventDefault();
        setHasTriggeredZoomToolbarTips(false);
        setEditorState((s) => ({ ...s, activeTool: 'brush' }));
        setHasExplicitBrushToolSelection(true);
        return;
      }
      if (key === 'e') {
        e.preventDefault();
        setHasTriggeredZoomToolbarTips(false);
        setEditorState((s) => ({ ...s, activeTool: 'eraser' }));
        return;
      }
      if (key === 'm') {
        e.preventDefault();
        setHasTriggeredZoomToolbarTips(false);
        setEditorState((s) => ({ ...s, activeTool: 'magicwand' }));
        return;
      }
      if (key === 'z' && canUndo) {
        e.preventDefault();
        handleUndo();
        return;
      }
      if (key === 'y' && canRedo) {
        e.preventDefault();
        handleRedo();
      }
    };

    const onKeyUp = (e) => {
      if (isTypingTarget(e.target)) return;
      if (e.key !== ' ' && e.code !== 'Space') return;
      e.preventDefault();
      restoreSpacePanTool();
    };

    const onWindowBlur = () => {
      restoreSpacePanTool();
    };

    window.addEventListener('keydown', onKeyDown);
    // Capture so Space release restores the prior tool even if focus moved (pan inactive again).
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', onWindowBlur);
      // Do not clear spacePanPriorToolRef here: this cleanup also runs when editorState updates
      // (e.g. activeTool → move), which would drop the stored tool before keyup fires.
    };
  }, [editingLayerIndex, handleUndo, handleRedo]);

  const handleZoomIn = useCallback(() => {
    if (editingLayerIndex != null) {
      setHasTriggeredZoomToolbarTips(true);
      setZoomToolbarTipsEpoch((n) => n + 1);
    }
    setEditorState((s) => ({ ...s, zoom: Math.min(3, s.zoom + 0.25) }));
  }, [editingLayerIndex]);

  const handleZoomOut = useCallback(() => {
    if (editingLayerIndex != null) {
      setHasTriggeredZoomToolbarTips(true);
      setZoomToolbarTipsEpoch((n) => n + 1);
    }
    setEditorState((s) => ({ ...s, zoom: Math.max(0.5, s.zoom - 0.25) }));
  }, [editingLayerIndex]);

  const handleEditorCanvasWheel = useCallback(
    (e) => {
      if (editingLayerIndex == null) return;
      e.preventDefault();
      if (e.deltaY > 0) {
        handleZoomOut();
      } else if (e.deltaY < 0) {
        handleZoomIn();
      }
    },
    [editingLayerIndex, handleZoomIn, handleZoomOut]
  );

  useEffect(() => {
    if (editingLayerIndex == null) {
      setHasTriggeredZoomToolbarTips(false);
      setZoomToolbarTipsEpoch(0);
      setPanToolbarTipsEpoch(0);
      spacePanPriorToolRef.current = null;
    }
  }, [editingLayerIndex]);

  const resetEditorState = useCallback(() => {
    setEditorState({
      activeTool: 'brush',
      magicMode: 'add',
      brushSize: 26,
      zoom: 1,
      pan: { x: 0, y: 0 },
      strokes: [],
      undoStack: [],
      clickPoints: [],
      polygonUndoStack: [],
      polygonRedoStack: [],
    });
    setSamError(null);
  }, []);

  const startLowConfidenceEdit = useCallback(
    (index, sourceList) => {
      const source = Array.isArray(sourceList) ? sourceList : [];
      if (!source[index]) return;
      const target = source[index];
      setPolygons([target]);
      setReviewLowConfidenceSource(source);
      setPendingDetectedObjects(null);
      setShowPolygonsOverlay(true);
      setShowHistoryPanel(false);
      setShowMaterialsPanel(false);
      setSelectedPolygonIndex(0);
      setShowAllPolygonsOnCanvas(false);
      setExpandedGroupLabel(null);
      polygonAtEditStartRef.current = { ...target };
      setReviewLowConfidenceEditIndex(index);
      setEditingLayerIndex(0);
      resetEditorState();
    },
    [resetEditorState]
  );

  // Persist interiors, handling mix of existing (with id) and new (without id). Returns saved list with ids from API (or refetch).
  const persistInteriors = useCallback(
    async (polys) => {
      if (roomId == null || roomImageId == null || !Array.isArray(polys) || polys.length === 0) {
        return null;
      }
      const formatted = polys.map((item) => {
        let next = { ...item };
        // If we have a label but no interior_type_id, try to resolve it from interiorTypesList.
        if ((next.interior_type_id == null && next.interiorTypeId == null) && next.label && interiorTypesList.length > 0) {
          const match = interiorTypesList.find(
            (t) => String(t.interiorType).toLowerCase() === String(next.label).toLowerCase()
          );
          if (match) {
            next.interior_type_id = match.id;
          }
        }
        const base = itemToInteriorFormat(next);
        return next.id != null ? { ...base, id: next.id } : base;
      });
      const existingPayload = formatted.filter((p) => p.id != null);
      const newPayload = formatted.filter((p) => p.id == null);
      if (interiorsFromExisting) {
        if (newPayload.length > 0) {
          await saveInteriors(roomId, roomImageId, newPayload);
        } else if (existingPayload.length > 0) {
          await updateInteriors(roomId, roomImageId, existingPayload);
        }
      } else {
        await saveInteriors(roomId, roomImageId, formatted);
      }
      const fetched = await getInteriors(roomId, roomImageId);
      clearAutoSavedDraft();
      return Array.isArray(fetched) ? fetched : null;
    },
    [interiorsFromExisting, roomId, roomImageId, clearAutoSavedDraft]
  );

  const handleEditorConfirm = useCallback(async () => {
    if (editingLayerIndex == null || !polygons) return;
    const confirmedEditingIndex = editingLayerIndex;
    let finalPolygons = polygons;
    if (editorState.strokes.length > 0) {
      const item = polygons[editingLayerIndex];
      const basePoly = item.polygon ?? item.polygons;
      const rawRings = normalizePolygonToRings(basePoly).filter((r) => (r ?? []).length >= 3);
      if (rawRings.length > 0 && image) {
        const { width: nw, height: nh } = getImagePixelSize(image);
        const scale =
          Math.max(nw, nh) > MAX_CANVAS_SIZE ? MAX_CANVAS_SIZE / Math.max(nw, nh) : 1;
        const itemMods = item.modifications ?? {};
        const brushStrokes = [
          ...(itemMods.brushAdditions ?? []),
          ...editorState.strokes.filter((s) => s.tool === 'brush'),
        ];
        const eraserStrokes = [
          ...(itemMods.eraserSubtractions ?? []),
          ...editorState.strokes.filter((s) => s.tool === 'eraser'),
        ];
        const merged =
          rawRings.length > 1
            ? mergeMultiRingPolygonWithStrokes(rawRings, brushStrokes, eraserStrokes, scale)
            : mergePolygonWithStrokes(rawRings[0], brushStrokes, eraserStrokes, scale);
        const poly = polygonFromMergeResult(merged);
        if (poly) {
          const bbox = computePolygonBbox(poly);
          finalPolygons = polygons.map((p, i) =>
            i !== editingLayerIndex
              ? p
              : { ...p, polygon: poly, bbox: bbox ?? p.bbox, modifications: undefined }
          );
          setPolygons(finalPolygons);
        }
      }
    }
    if (addingNewObjectFromPopup) {
      setObjectBeingLabeled(finalPolygons[0] ?? null);
      setObjectChoiceStep('editNewObjectLabel');
      setNewObjectLabel(finalPolygons[0]?.label ?? '');
      setAddingNewObjectFromPopup(false);
      setIsFirstMask(false);
      resetEditorState();
      setEditingLayerIndex(null);
      return;
    }
    let reviewMergedPolygons = null;
    if (reviewLowConfidenceEditIndex != null) {
      const base = Array.isArray(reviewLowConfidenceSource)
        ? reviewLowConfidenceSource
        : Array.isArray(interiorsListForLowConfidencePopup)
          ? interiorsListForLowConfidencePopup
          : [];
      const edited = finalPolygons?.[0] ?? null;
      if (edited != null) {
        reviewMergedPolygons = base.map((p, i) =>
          i === reviewLowConfidenceEditIndex ? { ...p, ...edited, confidence: 0.99 } : p
        );
        finalPolygons = reviewMergedPolygons;
      }
    }
    // Persist edits and merge returned ids so brush/material apply has room_interior_id.
    if (finalPolygons?.length > 0 && roomId != null && roomImageId != null) {
      try {
        const saved = await persistInteriors(finalPolygons);
        if (Array.isArray(saved) && saved.length > 0) {
          setPolygons((prev) =>
            (prev ?? []).map((p) => ({
              ...p,
              id: p.id ?? getInteriorIdFromSaved(p, saved),
              interior_id: p.interior_id ?? p.id ?? getInteriorIdFromSaved(p, saved),
            }))
          );
        }
      } catch (err) {
        console.error('Failed to save interiors:', err);
        setError(err.message ?? 'Failed to save interiors');
      }
    }
    if (removeObjectRefinePending) {
      setRemoveObjectRefinePending(false);
      void executeRemoveObjectInference(confirmedEditingIndex);
    }
    resetEditorState();
    setEditingLayerIndex(null);
    if (reviewLowConfidenceEditIndex != null) {
      setReviewLowConfidenceEditIndex(null);
      setPendingDetectedObjects(reviewMergedPolygons ?? finalPolygons ?? []);
      setDetectedObjectsFromAPI(reviewMergedPolygons ?? finalPolygons ?? []);
      setPolygons(reviewMergedPolygons ?? finalPolygons ?? []);
      setReviewLowConfidenceSource(reviewMergedPolygons ?? finalPolygons ?? []);
      setObjectChoiceStep('reviewLowConfidence');
    }
  }, [
    addingNewObjectFromPopup,
    editorState.strokes,
    editingLayerIndex,
    image,
    polygons,
    resetEditorState,
    interiorsFromExisting,
    roomId,
    roomImageId,
    isFirstMask,
    reviewLowConfidenceEditIndex,
    reviewLowConfidenceSource,
    interiorsListForLowConfidencePopup,
    removeObjectRefinePending,
    executeRemoveObjectInference,
    getImagePixelSize,
  ]);

  const handleEditorCancel = useCallback(() => {
    if (removeObjectRefinePending) {
      setRemoveObjectRefinePending(false);
      // When cancelling remove-result refine, return to the AI result state
      // so Apply / Regen / Refine / Download / Cancel actions are visible again.
      if (operationTypeForApply === 'remove_object' && lastMaterialTransferResultKey) {
        setSwapped(true);
      }
    }
    if (addingNewObjectFromPopup) {
      setPolygons((prev) => (prev?.length > 1 ? prev.slice(1) : []));
      setAddingNewObjectFromPopup(false);
      setIsFirstMask(false);
      resetEditorState();
      setEditingLayerIndex(null);
      return;
    }
    if (polygonAtEditStartRef.current != null && editingLayerIndex != null && polygons) {
      setPolygons((prev) =>
        prev.map((p, i) => (i === editingLayerIndex ? polygonAtEditStartRef.current : p))
      );
    }
    resetEditorState();
    setEditingLayerIndex(null);
    if (reviewLowConfidenceEditIndex != null) {
      setReviewLowConfidenceEditIndex(null);
      setReviewLowConfidenceSource(null);
      setObjectChoiceStep('reviewLowConfidence');
    }
  }, [
    addingNewObjectFromPopup,
    editingLayerIndex,
    polygons,
    resetEditorState,
    isFirstMask,
    reviewLowConfidenceEditIndex,
    removeObjectRefinePending,
    operationTypeForApply,
    lastMaterialTransferResultKey,
  ]);

  /** Add-object panel Cancel: drop in-progress mask or abandoned new-object label (draft is always prepended at index 0). */
  const handleCancelAddObjectActions = useCallback(() => {
    setAddObjectActionsVisible(false);
    setRemoveObjectActionsVisible(false);
    if (addingNewObjectFromPopup) {
      handleEditorCancel();
      return;
    }
    if (objectChoiceStep === 'editNewObjectLabel' && objectRenameIndex == null) {
      setPolygons((prev) => (Array.isArray(prev) && prev.length > 0 ? prev.slice(1) : []));
      setObjectChoiceStep(addObjectLabelReturnStepRef.current);
      setObjectBeingLabeled(null);
      setObjectRenameIndex(null);
      setNewObjectLabel('');
      setNewObjectLabelDropdownOpen(false);
      setIsFirstMask(false);
      resetEditorState();
      setEditingLayerIndex(null);
      polygonAtEditStartRef.current = null;
      return;
    }
  }, [
    addingNewObjectFromPopup,
    objectChoiceStep,
    objectRenameIndex,
    handleEditorCancel,
    resetEditorState,
  ]);

  /** Clear polygon focus on canvas (selection / show-all / layer editor). */
  const handleCancelPolygonSelection = useCallback(() => {
    setSelectedPolygonIndex(null);
    setShowAllPolygonsOnCanvas(false);
    setExpandedGroupLabel(null);
    setMultiSelectedPolygonIndices(new Set());
    setDetectedObjectMultiSelectActive(false);
    setObjectListOverflowMenuIndex(null);
    setPolygonBottomActionsOpen(false);
    if (editingLayerIndex != null) {
      handleEditorCancel();
    }
  }, [editingLayerIndex, handleEditorCancel]);

  /** Close the Materials side panel only (keep polygon selection intact). */
  const handleCloseMaterialsPanel = useCallback(() => {
    setShowMaterialsPanel(false);
    setMaterialsForInterior([]);
    setMaterialsInteriorTypeId(null);
    setMaterialsTargetInteriorId(null);
    setMaterialsTargetInteriorIds(null);
    setMaterialsSearchQuery('');
    setShowMaterialModifyPanel(false);
    setPendingMaterialSelection(null);
    setMaterialsError(null);
    setMaterialsLoading(false);
    setShowMaterialsGridDuringAiResult(false);
  }, []);

  const handleOpenSelectObjectsPopup = useCallback(async () => {
    setAddObjectActionsVisible(false);
    setRemoveObjectActionsVisible(false);
    setDeleteDetectionMode(false);
    if (selectObjectsSnapshotRef.current == null) {
      selectObjectsSnapshotRef.current = {
        detectedObjectsFromAPI,
        userDefinedObjects,
        interiorsFromExisting,
      };
    }
    if (roomId != null && roomImageId != null) {
      try {
        const refreshed = await getInteriors(roomId, roomImageId);
        const list = Array.isArray(refreshed) ? refreshed : [];
        setDetectedObjectsFromAPI(list);
        setInteriorsFromExisting(list.length > 0);
        setUserDefinedObjects([]);
        setPendingDetectedObjects(list);
        {
          const { detectedSet, userSet } = buildPreselectedSetsFromPolygons(polygons, list, []);
          setSelectedObjectIndices(detectedSet);
          setSelectedUserDefinedIndices(userSet);
        }
        // Same as implicit Ignore: badges on choose list, no duplicate banner (Select Objects opens on choose).
        applyLowConfidenceStateForObjectList(list);
      } catch (err) {
        console.error('Failed to refresh interiors list on open:', err);
        const source = [...detectedObjectsFromAPI, ...userDefinedObjects];
        setPendingDetectedObjects(source);
        applyLowConfidenceStateForObjectList(
          detectedObjectsFromAPI.length > 0 ? detectedObjectsFromAPI : source
        );
        {
          const { detectedSet, userSet } = buildPreselectedSetsFromPolygons(
            polygons,
            detectedObjectsFromAPI,
            userDefinedObjects
          );
          setSelectedObjectIndices(detectedSet);
          setSelectedUserDefinedIndices(userSet);
        }
      }
    } else {
      const source = [...detectedObjectsFromAPI, ...userDefinedObjects];
      setPendingDetectedObjects(source);
      applyLowConfidenceStateForObjectList(
        detectedObjectsFromAPI.length > 0 ? detectedObjectsFromAPI : source
      );
      {
        const { detectedSet, userSet } = buildPreselectedSetsFromPolygons(
          polygons,
          detectedObjectsFromAPI,
          userDefinedObjects
        );
        setSelectedObjectIndices(detectedSet);
        setSelectedUserDefinedIndices(userSet);
      }
    }
    setObjectChoiceStep('choose');
    setAddingNewObjectFromPopup(false);
    setIsFirstMask(false);
  }, [
    roomId,
    roomImageId,
    detectedObjectsFromAPI,
    userDefinedObjects,
    interiorsFromExisting,
    applyLowConfidenceStateForObjectList,
    polygons,
  ]);

  const handleStartAddObjectFlow = useCallback((selectedItems = []) => {
    addObjectLabelReturnStepRef.current = objectChoiceStep === 'choose' ? 'choose' : 'initial';
    const selected = Array.isArray(selectedItems) ? selectedItems : [];
    existingObjectIdsBeforeAddRef.current = new Set(
      selected
        .map((item) => item?.id ?? item?.interior_id)
        .filter((id) => id != null)
        .map((id) => Number(id))
    );
    const prevSelected =
      selectedPolygonIndex != null &&
      selectedPolygonIndex >= 0 &&
      selectedPolygonIndex < selected.length
        ? selected[selectedPolygonIndex]
        : null;
    selectedObjectBeforeAddRef.current = prevSelected
      ? {
          id: prevSelected?.id ?? prevSelected?.interior_id ?? null,
          typeId: Number(prevSelected?.interior_type_id ?? prevSelected?.interiorTypeId ?? -1),
          bbox: Array.isArray(prevSelected?.bbox) ? prevSelected.bbox : null,
          label: String(getLabelForInterior(prevSelected, interiorTypesList, selectedPolygonIndex)).trim().toLowerCase(),
        }
      : null;
    setShowPolygonsOverlay(true);
    setAddingNewObjectFromPopup(true);
    setIsFirstMask(true);
    if (image) {
      const { width: nw, height: nh } = getImagePixelSize(image);
      const cx = nw / 2;
      const cy = nh / 2;
      const x = Math.round(cx);
      const y = Math.round(cy);
      const points = [[x, y], [x, y], [x, y]];
      const newItem = {
        interior_type_id: 0,
        confidence: 1,
        bbox: [x, y, x, y],
        polygon: points,
        label: `Object ${selected.length + 1}`,
      };
      setPolygons([newItem, ...selected]);
      polygonAtEditStartRef.current = newItem;
      setEditingLayerIndex(0);
      resetEditorState();
    } else {
      setPolygons(selected);
      setAddingNewObjectFromPopup(false);
      setIsFirstMask(false);
    }
  }, [image, resetEditorState, selectedPolygonIndex, interiorTypesList, objectChoiceStep, getImagePixelSize]);

  /** When generate_mask fails or times out, skip auto re-scan and open manual mask (SAM) flow. */
  const handleScanFailureStartManualArea = useCallback(() => {
    if (roomImageId != null) {
      aiInferenceDone.add(roomImageId);
    }
    setScanTimeoutOrError(false);
    setScanNoDetections(false);
    setPolygonsLoading(false);
    setError(null);
    setAiNotification((prev) => (prev?.task === 'generate_mask' ? null : prev));
    handleStartAddObjectFlow([]);
  }, [roomImageId, handleStartAddObjectFlow]);

  const handleCloseSelectObjectsPopup = useCallback(() => {
    // If the popup is in "label new object" step (created from Add Object flow),
    // closing should return to editor instead of discarding the editing context.
    if (
      objectChoiceStep === 'editNewObjectLabel' &&
      objectRenameIndex == null &&
      Array.isArray(polygons) &&
      polygons.length > 0
    ) {
      polygonAtEditStartRef.current = { ...polygons[0] };
      setObjectChoiceStep('initial');
      setPendingDetectedObjects(null);
      setObjectBeingLabeled(null);
      setObjectRenameIndex(null);
      setReviewLowConfidenceSource(null);
      setNewObjectLabel('');
      setNewObjectLabelDropdownOpen(false);
      setDeleteDetectionMode(false);
      setIgnoredLowConfidenceKeys(new Set());
      setHideLowConfidenceBanner(false);
      selectObjectsSnapshotRef.current = null;
      setAddingNewObjectFromPopup(true);
      setEditingLayerIndex(0);
      resetEditorState();
      return;
    }

    const snapshot = selectObjectsSnapshotRef.current;
    if (snapshot) {
      setDetectedObjectsFromAPI(snapshot.detectedObjectsFromAPI);
      setUserDefinedObjects(snapshot.userDefinedObjects);
      setInteriorsFromExisting(snapshot.interiorsFromExisting);
    }
    setPendingDetectedObjects(null);
    setObjectChoiceStep('initial');
    setObjectBeingLabeled(null);
    setObjectRenameIndex(null);
    setReviewLowConfidenceSource(null);
    setNewObjectLabel('');
    setNewObjectLabelDropdownOpen(false);
    setSelectedObjectIndices(new Set());
    setSelectedUserDefinedIndices(new Set());
    setDeleteDetectionMode(false);
    setAddingNewObjectFromPopup(false);
    setIsFirstMask(false);
    setIgnoredLowConfidenceKeys(new Set());
    setHideLowConfidenceBanner(false);
    selectObjectsSnapshotRef.current = null;
  }, [objectChoiceStep, objectRenameIndex, polygons, resetEditorState]);

  useEffect(() => {
    if (editingLayerIndex == null) {
      setHasExplicitBrushToolSelection(false);
    }
  }, [editingLayerIndex]);

  useEffect(() => {
    if (!polygonBottomActionsOpen) setPolygonBottomActionsAnchor(null);
  }, [polygonBottomActionsOpen]);

  useEffect(() => {
    if (swapped) {
      setAddObjectActionsVisible(false);
      setRemoveObjectActionsVisible(false);
    }
  }, [swapped]);

  /** Dismiss "Please select a region first" once the user selects a polygon (single or multi-select). */
  useEffect(() => {
    if (!aiNotification) return;
    const isSelectRegionHint =
      aiNotification.status === 'Please select a region first' ||
      (typeof aiNotification.message === 'string' &&
        aiNotification.message.includes('Please select a region first'));
    if (!isSelectRegionHint) return;
    const hasActiveRegion =
      selectedPolygonIndex != null ||
      (detectedObjectMultiSelectActive && multiSelectedPolygonIndices.size > 0);
    if (hasActiveRegion) {
      setAiNotification(null);
    }
  }, [
    aiNotification,
    selectedPolygonIndex,
    detectedObjectMultiSelectActive,
    multiSelectedPolygonIndices,
  ]);

  useEffect(() => {
    if (!showMaterialsPanel) setMaterialsPanelCollapsed(false);
  }, [showMaterialsPanel]);

  useEffect(() => {
    if (!showTransformModelsPanel) setTransformModelsPanelCollapsed(false);
  }, [showTransformModelsPanel]);

  /** Reset material modify controls when selection is cleared or a different material is chosen. */
  useEffect(() => {
    setShowMaterialModifyPanel(false);
    setMaterialModifyRotation(0);
    setMaterialModifyScale(100);
    setMaterialModifyFlipVertical(false);
    setMaterialModifyFlipHorizontal(false);
    setMaterialTransferLastFailed(false);
  }, [pendingMaterialSelection?.id]);

  /** While materials is open, refetch the list when the user selects another object (or multi-select changes targets). */
  useEffect(() => {
    if (!showMaterialsPanel || selectedPolygonIndex == null || !materialsPanelSelectionKey) return;
    void syncMaterialsListForPolygonIndex(selectedPolygonIndex);
  }, [
    showMaterialsPanel,
    selectedPolygonIndex,
    materialsPanelSelectionKey,
    syncMaterialsListForPolygonIndex,
  ]);

  useEffect(() => {
    if (objectListOverflowMenuIndex == null) return;
    const close = (e) => {
      if (!e.target.closest('[data-object-row-overflow]')) {
        setObjectListOverflowMenuIndex(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [objectListOverflowMenuIndex]);

  useEffect(() => {
    if (
      objectListOverflowMenuIndex != null &&
      polygons != null &&
      objectListOverflowMenuIndex >= polygons.length
    ) {
      setObjectListOverflowMenuIndex(null);
    }
  }, [polygons, objectListOverflowMenuIndex]);

  useEffect(() => {
    // Accessibility shortcut: trigger the same flow as "Select Objects" button.
    if (swapped) return;

    const onKeyDown = (e) => {
      if (e.key === 'Tab' || e.keyCode === 9) {
        const target = e.target;
        const isTypingField =
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement ||
          (target instanceof HTMLElement && target.isContentEditable);
        if (isTypingField) return;
        e.preventDefault();
        void handleOpenSelectObjectsPopup();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [swapped, handleOpenSelectObjectsPopup]);

  const remapMultiSelectedIndicesAfterRemove = useCallback((removedIndex) => {
    setMultiSelectedPolygonIndices((prev) => {
      const next = new Set();
      for (const j of prev) {
        if (j === removedIndex) continue;
        next.add(j > removedIndex ? j - 1 : j);
      }
      return next;
    });
  }, []);

  const activateDetectedObjectRow = useCallback(
    (index, polygonsList) => {
      setSelectedPolygonIndex(index);
      setShowAllPolygonsOnCanvas(false);
      if (index == null) {
        // In multi-select mode, primary can be null while the user still wants the
        // group they opened to stay expanded (do not auto-collapse on last deselect).
        if (!detectedObjectMultiSelectActive) {
          setExpandedGroupLabel(null);
        }
        return;
      }
      const groups = groupObjectList(polygonsList ?? [], interiorTypesList);
      const grp = groups.find((g) => g.indices?.includes(index));
      if (grp && grp.indices.length > 1) {
        setExpandedGroupLabel(grp.label);
      } else {
        setExpandedGroupLabel(null);
      }
    },
    [interiorTypesList, detectedObjectMultiSelectActive]
  );

  const handleDetectedObjectRowClick = useCallback(
    (index, polygonsList) => {
      if (detectedObjectMultiSelectActive) {
        if (removeObjectRefinePending) {
          const next = new Set(multiSelectedPolygonIndices);
          if (!next.has(index)) return;
          const target = polygonsList?.[index];
          if (!target) return;
          polygonAtEditStartRef.current = { ...target };
          setEditingLayerIndex(index);
          resetEditorState();
          activateDetectedObjectRow(index, polygonsList);
          return;
        }
        const next = new Set(multiSelectedPolygonIndices);
        const wasInSet = next.has(index);
        if (wasInSet) next.delete(index);
        else next.add(index);
        setMultiSelectedPolygonIndices(next);

        let primary;
        if (wasInSet) {
          if (selectedPolygonIndex === index) {
            primary = next.size > 0 ? Math.min(...next) : null;
          } else {
            primary = selectedPolygonIndex;
            if (primary != null && !next.has(primary)) {
              primary = next.size > 0 ? Math.min(...next) : null;
            }
          }
        } else {
          primary = index;
        }
        activateDetectedObjectRow(primary, polygonsList);
      } else {
        setPolygonBottomActionsOpen(false);
        activateDetectedObjectRow(index, polygonsList);
      }
    },
    [
      detectedObjectMultiSelectActive,
      removeObjectRefinePending,
      multiSelectedPolygonIndices,
      selectedPolygonIndex,
      resetEditorState,
      activateDetectedObjectRow,
    ]
  );

  const hasActiveCanvasPolygons =
    showPolygonsOverlay &&
    !swapped &&
    Array.isArray(polygons) &&
    polygons.length > 0 &&
    (visiblePolygonIndicesForCanvas != null ||
      showAllPolygonsOnCanvas ||
      selectedPolygonIndex != null ||
      editingLayerIndex != null);
  const isEditorToolbarActive = editingLayerIndex != null && !swapped;

  const handleSwap = (e) => {
    e.stopPropagation();
    setSwapped((s) => !s);
  };

  const updateCompareSliderFromClientX = useCallback((clientX) => {
    const el = compareCanvasRef.current;
    if (!el) return;
    const cr = el.getBoundingClientRect();
    const { ox, rw } = compareFitRect;
    const contentLeft = cr.left + (rw > 0 ? ox : 0);
    const contentW = rw > 0 ? rw : cr.width;
    if (contentW <= 0) return;
    const next = ((clientX - contentLeft) / contentW) * 100;
    setCompareSliderPosition(Math.max(0, Math.min(100, next)));
  }, [compareFitRect]);

  const handleMagicWandClick = useCallback(
    ({ x, y, label }) => {
      if (!image || !gpuInfo || samStatus) return;
      const { width: pw, height: ph } = getImagePixelSize(image);
      const ready = image.complete !== false && pw > 0 && ph > 0;
      if (!ready) {
        setSamError('Image is not ready yet. Please wait for it to load.');
        return;
      }
      const nextClicks = [...editorState.clickPoints, { x, y, label }].slice(-MAX_SAM_PROMPT_POINTS);
      setEditorState((s) => ({
        ...s,
        clickPoints: nextClicks,
      }));
      setSamError(null);
    },
    [image, gpuInfo, samStatus, editorState.clickPoints, getImagePixelSize],
  );

  const handleMagicWandApply = useCallback(
    async () => {
      if (!image || !gpuInfo || samStatus || editorState.clickPoints.length === 0) return;
      const { width: pw, height: ph } = getImagePixelSize(image);
      const ready = image.complete !== false && pw > 0 && ph > 0;
      if (!ready) {
        setSamError('Image is not ready yet. Please wait for it to load.');
        return;
      }

      const targetIndex =
        editingLayerIndex != null
          ? editingLayerIndex
          : selectedPolygonIndex != null
            ? selectedPolygonIndex
            : null;
      const targetItem = targetIndex != null && polygons?.[targetIndex] ? polygons[targetIndex] : null;
      const polyPrompts = targetItem?.polygon ?? targetItem?.polygons ?? null;
      const bboxPrompt = targetItem?.bbox ?? null;

      setSamError(null);
      setSamStatus(SAM_STATUS.LOADING_SESSION);

      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      try {
        const { polygon: refinedPolygon, bbox } = await runMaskRefinement(
          image,
          polyPrompts ?? undefined,
          editorState.clickPoints,
          bboxPrompt ?? undefined,
          gpuInfo.device,
          roomImageId ?? image?.src ?? undefined,
          isFirstMask,
          (step) => setSamStatus(step),
        );

        if (targetIndex == null || !targetItem || !polygons) return;
        if (!refinedPolygon || !Array.isArray(refinedPolygon) || refinedPolygon.length === 0) {
          console.warn('SAM refine output did not return a valid polygon.', refinedPolygon);
          return;
        }

        const prevItem = { ...targetItem };
        setPolygons((prev) =>
          prev?.map((item, i) =>
            i === targetIndex
              ? { ...item, polygon: refinedPolygon, bbox: bbox ?? item.bbox, modifications: undefined }
              : item,
          ) ?? prev,
        );

        setEditorState((s) => ({
          ...s,
          strokes: [],
          undoStack: [],
          clickPoints: [],
          polygonUndoStack: [...(s.polygonUndoStack ?? []), prevItem],
          polygonRedoStack: [],
        }));
        setIsFirstMask(false);
        if (editingLayerIndex == null) {
          setEditingLayerIndex(targetIndex);
        }
      } catch (err) {
        console.error('SAM refine failed:', err);
        setSamError(err?.message || 'SAM refine failed');
      } finally {
        setSamStatus(null);
      }
    },
    [
      image,
      gpuInfo,
      samStatus,
      editorState.clickPoints,
      polygons,
      editingLayerIndex,
      selectedPolygonIndex,
      roomImageId,
      getImagePixelSize,
    ],
  );

  // Pre-load image from context when opening from StartDesigning
  useEffect(() => {
    if (!photorealisticImage) return;
    setImageRestoreLoading(true);
    const img = new Image();
    // Allow using image pixels in canvas for SAM / OpenCV without tainting.
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImage(img);
      setError(null);
      setImageRestoreLoading(false);
    };
    img.onerror = () => {
      setError('Failed to load image');
      setImageRestoreLoading(false);
    };
    img.src = photorealisticImage;
  }, [photorealisticImage]);

  // Reset thumbnail/result image errors when leaving result view
  useEffect(() => {
    if (!swapped) {
      setBasedImageLoadError(false);
      setResultImageLoadError(false);
    }
  }, [swapped]);

  useEffect(() => {
    if (!preview) {
      setShowCompareSlider(false);
      setCompareSliderPosition(50);
    }
  }, [preview]);

  // After reload on /design/:roomId: get room's latestRoomImageId then restore that image from API
  useEffect(() => {
    if (roomId == null || photorealisticImage != null) return;
    setImageRestoreLoading(true);
    setError(null);
    getRoom(roomId)
      .then((room) => {
        const latestImageId =
          room?.latestRoomImageId ??
          room?.latestImageId ??
          room?.roomImageId ??
          null;
        if (latestImageId != null) setRoomImageId(latestImageId);
        return getRoomImages(roomId).then((list) => ({ list, latestImageId }));
      })
      .then(({ list, latestImageId }) => {
        if (!Array.isArray(list) || list.length === 0) {
          setError('No room images were returned for this room.');
          return;
        }
        const byId = list.find((img) => img.id === latestImageId);
        const sorted = [...list].sort((a, b) => {
          const at = a.createdAt ?? '';
          const bt = b.createdAt ?? '';
          return new Date(bt) - new Date(at);
        });
        const chosen = byId ?? sorted[0];
        const url = resolveRoomImageUrl(chosen);
        const id = chosen.id ?? latestImageId;
        const apiDims = parseRoomImageDimensionsFromApi(chosen);
        if (apiDims) setRoomImageDimensions(apiDims);
        else setRoomImageDimensions(null);
        if (url && id != null) {
          setPhotorealisticImage(url);
          setRoomImageId(id);
        } else {
          setError('A room image exists, but its URL is missing. Please upload again or contact support.');
        }
      })
      .catch((err) => {
        console.error('Failed to restore room image after reload:', err);
        setError(err?.message ?? 'Failed to restore room image after reload.');
      })
      .finally(() => setImageRestoreLoading(false));
  }, [
    roomId,
    photorealisticImage,
    setPhotorealisticImage,
    setRoomImageId,
    setRoomImageDimensions,
    imageRestoreRetryKey,
  ]);

  useEffect(() => {
    detectGPU().then((info) => {
      setGpuInfo(info);
    });
  }, []);

  // Self-heal: if detections are present but canvas image is missing, retry restoring the room image URL.
  useEffect(() => {
    if (roomId == null || roomImageId == null) return;
    if (imageRestoreLoading || showSessionRecoveryModal) return;
    if (image != null) return;
    const hasDetectedObjects =
      (Array.isArray(polygons) && polygons.length > 0) ||
      (Array.isArray(detectedObjectsFromAPI) && detectedObjectsFromAPI.length > 0) ||
      (Array.isArray(pendingDetectedObjects) && pendingDetectedObjects.length > 0);
    if (!hasDetectedObjects) return;

    getRoomImages(roomId)
      .then((list) => {
        if (!Array.isArray(list) || list.length === 0) return;
        const byId = list.find((img) => Number(img?.id) === Number(roomImageId));
        const sorted = [...list].sort((a, b) => new Date(b?.createdAt ?? 0) - new Date(a?.createdAt ?? 0));
        const chosen = byId ?? sorted[0];
        const url = resolveRoomImageUrl(chosen);
        const id = chosen?.id ?? roomImageId;
        const apiDims = parseRoomImageDimensionsFromApi(chosen);
        if (apiDims) setRoomImageDimensions(apiDims);
        if (!url) return;
        if (id != null) setRoomImageId(id);
        if (photorealisticImage !== url) {
          setPhotorealisticImage(url);
        } else {
          // URL unchanged but image object missing (previous decode/load failed): force another load attempt.
          setImageRestoreRetryKey((k) => k + 1);
        }
      })
      .catch((err) => {
        console.error('Fallback room image restore failed:', err);
      });
  }, [
    roomId,
    roomImageId,
    image,
    imageRestoreLoading,
    showSessionRecoveryModal,
    polygons,
    detectedObjectsFromAPI,
    pendingDetectedObjects,
    photorealisticImage,
    setPhotorealisticImage,
    setRoomImageId,
    setRoomImageDimensions,
  ]);

  // Scene Understanding: use existing interiors if any, else run generate_mask when page loads
  useEffect(() => {
    if (roomImageId == null) return;
    if (promptApplyMaskInFlightRef.current) return;
    const imageKey = String(roomImageId);
    setInteriorsSavedOnce(false);
    // Never carry a stale scan-failure overlay into a new room image bootstrap.
    setScanTimeoutOrError(false);
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
    const hasLocalDetectionState =
      (Array.isArray(pendingDetectedObjects) && pendingDetectedObjects.length > 0) ||
      (Array.isArray(detectedObjectsFromAPI) && detectedObjectsFromAPI.length > 0) ||
      (Array.isArray(polygons) && polygons.length > 0);
    const forceGenerateMask = forceGenerateMaskRef.current === true;
    if (forceGenerateMask) {
      forceGenerateMaskRef.current = false;
    }
    if (aiInferenceDone.has(roomImageId) && hasLocalDetectionState && !forceGenerateMask) return;
    if (aiInferenceDone.has(roomImageId) && !hasLocalDetectionState) {
      // The room image was marked as processed in a prior lifecycle, but current UI has no objects.
      // Re-run bootstrap so popup/list state is rebuilt.
      aiInferenceDone.delete(roomImageId);
    }
    aiInferenceDone.add(roomImageId);

    const clearBootstrapInFlight = () => {
      generateMaskBootstrapInFlight.delete(imageKey);
    };

    const applyResult = (list) => {
      clearBootstrapInFlight();
      setIsGenerateMaskTaskActive(false);
      setGenerateMaskConflictRetrySeconds(null);
      setPolygonsLoading(false);
      setScanTimeoutOrError(false);
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
      setPendingDetectedObjects(list);
      setDetectedObjectsFromAPI(list);
      setUserDefinedObjects([]);
      setSelectedUserDefinedIndices(new Set());
      setScanNoDetections(!Array.isArray(list) || list.length === 0);
      setObjectChoiceStep('initial');
      setIgnoredLowConfidenceKeys(new Set());
      setHideLowConfidenceBanner(false);
      // If generate_mask eventually succeeds after a transient API error (503/409 race),
      // clear stale scan failure notification/error so UI reflects the resolved state.
      setError((prev) =>
        prev === 'Service is not available, please try again later.' ? null : prev
      );
      setAiNotification((prev) => {
        if (!prev) return prev;
        if (prev.task !== 'generate_mask') return prev;
        return null;
      });
    };

    const trackGenerateMaskTask = (taskId) => {
      if (taskId == null) return;
      setIsGenerateMaskTaskActive(true);
      setPolygonsLoading(true);
      setScanTimeoutOrError(false);
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
      const SCAN_TIMEOUT_MS = 60 * 1000;
      scanTimeoutRef.current = setTimeout(() => {
        removeTask(taskId);
        setIsGenerateMaskTaskActive(false);
        setPolygonsLoading(false);
        setScanTimeoutOrError(true);
        aiInferenceDone.delete(roomImageId);
        scanTimeoutRef.current = null;
      }, SCAN_TIMEOUT_MS);
      addTask(taskId, {
        roomImageId,
        onResolve: (data) => {
          clearBootstrapInFlight();
          setIsGenerateMaskTaskActive(false);
          setGenerateMaskConflictRetrySeconds(null);
          if (scanTimeoutRef.current) {
            clearTimeout(scanTimeoutRef.current);
            scanTimeoutRef.current = null;
          }
          setScanTimeoutOrError(false);
          console.log(`GET /ai-inference/${taskId} response (resolved):`, data);
          const resolvedList = Array.isArray(data?.taskResult)
            ? data.taskResult
            : Array.isArray(data?.polygons)
              ? data.polygons
              : Array.isArray(data)
                ? data
                : [];
          setInteriorsFromExisting(false);
          applyResult(resolvedList);
        },
        onError: (err) => {
          clearBootstrapInFlight();
          setIsGenerateMaskTaskActive(false);
          if (scanTimeoutRef.current) {
            clearTimeout(scanTimeoutRef.current);
            scanTimeoutRef.current = null;
          }
          console.error('Task failed:', err);
          console.log(
            '[Scan overlay] generate_mask task failed; setting scanTimeoutOrError=true for roomImageId:',
            roomImageId
          );
          aiInferenceDone.delete(roomImageId);
          setPolygonsLoading(false);
          setAiNotification({
            task: 'generate_mask',
            status: 'Failed',
            message:
              err?.status === 503
                ? 'Service is not available, please try again later.'
                : err?.message || 'Scan failed. Please try again.',
          });
          // Show the same Scan overlay used for 1-minute timeout,
          // so the user can easily retry the task.
          setScanTimeoutOrError(true);
        },
      });
    };

    const runInference = () => {
      if (isGenerateMaskTaskActive) {
        const wait = Number.isFinite(Number(generateMaskConflictRetrySeconds))
          ? Math.max(1, Number(generateMaskConflictRetrySeconds))
          : 30;
        setAiNotification({
          task: 'generate_mask',
          status: 'Started',
          message: `A similar task is already running, please wait for the result or try again after ${wait} seconds.`,
        });
        return;
      }
      setIsGenerateMaskTaskActive(true);
      setScanNoDetections(false);
      runAiInference({
        task_name: 'generate_mask',
        room_image_id: roomImageId,
        labels: [],
      })
        .then((res) => {
          //console.log('POST /ai-inference response:', res);
          const list = Array.isArray(res?.taskResult)
            ? res.taskResult
            : Array.isArray(res?.polygons)
                ? res.polygons
                : Array.isArray(res)
                  ? res
                  : [];
          const taskId = res.taskId;
          const hasResult =
            Array.isArray(res?.taskResult) ||
            Array.isArray(res?.polygons);
          if (hasResult) {
            setInteriorsFromExisting(false);
            applyResult(list);
            return;
          }
          if (taskId == null) {
            clearBootstrapInFlight();
            setIsGenerateMaskTaskActive(false);
            setPolygonsLoading(false);
            setAiNotification({
              task: 'generate_mask',
              status: 'Failed',
              message: 'Scan could not be started. Please try again.',
            });
            return;
          }
          //console.log('taskId:', taskId, '- added to taskNeedResolve (WebSocket only)');
          trackGenerateMaskTask(taskId);
        })
        .catch((err) => {
          if (err?.status === 409) {
            const retryAfterSeconds = Number(
              err?.retryAfterSeconds ??
              err?.retry_after_seconds ??
              err?.retryAfter ??
              err?.retry_after ??
              err?.details?.retryAfterSeconds ??
              err?.details?.retry_after_seconds ??
              err?.details?.retryAfter ??
              err?.details?.retry_after
            );
            const wait = Number.isFinite(retryAfterSeconds)
              ? Math.max(1, Math.ceil(retryAfterSeconds))
              : 30;
            setGenerateMaskConflictRetrySeconds(wait);
            const conflictTaskId = err?.taskId ?? err?.details?.taskId ?? err?.details?.aiTaskId ?? err?.details?.id;
            setAiNotification({
              task: 'generate_mask',
              status: 'Started',
              message: `A similar task is already running, please wait for the result or try again after ${wait} seconds.`,
            });
            setScanTimeoutOrError(false);
            if (conflictTaskId != null) {
              setPolygonsLoading(true);
              trackGenerateMaskTask(conflictTaskId);
            } else {
              clearBootstrapInFlight();
              setIsGenerateMaskTaskActive(false);
              setPolygonsLoading(false);
            }
            // Do not surface 409 as a hard failure overlay; backend may already be processing
            // and WebSocket/catch-up will resolve the running task shortly.
            return;
          }
          setIsGenerateMaskTaskActive(false);
          clearBootstrapInFlight();
          aiInferenceDone.delete(roomImageId);
          setPolygonsLoading(false);
          setScanTimeoutOrError(true);
          const msg =
            err?.status === 503
              ? 'Service is not available, please try again later.'
              : err?.message || 'AI inference failed. Please try again.';
          setError(msg);
          setAiNotification({
            task: 'generate_mask',
            status: 'Failed',
            message: msg,
          });
          console.error('AI inference failed:', err);
        });
    };

    if (!forceGenerateMask && generateMaskBootstrapInFlight.has(imageKey)) {
      return;
    }
    generateMaskBootstrapInFlight.add(imageKey);
    setPolygonsLoading(true);
    if (roomId != null) {
      getInteriors(roomId, roomImageId)
        .then(async (existing) => {
          const list = Array.isArray(existing) ? existing : [];
          if (list.length > 0 && !forceGenerateMask) {
            console.log('Using existing interiors for room image:', roomImageId, list.length, 'items');
            setInteriorsFromExisting(true);
            setInteriorsSavedOnce(true);
            applyResult(list);
            return;
          }
          if (forceGenerateMask && list.length > 0) {
            // Re-scan should replace previous detections. Remove existing interiors first
            // so backend does not accumulate duplicate rows for the same room image.
            const idsToDelete = list
              .map((item) => Number(item?.id ?? item?.interior_id))
              .filter((id) => Number.isFinite(id));
            if (idsToDelete.length > 0) {
              await Promise.allSettled(
                idsToDelete.map((id) => deleteInterior(roomId, roomImageId, id))
              );
            }
          }
          runInference();
        })
        .catch(() => {
          runInference();
        });
    } else {
      runInference();
    }
  }, [
    roomImageId,
    roomId,
    inferenceRetryKey,
    pendingDetectedObjects,
    detectedObjectsFromAPI,
    polygons,
  ]);

  // Preload interior types when design page has roomImageId so popup labels (Walls, Floors, etc.) show instead of "Object N"
  useEffect(() => {
    if (roomImageId == null) return;
    if (interiorTypesList.length > 0 || interiorTypesLoading) return;
    setInteriorTypesLoading(true);
    getAllInteriorTypes()
      .then((list) => setInteriorTypesList(list))
      .catch((err) => console.error('Failed to load interior types:', err))
      .finally(() => setInteriorTypesLoading(false));
  }, [roomImageId, interiorTypesList.length, interiorTypesLoading]);

  // Load interior types when user enters edit mode (for label search dropdown)
  useEffect(() => {
    if (editingLayerIndex == null) return;
    if (interiorTypesList.length > 0 || interiorTypesLoading) return;
    setInteriorTypesLoading(true);
    getAllInteriorTypes()
      .then((list) => setInteriorTypesList(list))
      .catch((err) => console.error('Failed to load interior types:', err))
      .finally(() => setInteriorTypesLoading(false));
  }, [editingLayerIndex, interiorTypesList.length, interiorTypesLoading]);

  // Load interior types when in modal "Edit label for new object" step (for search dropdown)
  useEffect(() => {
    if (objectChoiceStep !== 'editNewObjectLabel') return;
    if (interiorTypesList.length > 0 || interiorTypesLoading) return;
    setInteriorTypesLoading(true);
    getAllInteriorTypes()
      .then((list) => setInteriorTypesList(list))
      .catch((err) => console.error('Failed to load interior types:', err))
      .finally(() => setInteriorTypesLoading(false));
  }, [objectChoiceStep, interiorTypesList.length, interiorTypesLoading]);

  // Load interior types when we have polygons or popup (so we can resolve label from interior_type_id for GET /interiors data)
  useEffect(() => {
    const hasObjects = (polygons?.length ?? 0) > 0 || (pendingDetectedObjects != null && pendingDetectedObjects.length > 0);
    if (!hasObjects) return;
    if (interiorTypesList.length > 0 || interiorTypesLoading) return;
    setInteriorTypesLoading(true);
    getAllInteriorTypes()
      .then((list) => setInteriorTypesList(list))
      .catch((err) => console.error('Failed to load interior types:', err))
      .finally(() => setInteriorTypesLoading(false));
  }, [polygons?.length, pendingDetectedObjects?.length, interiorTypesList.length, interiorTypesLoading]);

  // Load room images (history) when History panel is opened
  useEffect(() => {
    if (!showHistoryPanel || roomId == null) return;
    setHistoryLoading(true);
    setHistoryError(null);
    getRoomImages(roomId)
      .then((list) => {
        setHistoryImages(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        setHistoryError(err?.message ?? 'Failed to load history');
        setHistoryImages([]);
      })
      .finally(() => setHistoryLoading(false));
  }, [showHistoryPanel, roomId]);

  const handleHistoryPreviewItem = useCallback(
    (item) => {
      const nextUrl = resolveRoomImageUrl(item);
      const previewImageId = item?.id;
      if (!nextUrl || previewImageId == null) {
        setError('Selected history image is missing URL data.');
        return;
      }
      setError(null);
      setSwapped(false);
      setPreview(null);
      setResultImageElement(null);
      setLastMaterialTransferResultKey(null);
      setShowCompareSlider(false);
      setCompareSliderPosition(50);
      setShowMaterialsGridDuringAiResult(false);
      setHistoryPreviewImageId(previewImageId);
      setPhotorealisticImage(nextUrl);
      const apiDims = parseRoomImageDimensionsFromApi(item);
      setRoomImageDimensions(apiDims ?? null);
    },
    [setPhotorealisticImage, setRoomImageDimensions]
  );

  const handleHistoryRestoreAsActiveBase = useCallback(
    async (item) => {
      const nextUrl = resolveRoomImageUrl(item);
      const nextImageId = item?.id;
      if (!nextUrl || nextImageId == null) {
        setError('Selected history image is missing URL data.');
        return;
      }
      // Switch canvas base image to the selected history snapshot.
      setError(null);
      setSwapped(false);
      setPreview(null);
      setResultImageElement(null);
      setLastMaterialTransferResultKey(null);
      setShowCompareSlider(false);
      setCompareSliderPosition(50);
      setShowMaterialsGridDuringAiResult(false);
      suppressNextRecoveryModalRef.current = true;
      setShowSessionRecoveryModal(false);
      setRecoveryDraftPayload(null);
      setRoomImageId(nextImageId);
      setPhotorealisticImage(nextUrl);
      const apiDims = parseRoomImageDimensionsFromApi(item);
      setRoomImageDimensions(apiDims ?? null);
      setHistoryPreviewImageId(nextImageId);
      setRemoveObjectMode(false);
      setRemoveObjectActionsVisible(false);
      setObjectListOverflowMenuIndex(null);
      setSelectedPolygonIndex(null);
      setMultiSelectedPolygonIndices(new Set());
      setShowAllPolygonsOnCanvas(false);
      setEditingLayerIndex(null);
      resetEditorState();

      // Critical: interiors are versioned per room_image_id. Reload the list for the selected history
      // image so remove_object sends room_interior_id values that belong to that image version.
      let historyInteriorsLoadFailed = false;
      if (roomId != null) {
        try {
          const interiors = await getInteriors(roomId, nextImageId);
          const list = Array.isArray(interiors) ? interiors : [];
          setDetectedObjectsFromAPI(list);
          setPendingDetectedObjects(null);
          setPolygons(list);
          setUserDefinedObjects([]);
          setSelectedObjectIndices(new Set());
          setSelectedUserDefinedIndices(new Set());
          setInteriorsFromExisting(list.length > 0);
          setInteriorsSavedOnce(list.length > 0);
          setObjectChoiceStep('initial');
          if (nextImageId != null) {
            aiInferenceDone.delete(nextImageId);
          }
        } catch (err) {
          historyInteriorsLoadFailed = true;
          console.error('Failed to load interiors for selected history image:', err);
          setDetectedObjectsFromAPI([]);
          setPendingDetectedObjects(null);
          setPolygons([]);
          setUserDefinedObjects([]);
          setSelectedObjectIndices(new Set());
          setSelectedUserDefinedIndices(new Set());
          setInteriorsFromExisting(false);
          setInteriorsSavedOnce(false);
          setObjectChoiceStep('initial');
          setAiNotification({
            task: 'history',
            status: 'Failed',
            message: err?.message || 'Failed to load objects for selected history image.',
          });
        }
      }
      if (!historyInteriorsLoadFailed) {
        setAiNotification({
          task: 'history',
          status: 'Success',
          message: 'History image set as active base.',
        });
      }
      setShowHistoryPanel(false);
      setShowPolygonsOverlay(true);

      // Persist selection so reload restores this image as the room's active base.
      if (roomId != null) {
        try {
          await updateRoom(roomId, {
            latestImageId: nextImageId,
            latestRoomImageId: nextImageId,
            roomImageId: nextImageId,
          });
        } catch (err) {
          console.error('Failed to persist selected history base image:', err);
          setAiNotification({
            task: 'history',
            status: 'Failed',
            message:
              err?.message ||
              'Selected image was applied locally but could not be saved as the default base image.',
          });
        }
      }
    },
    [roomId, setPhotorealisticImage, setRoomImageId, setRoomImageDimensions, resetEditorState]
  );

  const handleUploadStandardMaskLoading = useCallback(
    (active) => {
      setPolygonsLoading(active);
      if (active) {
        setUploadModelMaskFlowActive(true);
        setSelectedPlacedCuratedId(null);
        uploadModelMaskPreviousImageUrlRef.current = photorealisticImage || null;
        setUploadModelMaskResults((prev) => {
          if (uploadModelMaskPreFlowSnapshotRef.current == null && prev.length === 0) {
            uploadModelMaskPreFlowSnapshotRef.current = captureUploadModelMaskPreFlowSnapshot({
              polygons,
              detectedObjectsFromAPI,
              userDefinedObjects,
              selectedPolygonIndex,
              multiSelectedPolygonIndices,
              detectedObjectMultiSelectActive,
              interiorsFromExisting,
              interiorsSavedOnce,
              selectedObjectIndices,
              selectedUserDefinedIndices,
              roomImageDimensions,
            });
          }
          return [];
        });
        setSelectedUploadModelMaskRoomObjectId(null);
        setShowModelStudioPanel(false);
        setShowPromptPanel(false);
        setShowRenderPanel(false);
        setShowExportImagePanel(false);
      setShowExportImagePanel(false);
        setRightPanelCollapsed(false);
        setAiNotification({
          task: 'generate_mask',
          status: 'Started',
          message: 'Generating masks from your upload…',
        });
      }
    },
    [
      photorealisticImage,
      polygons,
      detectedObjectsFromAPI,
      userDefinedObjects,
      selectedPolygonIndex,
      multiSelectedPolygonIndices,
      detectedObjectMultiSelectActive,
      interiorsFromExisting,
      interiorsSavedOnce,
      selectedObjectIndices,
      selectedUserDefinedIndices,
      roomImageDimensions,
    ],
  );

  const applyUploadModelMaskSelection = useCallback(
    async ({ imageUrl, polygons: polygonList, roomObjectId }) => {
      const url = typeof imageUrl === 'string' ? imageUrl.trim() : '';
      if (!url) return;
      try {
        const loaded = await new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error('Failed to load selected generated image.'));
          img.src = url;
        });
        setImage(loaded);
        const rw = Number(loaded.naturalWidth || loaded.width || 0);
        const rh = Number(loaded.naturalHeight || loaded.height || 0);
        if (rw > 0 && rh > 0) {
          setRoomImageDimensions({ width: rw, height: rh });
        } else {
          setRoomImageDimensions(null);
        }
      } catch {
        // Fallback to context image loader path; clear stale reference dims so overlay uses decoded size.
        setRoomImageDimensions(null);
      }
      setPhotorealisticImage(url);
      const list = Array.isArray(polygonList) ? polygonList : [];
      setDetectedObjectsFromAPI(list);
      setPendingDetectedObjects(null);
      setPolygons(list);
      setUserDefinedObjects([]);
      setSelectedObjectIndices(new Set());
      setSelectedUserDefinedIndices(new Set());
      setScanNoDetections(list.length === 0);
      setInteriorsFromExisting(list.length > 0);
      setInteriorsSavedOnce(list.length > 0);
      setObjectChoiceStep('initial');
      setIgnoredLowConfidenceKeys(new Set());
      setHideLowConfidenceBanner(false);
      setShowModelStudioPanel(false);
      setShowHistoryPanel(false);
      setShowPromptPanel(false);
      setShowRenderPanel(false);
      setShowExportImagePanel(false);
      setShowPolygonsOverlay(true);
      setImageSelectionPanelCollapsed(false);
      setRightPanelCollapsed(false);
      setSelectedPolygonIndex(null);
      setMultiSelectedPolygonIndices(new Set());
      setShowAllPolygonsOnCanvas(false);
      setEditingLayerIndex(null);
      if (roomObjectId != null) {
        setSelectedUploadModelMaskRoomObjectId(Number(roomObjectId));
      }
      resetEditorState();
    },
    [setPhotorealisticImage, setRoomImageDimensions, resetEditorState],
  );

  /**
   * Re-run scene detection: room image uses the bootstrap effect + `inferenceRetryKey`.
   * When Image Selection (upload-model mask results) is active, scan the selected room object instead.
   */
  const handleScanFromPopup = useCallback(async () => {
    if (polygonsLoading || isGenerateMaskTaskActive) {
      const wait = Number.isFinite(Number(generateMaskConflictRetrySeconds))
        ? Math.max(1, Number(generateMaskConflictRetrySeconds))
        : 30;
      setAiNotification({
        task: 'generate_mask',
        status: 'Started',
        message: `A similar task is already running, please wait for the result or try again after ${wait} seconds.`,
      });
      return;
    }

    const imageSelectionActive = uploadModelMaskResults.length > 0;

    if (imageSelectionActive) {
      if (roomId == null) {
        setError('Room ID is missing.');
        return;
      }
      const target =
        uploadModelMaskResults.find(
          (r) => Number(r?.roomObjectId) === Number(selectedUploadModelMaskRoomObjectId),
        ) ?? uploadModelMaskResults[0];
      const roomObjectId = Number(target?.roomObjectId);
      if (!Number.isFinite(roomObjectId) || roomObjectId <= 0) {
        setError('No valid uploaded model is selected to scan.');
        return;
      }
      const previousImageUrl =
        typeof target?.imageUrl === 'string' ? target.imageUrl.trim() : '';

      setError(null);
      setScanTimeoutOrError(false);
      setAiNotification({
        task: 'generate_mask',
        status: 'Started',
        message: 'Scanning selected image…',
      });

      const SCAN_TIMEOUT_MS = 60 * 1000;
      let scanTimeoutId = null;
      const trackedTaskRef = { id: null };

      const clearScanTimer = () => {
        if (scanTimeoutId != null) {
          clearTimeout(scanTimeoutId);
          scanTimeoutId = null;
        }
      };

      const finishLoading = () => {
        clearScanTimer();
        if (trackedTaskRef.id != null) {
          removeTask(trackedTaskRef.id);
          trackedTaskRef.id = null;
        }
        setPolygonsLoading(false);
        setIsGenerateMaskTaskActive(false);
      };

      const buildOutcomeFromData = async (data) => {
        const list = extractGenerateMaskPolygonList(data);
        const rawTr = data?.taskResult ?? data?.polygons;
        const resultKey =
          rawTr && !Array.isArray(rawTr)
            ? rawTr.resultS3Key ?? rawTr.result_s3_key
            : null;
        let resultImageUrl = null;
        if (resultKey) {
          const presigned = await getAiInferencePresignedUrl(resultKey);
          resultImageUrl = presigned?.url ?? presigned?.presignedUrl ?? null;
        }
        const fallbackImageUrl = await resolveRoomObjectDisplayImageUrl(roomId, roomObjectId);
        const imageUrl =
          (resultImageUrl && String(resultImageUrl).trim()) ||
          (fallbackImageUrl && String(fallbackImageUrl).trim()) ||
          previousImageUrl ||
          '';
        return { polygons: list, imageUrl };
      };

      const waitForTask = (taskId) =>
        new Promise((resolve, reject) => {
          trackedTaskRef.id = taskId;
          addTask(taskId, {
            onResolve: async (data) => {
              try {
                resolve(await buildOutcomeFromData(data));
              } catch (e) {
                reject(e);
              }
            },
            onError: reject,
          });
        });

      const applySelectionScanOutcome = async (outcome) => {
        const finalImageUrl =
          (outcome.imageUrl && String(outcome.imageUrl).trim()) || previousImageUrl || '';
        setUploadModelMaskResults((prev) =>
          prev.map((entry) =>
            Number(entry?.roomObjectId) === roomObjectId
              ? {
                  ...entry,
                  imageUrl: finalImageUrl || entry.imageUrl,
                  polygons: Array.isArray(outcome.polygons) ? outcome.polygons : [],
                }
              : entry,
          ),
        );
        if (Number(selectedUploadModelMaskRoomObjectId) === roomObjectId) {
          if (finalImageUrl) {
            await applyUploadModelMaskSelection({
              imageUrl: finalImageUrl,
              polygons: outcome.polygons,
              roomObjectId,
            });
          } else {
            const list = Array.isArray(outcome.polygons) ? outcome.polygons : [];
            setDetectedObjectsFromAPI(list);
            setPolygons(list);
            setScanNoDetections(list.length === 0);
          }
        }
        setAiNotification({
          task: 'generate_mask',
          status: 'Success',
          message: 'Scan complete.',
        });
      };

      setPolygonsLoading(true);
      setIsGenerateMaskTaskActive(true);
      scanTimeoutId = setTimeout(() => {
        finishLoading();
        setAiNotification({
          task: 'generate_mask',
          status: 'Failed',
          message: 'Scan timed out. Please try again.',
        });
      }, SCAN_TIMEOUT_MS);

      try {
        const res = await runAiInference({
          task_name: 'generate_mask',
          room_object_id: roomObjectId,
          labels: [],
        });

        let outcome;
        const hasImmediatePayload =
          Array.isArray(res?.taskResult) || Array.isArray(res?.polygons) || Array.isArray(res);
        if (hasImmediatePayload) {
          outcome = await buildOutcomeFromData(res);
        } else {
          const taskId = res?.taskId ?? res?.aiTaskId ?? res?.id;
          if (taskId == null) throw new Error('Scan could not be started.');
          try {
            outcome = await waitForTask(taskId);
          } finally {
            trackedTaskRef.id = null;
          }
        }

        clearScanTimer();
        trackedTaskRef.id = null;
        setPolygonsLoading(false);
        setIsGenerateMaskTaskActive(false);
        await applySelectionScanOutcome(outcome);
      } catch (err) {
        if (err?.status === 409) {
          const retryAfterSeconds = Number(
            err?.retryAfterSeconds ??
              err?.retry_after_seconds ??
              err?.retryAfter ??
              err?.retry_after ??
              err?.details?.retryAfterSeconds ??
              err?.details?.retry_after_seconds ??
              err?.details?.retryAfter ??
              err?.details?.retry_after
          );
          const wait = Number.isFinite(retryAfterSeconds)
            ? Math.max(1, Math.ceil(retryAfterSeconds))
            : 30;
          setGenerateMaskConflictRetrySeconds(wait);
          const conflictTaskId =
            err?.taskId ?? err?.details?.taskId ?? err?.details?.aiTaskId ?? err?.details?.id;
          setAiNotification({
            task: 'generate_mask',
            status: 'Started',
            message: `A similar task is already running, please wait for the result or try again after ${wait} seconds.`,
          });
          if (conflictTaskId != null) {
            try {
              const outcome = await waitForTask(conflictTaskId);
              clearScanTimer();
              trackedTaskRef.id = null;
              setPolygonsLoading(false);
              setIsGenerateMaskTaskActive(false);
              await applySelectionScanOutcome(outcome);
              return;
            } catch (innerErr) {
              clearScanTimer();
              trackedTaskRef.id = null;
              setPolygonsLoading(false);
              setIsGenerateMaskTaskActive(false);
              setAiNotification({
                task: 'generate_mask',
                status: 'Failed',
                message: innerErr?.message || 'Scan failed. Please try again.',
              });
              return;
            }
          }
        }
        finishLoading();
        const msg =
          err?.status === 503
            ? 'Service is not available, please try again later.'
            : err?.message || 'Scan failed. Please try again.';
        setError(msg);
        setAiNotification({
          task: 'generate_mask',
          status: 'Failed',
          message: msg,
        });
      }
      return;
    }

    if (roomId == null || roomImageId == null) {
      setError('No room image is available to scan yet.');
      return;
    }
    setError(null);
    setScanTimeoutOrError(false);
    setScanNoDetections(false);
    setGenerateMaskConflictRetrySeconds(null);
    // Explicit re-scan should replace local detections, not stack new ones on top.
    setPendingDetectedObjects(null);
    setDetectedObjectsFromAPI([]);
    setUserDefinedObjects([]);
    setSelectedObjectIndices(new Set());
    setSelectedUserDefinedIndices(new Set());
    setPolygons([]);
    setObjectChoiceStep('initial');
    forceGenerateMaskRef.current = true;
    aiInferenceDone.delete(roomImageId);
    setPolygonsLoading(true);
    setInferenceRetryKey((k) => k + 1);
  }, [
    roomId,
    roomImageId,
    polygonsLoading,
    isGenerateMaskTaskActive,
    generateMaskConflictRetrySeconds,
    uploadModelMaskResults,
    selectedUploadModelMaskRoomObjectId,
    applyUploadModelMaskSelection,
  ]);

  const handleUploadStandardMaskError = useCallback(({ message }) => {
    const msg = message || 'Failed to generate masks.';
    setError(msg);
    setUploadModelMaskFlowActive(false);
    setAiNotification({
      task: 'generate_mask',
      status: 'Failed',
      message: msg,
    });
  }, []);

  const handleUploadStandardMaskComplete = useCallback(
    async ({ imageUrl, polygons: polygonList, roomObjectId }) => {
      const url = typeof imageUrl === 'string' ? imageUrl.trim() : '';
      if (!url) {
        const msg = 'Mask result did not include an image URL.';
        setError(msg);
        setAiNotification({
          task: 'generate_mask',
          status: 'Failed',
          message: msg,
        });
        return;
      }
      setError(null);
      setSwapped(false);
      setPreview(null);
      const normalizedRoomObjectId = Number(roomObjectId);
      setUploadModelMaskResults((prev) => {
        const nextEntry = {
          roomObjectId: Number.isFinite(normalizedRoomObjectId) ? normalizedRoomObjectId : prev.length + 1,
          imageUrl: url,
          polygons: Array.isArray(polygonList) ? polygonList : [],
        };
        const existingIndex = prev.findIndex((item) => item.roomObjectId === nextEntry.roomObjectId);
        if (existingIndex >= 0) {
          const cloned = [...prev];
          cloned[existingIndex] = nextEntry;
          return cloned;
        }
        return [...prev, nextEntry];
      });
      await applyUploadModelMaskSelection({
        imageUrl: url,
        polygons: polygonList,
        roomObjectId: normalizedRoomObjectId,
      });
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        await new Promise((resolve) => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(resolve);
          });
        });
      }
      setAiNotification({
        task: 'generate_mask',
        status: 'Success',
        message: 'Masks ready.',
      });
    },
    [applyUploadModelMaskSelection],
  );

  const handleCancelUploadModelMaskSelection = useCallback(async () => {
    const idsToDelete = uploadModelMaskResults
      .map((r) => Number(r?.roomObjectId))
      .filter((n) => Number.isFinite(n));

    if (roomId != null && idsToDelete.length > 0) {
      const purgeSet = new Set(idsToDelete.map(String));
      await Promise.all(
        idsToDelete.map(async (objectId) => {
          try {
            await deleteRoomObject(roomId, objectId);
          } catch (err) {
            console.error('Failed to delete temporary upload-model room object', objectId, err);
          }
        }),
      );
      setStudioCuratedLibraryItems((prev) => prev.filter((item) => !purgeSet.has(String(item.id))));
    }

    const previousUrl = uploadModelMaskPreviousImageUrlRef.current;
    if (previousUrl && typeof previousUrl === 'string') {
      setPhotorealisticImage(previousUrl);
    }
    const snap = uploadModelMaskPreFlowSnapshotRef.current;
    uploadModelMaskPreFlowSnapshotRef.current = null;
    if (snap) {
      const restoredApi = Array.isArray(snap.detectedObjectsFromAPI) ? snap.detectedObjectsFromAPI : [];
      const restoredUser = Array.isArray(snap.userDefinedObjects) ? snap.userDefinedObjects : [];
      let selectedObjIdx = new Set(
        [...(snap.selectedObjectIndices ?? [])].map((x) => Number(x)).filter((n) => Number.isFinite(n)),
      );
      let selectedUsrIdx = new Set(
        [...(snap.selectedUserDefinedIndices ?? [])].map((x) => Number(x)).filter((n) => Number.isFinite(n)),
      );
      if (selectedObjIdx.size === 0 && selectedUsrIdx.size === 0 && (restoredApi.length > 0 || restoredUser.length > 0)) {
        const { detectedSet, userSet } = buildPreselectedSetsFromPolygons(
          Array.isArray(snap.polygons) ? snap.polygons : [],
          restoredApi,
          restoredUser,
        );
        selectedObjIdx = detectedSet;
        selectedUsrIdx = userSet;
      }

      const activePolygons = [];
      restoredApi.forEach((obj, i) => {
        if (selectedObjIdx.has(i)) activePolygons.push(obj);
      });
      restoredUser.forEach((obj, i) => {
        if (selectedUsrIdx.has(i)) activePolygons.push(obj);
      });
      if (activePolygons.length === 0 && Array.isArray(snap.polygons) && snap.polygons.length > 0) {
        activePolygons.push(...snap.polygons);
      }

      const len = activePolygons.length;
      let nextSelected = snap.selectedPolygonIndex;
      if (nextSelected != null && (nextSelected < 0 || nextSelected >= len)) {
        nextSelected = len > 0 ? 0 : null;
      }
      const validMulti = new Set(
        (snap.multiSelectedPolygonIndices ?? []).filter(
          (i) => Number.isInteger(i) && i >= 0 && i < len,
        ),
      );

      setUserDefinedObjects(restoredUser);
      setDetectedObjectsFromAPI(restoredApi);
      setSelectedObjectIndices(selectedObjIdx);
      setSelectedUserDefinedIndices(selectedUsrIdx);
      setPolygons(activePolygons);
      setScanNoDetections(len === 0);
      setSelectedPolygonIndex(nextSelected);
      setMultiSelectedPolygonIndices(validMulti);
      setDetectedObjectMultiSelectActive(
        validMulti.size > 0 ? snap.detectedObjectMultiSelectActive : false,
      );
      setInteriorsFromExisting(snap.interiorsFromExisting);
      setInteriorsSavedOnce(snap.interiorsSavedOnce);
      setShowAllPolygonsOnCanvas(false);
      setExpandedGroupLabel(null);
      setEditingLayerIndex(null);
      setSwapped(false);
      setPreview(null);
      resetEditorState();

      setPendingDetectedObjects(null);
      setObjectChoiceStep('initial');
      selectObjectsSnapshotRef.current = null;
      setDeleteDetectionMode(false);
      applyLowConfidenceStateForObjectList(restoredApi);
    } else {
      setPolygons([]);
      setDetectedObjectsFromAPI([]);
      setUserDefinedObjects([]);
      setPendingDetectedObjects(null);
      setObjectChoiceStep('initial');
      setScanNoDetections(true);
      setInteriorsFromExisting(false);
      setInteriorsSavedOnce(false);
      setSelectedPolygonIndex(null);
      setMultiSelectedPolygonIndices(new Set());
      setDetectedObjectMultiSelectActive(false);
      setSelectedObjectIndices(new Set());
      setSelectedUserDefinedIndices(new Set());
      setIgnoredLowConfidenceKeys(new Set());
      setHideLowConfidenceBanner(false);
      resetEditorState();
    }
    if (snap) {
      const rd = snap.roomImageDimensions;
      if (rd && Number(rd.width) > 0 && Number(rd.height) > 0) {
        setRoomImageDimensions({ width: Number(rd.width), height: Number(rd.height) });
      } else {
        setRoomImageDimensions(null);
      }
    } else {
      setRoomImageDimensions(null);
    }
    setUploadModelMaskResults([]);
    setUploadModelMaskFlowActive(false);
    setSelectedUploadModelMaskRoomObjectId(null);
    setImageSelectionPanelCollapsed(true);
    setShowPolygonsOverlay(false);
    setPolygonsLoading(false);
    setShowModelStudioPanel(true);
    setShowHistoryPanel(false);
    setModelLibraryModalOpen(false);
    setModelLibraryDetectedSelectionIds(null);
    setRightPanelCollapsed(false);
  }, [
    roomId,
    uploadModelMaskResults,
    setPhotorealisticImage,
    setRoomImageDimensions,
    resetEditorState,
    applyLowConfidenceStateForObjectList,
  ]);

  const handleSaveSelectedPolygonAsObject = useCallback(async () => {
    if (roomId == null) {
      setError('Room ID is missing.');
      return;
    }
    if (!image) {
      setError('No image is available to crop from.');
      return;
    }
    if (!Array.isArray(polygons) || polygons.length === 0) {
      setAiNotification({
        task: 'save_object',
        status: 'Please select an object first',
        message: '',
      });
      return;
    }

    let indicesToSave = [];
    if (detectedObjectMultiSelectActive && multiSelectedPolygonIndices.size > 0) {
      indicesToSave = [...multiSelectedPolygonIndices]
        .sort((a, b) => a - b)
        .filter((i) => Number.isInteger(i) && i >= 0 && i < polygons.length && polygons[i]);
    } else if (selectedPolygonIndex != null && polygons[selectedPolygonIndex]) {
      indicesToSave = [selectedPolygonIndex];
    }

    if (indicesToSave.length === 0) {
      setAiNotification({
        task: 'save_object',
        status: 'Please select an object first',
        message: '',
      });
      return;
    }

    const naturalWidth = Number(image.naturalWidth || image.width || 0);
    const naturalHeight = Number(image.naturalHeight || image.height || 0);
    if (!naturalWidth || !naturalHeight) {
      setError('Source image dimensions are invalid.');
      return;
    }
    const refWidth = Number(roomImageDimensions?.width) > 0 ? Number(roomImageDimensions.width) : naturalWidth;
    const refHeight = Number(roomImageDimensions?.height) > 0 ? Number(roomImageDimensions.height) : naturalHeight;
    const sx = naturalWidth / refWidth;
    const sy = naturalHeight / refHeight;

    const cropIndexToPngFile = (polyIndex) => {
      const target = polygons[polyIndex];
      const rings = normalizePolygonToRings(target?.polygon ?? target?.polygons);
      if (!Array.isArray(rings) || rings.length === 0 || !Array.isArray(rings[0]) || rings[0].length < 3) {
        throw new Error(`Object ${polyIndex + 1} polygon is invalid.`);
      }

      const points = rings.flat().filter((pt) => Array.isArray(pt) && pt.length >= 2);
      if (points.length < 3) {
        throw new Error(`Object ${polyIndex + 1} has insufficient points.`);
      }
      const xs = points.map((pt) => Number(pt[0]) * sx).filter((v) => Number.isFinite(v));
      const ys = points.map((pt) => Number(pt[1]) * sy).filter((v) => Number.isFinite(v));
      if (!xs.length || !ys.length) {
        throw new Error(`Object ${polyIndex + 1} has invalid points.`);
      }
      const minX = Math.max(0, Math.floor(Math.min(...xs)));
      const minY = Math.max(0, Math.floor(Math.min(...ys)));
      const maxX = Math.min(naturalWidth, Math.ceil(Math.max(...xs)));
      const maxY = Math.min(naturalHeight, Math.ceil(Math.max(...ys)));
      const cropW = Math.max(1, maxX - minX);
      const cropH = Math.max(1, maxY - minY);

      const canvas = document.createElement('canvas');
      canvas.width = cropW;
      canvas.height = cropH;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        throw new Error('Could not create crop canvas.');
      }
      ctx.clearRect(0, 0, cropW, cropH);
      ctx.drawImage(image, -minX, -minY);
      ctx.globalCompositeOperation = 'destination-in';
      ctx.beginPath();
      rings.forEach((ring) => {
        if (!Array.isArray(ring) || ring.length < 3) return;
        const [fx, fy] = ring[0];
        ctx.moveTo(Number(fx) * sx - minX, Number(fy) * sy - minY);
        for (let i = 1; i < ring.length; i += 1) {
          const pt = ring[i];
          ctx.lineTo(Number(pt[0]) * sx - minX, Number(pt[1]) * sy - minY);
        }
        ctx.closePath();
      });
      ctx.fill('evenodd');
      ctx.globalCompositeOperation = 'source-over';

      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error(`Failed to export object ${polyIndex + 1}.`));
            return;
          }
          const rawLabel = getLabelForInterior(target, interiorTypesList, polyIndex) || 'Extracted Object';
          const safeBase = String(rawLabel).replace(/[^\w\- ]+/g, '').trim() || 'Extracted Object';
          const slug = safeBase.toLowerCase().replace(/\s+/g, '-') || 'extracted-object';
          const filename =
            indicesToSave.length > 1 ? `${slug}-${polyIndex + 1}.png` : `${slug}.png`;
          resolve({
            file: new File([blob], filename, { type: 'image/png' }),
            name: safeBase,
          });
        }, 'image/png');
      });
    };

    setSaveSelectedObjectLoading(true);
    let successCount = 0;
    let firstErr = null;
    const sessionItemsForDetectedModels = [];

    try {
      for (const polyIndex of indicesToSave) {
        try {
          const { file, name } = await cropIndexToPngFile(polyIndex);
          const createRes = await createRoomObject(roomId, {
            name,
            category: 'Other',
            image: file,
          });
          const roomObjectIdRaw =
            createRes?.id ?? createRes?.objectId ?? createRes?.object_id ?? createRes?.roomObjectId ?? createRes?.room_object_id;
          const roomObjectId = Number(roomObjectIdRaw);
          if (!Number.isFinite(roomObjectId)) {
            throw new Error('Saved model did not return a room object id.');
          }
          sessionItemsForDetectedModels.push(
            buildDetectedModelsSessionItemFromCanvasCrop({
              file,
              displayName: name,
              roomObjectId,
            })
          );
          successCount += 1;
        } catch (err) {
          if (!firstErr) firstErr = err;
        }
      }

      if (successCount > 0) {
        setAiNotification({
          task: 'save_object',
          status: 'Success',
          message:
            successCount === indicesToSave.length
              ? `Saved ${successCount} object${successCount === 1 ? '' : 's'} to Model Library.`
              : `Saved ${successCount} of ${indicesToSave.length} objects.`,
        });
        setRightPanelCollapsed(false);
        setStudioModels((prev) => [...sessionItemsForDetectedModels, ...prev]);
        // Let ModelLibraryModal auto-select *all* isolated rows in the current session.
        setModelLibraryDetectedSelectionIds(null);
        setModelLibraryInitialStep('detectedModels');
        setModelLibraryModalKey((k) => k + 1);
        setModelLibraryModalOpen(true);
      }

      if (successCount < indicesToSave.length) {
        const msg =
          firstErr?.message ||
          (successCount === 0 ? 'Failed to save objects.' : 'Some objects failed to save.');
        setError(msg);
        if (successCount === 0) {
          setAiNotification({
            task: 'save_object',
            status: 'Failed',
            message: msg,
          });
        }
      }
    } finally {
      setSaveSelectedObjectLoading(false);
    }
  }, [
    roomId,
    image,
    polygons,
    selectedPolygonIndex,
    detectedObjectMultiSelectActive,
    multiSelectedPolygonIndices,
    roomImageDimensions,
    interiorTypesList,
  ]);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      // Data URLs are same-origin but we keep this consistent with other image loads.
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImage(img);
        setPreview(null);
        setError(null);
        setSwapped(false);
        setUploadModelMaskResults([]);
        setSelectedUploadModelMaskRoomObjectId(null);
        setPolygons(null);
        setPolygonsLoading(false);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleExecute = async () => {
    if (!image) {
      setError('Please upload an image first');
      return;
    }
    if (!gpuInfo) {
      setError('GPU detection not complete. Please wait...');
      return;
    }
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const device = gpuInfo.device;
      const result = await processImage(MODEL_PATH, image, device);
      setPreview(result.canvas.toDataURL());
      setExecutionTime(result.executionTime);
      setSwapped(false);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to process image');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setPhotorealisticImage(null);
    setRoomImageDimensions(null);
    setCurrentView('StartDesigning');
  };

  useEffect(() => {
    if (scanTimeoutOrError) {
      console.log(
        '[Scan overlay] Showing scan overlay due to timeout or failed generate_mask task for roomImageId:',
        roomImageId
      );
    }
  }, [scanTimeoutOrError, roomImageId]);

  useEffect(() => {
    if (!Number.isFinite(Number(generateMaskConflictRetrySeconds))) return undefined;
    if (Number(generateMaskConflictRetrySeconds) <= 1) return undefined;
    const tick = setInterval(() => {
      setGenerateMaskConflictRetrySeconds((prev) => {
        if (!Number.isFinite(Number(prev))) return prev;
        const next = Number(prev) - 1;
        return next > 0 ? next : 0;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [generateMaskConflictRetrySeconds]);

  const handleMaterialResultCancel = useCallback(() => {
    if (operationTypeForApply === 'remove_object') {
      if (removeResultRefineOriginImageRef.current) {
        setImage(removeResultRefineOriginImageRef.current);
      }
      if (removeResultRefineOriginUrlRef.current) {
        setPhotorealisticImage(removeResultRefineOriginUrlRef.current);
      }
      if (Array.isArray(removeResultRefineOriginPolygonsRef.current)) {
        const restoredPolygons = removeResultRefineOriginPolygonsRef.current;
        setPolygons(restoredPolygons);
        if (roomId != null && roomImageId != null) {
          void persistInteriors(restoredPolygons).catch((err) => {
            console.error('Failed to restore polygons after cancelling refine:', err);
          });
        }
      }
      removeResultRefineOriginImageRef.current = null;
      removeResultRefineOriginUrlRef.current = null;
      removeResultRefineOriginPolygonsRef.current = null;
      setRemoveObjectRefinePending(false);
    }
    setPreview(null);
    setSwapped(false);
    setShowCompareSlider(false);
    // Result preview hid the Objects toggle; bring polygon overlay back so canvas matches the list.
    if (operationTypeForApply === 'remove_object') {
      setShowPolygonsOverlay(true);
    }
    setLastMaterialTransferResultKey(null);
    setResultImageElement(null);
    setShowMaterialsGridDuringAiResult(false);
  }, [
    operationTypeForApply,
    setPhotorealisticImage,
    persistInteriors,
    roomId,
    roomImageId,
  ]);

  const handleRemoveObjectResultRefine = useCallback(() => {
    const ctx = lastAiInferenceRegenerateContextRef.current;
    const removeIndicesFromCtx =
      ctx?.type === 'remove_object' && Array.isArray(ctx?.polygonIndices)
        ? ctx.polygonIndices
            .map((i) => Number(i))
            .filter((i) => Number.isInteger(i))
            .sort((a, b) => a - b)
        : [];
    const activeRemoveIndices =
      removeIndicesFromCtx.length > 0
        ? removeIndicesFromCtx
        : (removeObjectLastRunIndicesRef.current ?? []);
    const preferredIdx =
      ctx?.type === 'remove_object' && Number.isInteger(ctx?.polygonIndex)
        ? ctx.polygonIndex
        : activeRemoveIndices.length > 0
          ? activeRemoveIndices[0]
          : null;
    const idx =
      preferredIdx != null && polygons?.[preferredIdx]
        ? preferredIdx
        : activeRemoveIndices.find((i) => polygons?.[i]) ?? null;
    const targetItem = idx != null ? polygons?.[idx] : null;
    if (idx == null || !targetItem) {
      setAiNotification({
        task: 'remove_object',
        status: 'Please select a region first',
        message: '',
      });
      return;
    }
    const enterRefineMode = () => {
      if (!removeResultRefineOriginImageRef.current) {
        removeResultRefineOriginImageRef.current = image;
      }
      if (!removeResultRefineOriginUrlRef.current) {
        removeResultRefineOriginUrlRef.current = photorealisticImage ?? image?.src ?? null;
      }
      if (!removeResultRefineOriginPolygonsRef.current && Array.isArray(polygons)) {
        removeResultRefineOriginPolygonsRef.current = JSON.parse(JSON.stringify(polygons));
      }
      // Exit result preview and enter layer-edit mode so EditorActionToolbar is shown.
      setSwapped(false);
      setShowCompareSlider(false);
      setRemoveObjectMode(false);
      setRemoveObjectActionsVisible(false);
      setShowPolygonsOverlay(true);
      setShowHistoryPanel(false);
      setShowMaterialsPanel(false);
      const nextMultiIndices = new Set(activeRemoveIndices.filter((i) => polygons?.[i]));
      if (nextMultiIndices.size > 1) {
        setDetectedObjectMultiSelectActive(true);
        setMultiSelectedPolygonIndices(nextMultiIndices);
      }
      setSelectedPolygonIndex(idx);
      setPolygonBottomActionsOpen(false);
      polygonAtEditStartRef.current = { ...targetItem };
      setRemoveObjectRefinePending(true);
      setEditingLayerIndex(idx);
      resetEditorState();
    };

    // Refine should start from AI result image, not from the pre-remove base.
    if (resultImageElement) {
      setImage(resultImageElement);
      if (preview) setPhotorealisticImage(preview);
      enterRefineMode();
      return;
    }

    if (preview) {
      const refinedBase = new Image();
      refinedBase.crossOrigin = 'anonymous';
      refinedBase.onload = () => {
        setImage(refinedBase);
        setPhotorealisticImage(preview);
        enterRefineMode();
      };
      refinedBase.onerror = () => {
        setAiNotification({
          task: 'remove_object',
          status: 'Failed',
          message: 'Result image could not be prepared for refinement.',
        });
      };
      refinedBase.src = preview;
      return;
    }

    setAiNotification({
      task: 'remove_object',
      status: 'Failed',
      message: 'No AI result image found to refine.',
    });
  }, [
    polygons,
    preview,
    resetEditorState,
    resultImageElement,
    setPhotorealisticImage,
    image,
    photorealisticImage,
    polygons,
  ]);

  const handleMaterialResultDownload = useCallback(async () => {
    if (!preview) return;
    try {
      const res = await fetch(preview, { mode: 'cors' });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `material-result-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download result image failed:', err);
    }
  }, [preview]);

  const handleMaterialResultApply = useCallback(async (overrides = {}) => {
    const resultKey = overrides.resultS3Key ?? lastMaterialTransferResultKey;
    const opType = overrides.operationType ?? operationTypeForApply;
    const appliedImageElement = overrides.imageElement ?? resultImageElement;
    const appliedPreviewUrl = overrides.previewUrl ?? preview;
    if (!resultKey || !roomId || roomImageId == null) return;
    /** Polygons the user was working with before apply (may be a subset after "Choose object"). */
    const polygonsBeforeApply = polygons;
    const removeContext =
      opType === 'remove_object'
        ? lastAiInferenceRegenerateContextRef.current
        : null;
    const removeInteriorIds = new Set(
      Array.isArray(removeContext?.interiorIds)
        ? removeContext.interiorIds
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        : []
    );
    try {
      const applyBody = {
        parent_room_image_id: roomImageId,
        ai_result_image_s3_key: resultKey,
        operation_type: opType,
        prompt: '',
      };
      console.log('PUT /rooms/{room_id}/images payload:', { roomId, body: applyBody });
      const applyResult = await applyImageToRoom(roomId, applyBody);
      const appliedDims = parseRoomImageDimensionsFromApi(applyResult);
      if (appliedDims) setRoomImageDimensions(appliedDims);
      // Backend creates a new room image when applying; use it so the next edit (material/remove) uses the updated image
      const newImageId =
        applyResult?.id ??
        applyResult?.latestRoomImageId;
      if (newImageId != null) {
        setRoomImageId(newImageId);
        // We already have polygons for this image (they didn't geometrically change),
        // so mark generate_mask as done to avoid re-running detection unnecessarily.
        aiInferenceDone.add(newImageId);
        // Copy interiors to the new room image id.
        //
        // IMPORTANT: do not use only the in-memory `polygons` list here — it may represent only the
        // currently visible subset (e.g. user chose "show vase only"), while the backend still has
        // all detections. Using only `polygons` would accidentally drop hidden objects on Apply.
        try {
          // `roomImageId` is still the parent id in this closure (setRoomImageId schedules an update).
          const parentImageId = roomImageId;
          let sourceList = await getInteriors(roomId, newImageId);
          if (!Array.isArray(sourceList) || sourceList.length === 0) {
            sourceList = await getInteriors(roomId, parentImageId);
          }
          if (!Array.isArray(sourceList) || sourceList.length === 0) {
            // Nothing authoritative to copy; fall back to local state if present.
            if (Array.isArray(polygons) && polygons.length > 0) {
              const polygonsForNewImage = polygons.filter((item) => {
                if (removeInteriorIds.size === 0) return true;
                const interiorId = item?.id ?? item?.interior_id;
                const numericId = Number(interiorId);
                return !Number.isFinite(numericId) || !removeInteriorIds.has(numericId);
              });
              const formattedForNewImage = polygonsForNewImage.map((item) => {
                const next = { ...item };
                if (
                  (next.interior_type_id == null && next.interiorTypeId == null) &&
                  next.label &&
                  interiorTypesList.length > 0
                ) {
                  const match = interiorTypesList.find(
                    (t) => String(t.interiorType).toLowerCase() === String(next.label).toLowerCase()
                  );
                  if (match) next.interior_type_id = match.id;
                }
                return itemToInteriorFormat(next);
              });
              if (formattedForNewImage.length > 0) {
                await saveInteriors(roomId, newImageId, formattedForNewImage);
                const savedForNew = await getInteriors(roomId, newImageId);
                if (Array.isArray(savedForNew) && savedForNew.length > 0) {
                  setPolygons(
                    polygonsForNewImage.map((p) => ({
                      ...p,
                      id: getInteriorIdFromSaved(p, savedForNew),
                      interior_id: p.interior_id ?? p.id ?? getInteriorIdFromSaved(p, savedForNew),
                    }))
                  );
                }
              }
            }
          } else {
            const filtered =
              opType === 'remove_object' && removeInteriorIds.size > 0
                ? sourceList.filter((item) => {
                    const interiorId = item?.id ?? item?.interior_id;
                    const numericId = Number(interiorId);
                    return !Number.isFinite(numericId) || !removeInteriorIds.has(numericId);
                  })
                : sourceList;
            const formattedForNewImage = filtered.map((item) => {
              const next = { ...item };
              if (
                (next.interior_type_id == null && next.interiorTypeId == null) &&
                next.label &&
                interiorTypesList.length > 0
              ) {
                const match = interiorTypesList.find(
                  (t) => String(t.interiorType).toLowerCase() === String(next.label).toLowerCase()
                );
                if (match) next.interior_type_id = match.id;
              }
              const base = itemToInteriorFormat(next);
              return next.id != null ? { ...base, id: next.id } : base;
            });
            if (formattedForNewImage.length > 0) {
              await saveInteriors(roomId, newImageId, formattedForNewImage);
              const savedForNew = await getInteriors(roomId, newImageId);
              if (Array.isArray(savedForNew) && savedForNew.length > 0) {
                let polygonsForDisplay = savedForNew;
                if (opType === 'remove_object' && removeInteriorIds.size > 0) {
                  const previousVisible = Array.isArray(polygonsBeforeApply) ? polygonsBeforeApply : [];
                  const previousVisibleKept = previousVisible.filter((p) => {
                    const id = Number(p?.id ?? p?.interior_id);
                    return !Number.isFinite(id) || !removeInteriorIds.has(id);
                  });
                  if (previousVisibleKept.length > 0) {
                    // Preserve the same visible subset after Apply. IDs can change after copy/save,
                    // so remap each row against the saved list using id first, then label/type/bbox.
                    polygonsForDisplay = previousVisibleKept.map((p) => {
                      const prevId = Number(p?.id ?? p?.interior_id);
                      let matched =
                        Number.isFinite(prevId)
                          ? savedForNew.find((s) => Number(s?.id ?? s?.interior_id) === prevId)
                          : null;
                      if (!matched) {
                        const prevType = Number(p?.interior_type_id ?? p?.interiorTypeId ?? -1);
                        const prevLabel = String(p?.label ?? '').trim().toLowerCase();
                        const prevBbox = Array.isArray(p?.bbox) ? p.bbox : null;
                        matched = savedForNew.find((s) => {
                          const sType = Number(s?.interior_type_id ?? s?.interiorTypeId ?? -2);
                          if (sType !== prevType) return false;
                          const sLabel = String(s?.label ?? '').trim().toLowerCase();
                          const labelMatch = prevLabel && sLabel === prevLabel;
                          const sBbox = Array.isArray(s?.bbox) ? s.bbox : null;
                          const bboxMatch =
                            Array.isArray(prevBbox) &&
                            Array.isArray(sBbox) &&
                            prevBbox.length === sBbox.length &&
                            prevBbox.every((v, i) => Number(v) === Number(sBbox[i]));
                          return labelMatch || bboxMatch;
                        });
                      }
                      return matched
                        ? {
                            ...p,
                            ...matched,
                            id: matched?.id ?? matched?.interior_id ?? p?.id ?? p?.interior_id,
                            interior_id: matched?.id ?? matched?.interior_id ?? p?.interior_id ?? p?.id,
                          }
                        : p;
                    });
                  } else {
                    polygonsForDisplay = [];
                  }
                }
                setPolygons(polygonsForDisplay);
                setDetectedObjectsFromAPI(savedForNew);
                setInteriorsFromExisting(true);
                setInteriorsSavedOnce(true);
              }
            }
          }
        } catch (copyErr) {
          console.error('Failed to copy interiors to new room image:', copyErr);
        }
      } else {
        const room = await getRoom(roomId);
        const latestId = room?.latestRoomImageId;
        if (latestId != null) {
          setRoomImageId(latestId);
          aiInferenceDone.add(latestId);
        }
      }
      // Immediately update the base canvas image and base URL to the applied result
      if (appliedImageElement) {
        setImage(appliedImageElement);
        if (appliedImageElement.src) {
          setPhotorealisticImage(appliedImageElement.src);
        }
      } else if (appliedPreviewUrl) {
        const img = new Image();
        // Ensure result image can be sampled by canvas for further SAM operations.
        img.crossOrigin = 'anonymous';
        img.onload = () => setImage(img);
        img.src = appliedPreviewUrl;
        setPhotorealisticImage(appliedPreviewUrl);
      }
      // After applying, show the Detected Objects panel again so user can continue working.
      setShowPolygonsOverlay(true);
      setShowHistoryPanel(false);
      setShowMaterialsPanel(false);
      if (removeInteriorIds.size > 0) {
        setSelectedPolygonIndex(null);
        setMultiSelectedPolygonIndices(new Set());
      }
      removeResultRefineOriginImageRef.current = null;
      removeResultRefineOriginUrlRef.current = null;
      removeResultRefineOriginPolygonsRef.current = null;
      // Keep an explicit confirmation when remove-object apply also updates saved detections.
      if (removeInteriorIds.size > 0) {
        setAiNotification({
          task: 'remove_object',
          status: 'Success',
          message: 'Removed object detections were updated.',
        });
      } else {
        // Clear AI notification after non-remove apply is completed.
        setAiNotification(null);
      }
      setPreview(null);
      setSwapped(false);
      setLastMaterialTransferResultKey(null);
      setResultImageElement(null);
      setShowMaterialsGridDuringAiResult(false);
      return { newImageId: newImageId ?? null };
    } catch (err) {
      console.error('Apply AI result image failed:', err);
      throw err;
    }
  }, [
    lastMaterialTransferResultKey,
    roomId,
    roomImageId,
    operationTypeForApply,
    setRoomImageId,
    setPhotorealisticImage,
    setRoomImageDimensions,
    polygons,
    interiorTypesList,
  ]);

  const handleExportPreviewSave = useCallback(async () => {
    if (exportPreviewSaveLoading || exportPreviewLoading || !exportPreviewResultKey) return;
    if (roomId == null || roomImageId == null) {
      setAiNotification({
        task: 'room_image_render',
        status: 'Failed',
        message: 'Missing room or active image id.',
      });
      return;
    }
    if (!exportPreviewSavedConfig) {
      setAiNotification({
        task: 'room_image_render',
        status: 'Failed',
        message: 'Missing render configuration for this preview.',
      });
      return;
    }

    setExportPreviewSaveLoading(true);
    try {
      setAiNotification({
        task: 'room_image_render',
        status: 'Pending',
        message: 'Saving render…',
      });

      const body = buildRenderHistoryRequestBody({
        resolutionId: exportPreviewSavedConfig.resolutionId,
        lightingId: exportPreviewSavedConfig.lightingId,
        cameraAngle: exportPreviewSavedConfig.cameraAngle,
        resultS3Key: exportPreviewResultKey,
      });

      await saveRenderHistory(roomId, roomImageId, body);
      await loadRenderHistory();

      setExportPreviewUrl('');
      setExportPreviewResultKey('');
      setExportPreviewGeneratedAt(null);
      setExportResultResolutionId(null);
      setExportPreviewSavedConfig(null);
      setAiNotification({
        task: 'room_image_render',
        status: 'Success',
        message: 'Render saved to history.',
      });
    } catch (err) {
      console.error('Failed to save room_image_render result:', err);
      setAiNotification({
        task: 'room_image_render',
        status: 'Failed',
        message: err?.message || 'Failed to save render.',
      });
    } finally {
      setExportPreviewSaveLoading(false);
    }
  }, [
    exportPreviewLoading,
    exportPreviewResultKey,
    exportPreviewSaveLoading,
    exportPreviewSavedConfig,
    loadRenderHistory,
    roomId,
    roomImageId,
  ]);

  const showPromptResultOnCanvas = useCallback(({ resultS3Key, imageUrl }) => {
    if (!resultS3Key || !imageUrl) return;
    const displayUrl = resolveRoomObjectImageSrcForDisplay(String(imageUrl));
    if (!displayUrl) return;

    promptPreviewResultKeyRef.current = resultS3Key;
    setOperationTypeForApply('photorealistic_image');
    setLastMaterialTransferResultKey(resultS3Key);
    setPreview(displayUrl);
    setSwapped(true);
    setResultImageLoadError(false);
    setResultImageElement(null);
    setShowMaterialsGridDuringAiResult(false);
    setShowCompareSlider(false);
    setCompareSliderPosition(50);

    const resultImg = new Image();
    resultImg.crossOrigin = 'anonymous';
    resultImg.onload = () => {
      setResultImageElement(resultImg);
      setResultImageLoadError(false);
    };
    resultImg.onerror = () => {
      setResultImageElement(null);
      setResultImageLoadError(true);
    };
    resultImg.src = displayUrl;
  }, []);

  const handlePromptResultCancel = useCallback((resultS3Key) => {
    if (resultS3Key == null || promptPreviewResultKeyRef.current !== resultS3Key) return;
    promptPreviewResultKeyRef.current = null;
    setSwapped(false);
    setPreview(null);
    setLastMaterialTransferResultKey(null);
    setResultImageElement(null);
    setResultImageLoadError(false);
    setShowCompareSlider(false);
    setCompareSliderPosition(50);
  }, []);

  const handlePromptChatSubmit = useCallback(
    async (promptText) => {
      if (roomImageId == null) {
        throw new Error('No active room image.');
      }
      const result = await submitPhotorealisticImagePrompt(roomImageId, promptText);
      if (result?.type === 'image_result') {
        showPromptResultOnCanvas(result);
      }
      return result;
    },
    [roomImageId, showPromptResultOnCanvas],
  );

  const handlePromptResultApply = useCallback(
    async ({ resultS3Key, imageUrl }) => {
      if (!resultS3Key || !imageUrl) {
        throw new Error('Missing result image data.');
      }

      setPolygonsLoading(true);
      setWorkspaceOverlayMessage('Preparing the image');
      promptApplyMaskInFlightRef.current = true;

      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Could not load result image.'));
        img.src = imageUrl;
      });

      try {
        setOperationTypeForApply('photorealistic_image');
        const applyOutcome = await handleMaterialResultApply({
          resultS3Key,
          operationType: 'photorealistic_image',
          previewUrl: imageUrl,
          imageElement: img,
        });
        const targetImageId = applyOutcome?.newImageId ?? roomImageId;
        if (targetImageId == null || roomId == null) {
          throw new Error('Could not determine the applied room image.');
        }

        aiInferenceDone.delete(targetImageId);
        const scannedList = await runGenerateMaskForRoomImage(targetImageId);
        aiInferenceDone.add(targetImageId);

        if (Array.isArray(scannedList) && scannedList.length > 0) {
          const existingOnNew = await getInteriors(roomId, targetImageId);
          if (Array.isArray(existingOnNew) && existingOnNew.length > 0) {
            const idsToDelete = existingOnNew
              .map((item) => Number(item?.id ?? item?.interior_id))
              .filter((id) => Number.isFinite(id));
            if (idsToDelete.length > 0) {
              await Promise.allSettled(
                idsToDelete.map((id) => deleteInterior(roomId, targetImageId, id)),
              );
            }
          }

          const formattedForNewImage = scannedList.map((item) => {
            const next = { ...item };
            if (
              (next.interior_type_id == null && next.interiorTypeId == null) &&
              next.label &&
              interiorTypesList.length > 0
            ) {
              const match = interiorTypesList.find(
                (t) => String(t.interiorType).toLowerCase() === String(next.label).toLowerCase(),
              );
              if (match) next.interior_type_id = match.id;
            }
            return itemToInteriorFormat(next);
          });
          await saveInteriors(roomId, targetImageId, formattedForNewImage);
          const savedForNew = await getInteriors(roomId, targetImageId);
          const displayList =
            Array.isArray(savedForNew) && savedForNew.length > 0 ? savedForNew : scannedList;

          setDetectedObjectsFromAPI(displayList);
          setPolygons(displayList);
          setPendingDetectedObjects(null);
          setInteriorsFromExisting(false);
          setInteriorsSavedOnce(true);
          setScanNoDetections(false);
          setShowAllPolygonsOnCanvas(true);
        } else {
          setDetectedObjectsFromAPI([]);
          setPolygons([]);
          setPendingDetectedObjects(null);
          setScanNoDetections(true);
        }

        setShowPolygonsOverlay(true);
        setShowHistoryPanel(false);
        setShowModelStudioPanel(false);
        setShowRenderPanel(false);
        setShowExportImagePanel(false);
      setShowExportImagePanel(false);
        setShowMaterialsPanel(false);
        setRightPanelCollapsed(false);
        promptPreviewResultKeyRef.current = null;
      } catch (err) {
        console.error('Prompt result apply failed:', err);
        setAiNotification({
          task: 'photorealistic_image',
          status: 'Failed',
          message: err?.message || 'Failed to apply prompt result.',
        });
        throw err;
      } finally {
        promptApplyMaskInFlightRef.current = false;
        setPolygonsLoading(false);
        setWorkspaceOverlayMessage(null);
      }
    },
    [
      roomId,
      roomImageId,
      handleMaterialResultApply,
      interiorTypesList,
      setShowPolygonsOverlay,
      setShowHistoryPanel,
      setShowModelStudioPanel,
      setShowMaterialsPanel,
    ],
  );

  const hasActiveToolPanel =
    showModelStudioPanel ||
    showHistoryPanel ||
    showPolygonsOverlay ||
    showPromptPanel ||
    showRenderPanel ||
    showFlythroughPanel;
  const toolPanelExpanded = hasActiveToolPanel && !rightPanelCollapsed;

  return (
    <div className="w-full min-w-0 h-full flex flex-col">

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Canvas + button panel in one border */}
        <div className="flex-1 flex min-w-0">
          {/* Main content: image area */}
          <div className="flex-1 flex flex-row min-w-0 relative">
            <div
              className="pointer-events-none absolute bottom-2 right-[6.75rem] z-20 rounded-md border border-gray-200 bg-white/95 px-2 py-1 text-right shadow-sm hidden"
              role="status"
              aria-live="polite"
            >
              <div className="text-[10px] text-gray-700">
                {autoSaveStatus === 'saving' && 'Saving...'}
                {autoSaveStatus === 'saved' && 'Auto-saved'}
                {autoSaveStatus === 'recovered' && 'Recovered'}
                {autoSaveStatus === 'synced' && 'Synced'}
                {autoSaveStatus === 'error' && 'Save failed'}
                {(autoSaveStatus === 'idle' || !autoSaveStatus) && 'Draft idle'}
              </div>
              {lastSavedAgoText && (
                <div className="text-[9px] text-gray-500">{lastSavedAgoText}</div>
              )}
            </div>

          {/* Button panel: actions (Show/Hide Objects, History, Back at bottom) - right of canvas */}
          <PhotorealisticCanvasToolAside
            swapped={swapped}
            image={image}
            showPolygonsOverlay={showPolygonsOverlay}
            showHistoryPanel={showHistoryPanel}
            showModelStudioPanel={showModelStudioPanel}
            showPromptPanel={showPromptPanel}
            showRenderPanel={showRenderPanel}
            showFlythroughPanel={showFlythroughPanel}
            imageSelectionActive={uploadModelMaskResults.length > 0 || uploadModelMaskFlowActive}
            onToggleObjectsClick={() => {
              if (!showPolygonsOverlay) setRightPanelCollapsed(false);
              setShowPolygonsOverlay((v) => !v);
              setEditingLayerIndex(null);
              resetEditorState();
              setSelectedPolygonIndex(null);
              if (!showPolygonsOverlay) {
                setShowHistoryPanel(false);
                setShowModelStudioPanel(false);
                setShowPromptPanel(false);
                setShowRenderPanel(false);
              setShowExportImagePanel(false);
                setShowFlythroughPanel(false);
                setShowExportImagePanel(false);
        setShowExportImagePanel(false);
      setShowExportImagePanel(false);
                setShowTransformModelsPanel(false);
                setShowMaterialsPanel(false);
              }
            }}
            onToggleHistoryClick={() => {
              if (!showHistoryPanel) setRightPanelCollapsed(false);
              setShowHistoryPanel((v) => !v);
              setShowModelStudioPanel(false);
              setShowPromptPanel(false);
              setShowRenderPanel(false);
              setShowExportImagePanel(false);
              setShowFlythroughPanel(false);
        setShowExportImagePanel(false);
      setShowExportImagePanel(false);
              setShowTransformModelsPanel(false);
              setShowMaterialsPanel(false);
              if (!showHistoryPanel) {
                setShowPolygonsOverlay(false);
                setSelectedPolygonIndex(null);
                setShowAllPolygonsOnCanvas(false);
              }
            }}
            onToggleModelStudioClick={() => {
              if (!showModelStudioPanel) setRightPanelCollapsed(false);
              setShowModelStudioPanel((v) => !v);
              setShowHistoryPanel(false);
              setShowPromptPanel(false);
              setShowRenderPanel(false);
              setShowExportImagePanel(false);
              setShowFlythroughPanel(false);
        setShowExportImagePanel(false);
      setShowExportImagePanel(false);
              setShowPolygonsOverlay(false);
              setShowTransformModelsPanel(false);
              setShowMaterialsPanel(false);
              setSelectedPolygonIndex(null);
              setShowAllPolygonsOnCanvas(false);
            }}
            onTogglePromptClick={() => {
              if (!showPromptPanel) setRightPanelCollapsed(false);
              setShowPromptPanel((v) => !v);
              setShowHistoryPanel(false);
              setShowModelStudioPanel(false);
              setShowRenderPanel(false);
              setShowExportImagePanel(false);
              setShowFlythroughPanel(false);
        setShowExportImagePanel(false);
      setShowExportImagePanel(false);
              setShowPolygonsOverlay(false);
              setShowTransformModelsPanel(false);
              setShowMaterialsPanel(false);
              setSelectedPolygonIndex(null);
              setShowAllPolygonsOnCanvas(false);
            }}
            onToggleRenderClick={() => {
              const nextOpen = !showRenderPanel;
              if (nextOpen) setRightPanelCollapsed(false);
              setShowRenderPanel(nextOpen);
              setShowExportImagePanel(nextOpen);
              setShowFlythroughPanel(false);
              if (nextOpen) {
                setExportImagePanelCollapsed(false);
              }
              if (!nextOpen) {
                setExportPreviewUrl('');
                setExportPreviewLoading(false);
                setExportRenderLoading(false);
              }
              setShowHistoryPanel(false);
              setShowModelStudioPanel(false);
              setShowPromptPanel(false);
              setShowPolygonsOverlay(false);
              setShowTransformModelsPanel(false);
              setShowMaterialsPanel(false);
              setSelectedPolygonIndex(null);
              setShowAllPolygonsOnCanvas(false);
            }}
            onToggleFlythroughClick={() => {
              const nextOpen = !showFlythroughPanel;
              if (nextOpen) {
                setRightPanelCollapsed(false);
                setFlythroughExportVideoPanelCollapsed(false);
              }
              setShowFlythroughPanel(nextOpen);
              setShowHistoryPanel(false);
              setShowModelStudioPanel(false);
              setShowPromptPanel(false);
              setShowRenderPanel(false);
              setShowExportImagePanel(false);
              setShowPolygonsOverlay(false);
              setShowTransformModelsPanel(false);
              setShowMaterialsPanel(false);
              setSelectedPolygonIndex(null);
              setShowAllPolygonsOnCanvas(false);
            }}
            onBackToDashboard={() => {
              setPhotorealisticImage(null);
              setRoomImageDimensions(null);
              setCurrentView('Dashboard');
            }}
          />

        {/* Right side panel: Model Studio, History, Objects, Prompt, or Render */}
        <div className="flex shrink-0 min-h-0 items-stretch">
          <aside
            className={`shrink-0 min-h-0 overflow-hidden transition-[width] duration-300 ease-in-out motion-reduce:transition-none ${
              toolPanelExpanded ? 'w-[16rem]' : 'w-0 pointer-events-none'
            }`}
          >
            <div className="flex h-full min-h-0 w-[16] min-w-[16rem] flex-col gap-4 border-l border-gray-200 border-white/50 bg-white/35 py-4 px-2 box-border backdrop-blur-xl backdrop-saturate-150">
          {
            showModelStudioPanel ? (
              <ModelStudioPanel
                roomId={roomId}
                curatedLibraryModels={studioCuratedLibraryItems}
                onCuratedLibraryModelsChange={handleCuratedLibraryModelsChange}
                onRemoveCuratedModel={handleRemoveCuratedModel}
                canvasPlacements={placedCuratedModels}
                selectedPlacementId={selectedPlacedCuratedId}
                onSelectPlacement={setSelectedPlacedCuratedId}
                onReorderPlacements={handleReorderPlacedCurated}
                curatedDragEnabled={canPlaceCuratedOnCanvas}
                curatedDragPreviewWidthPx={curatedDragPreviewWidthPx}
                onOpenLibrary={openModelLibraryLibrary}
                onOpenQuickAdd={openModelLibraryUpload}
                onOpenTransformModelsPanel={handleOpenTransformModelsPanel}
              />
            ) : showHistoryPanel ? (
              <RoomHistoryPanel
                historyLoading={historyLoading}
                historyError={historyError}
                historyImages={historyImages}
                activeImageId={roomImageId}
                previewImageId={historyPreviewImageId}
                onPreviewHistoryItem={handleHistoryPreviewItem}
                onRestoreHistoryItem={handleHistoryRestoreAsActiveBase}
              />
            ) : showPolygonsOverlay ? (
              <DetectedObjectsPanel
                swapped={swapped}
                image={image}
                addObjectActionsVisible={addObjectActionsVisible}
                setAddObjectActionsVisible={setAddObjectActionsVisible}
                removeObjectMode={removeObjectMode}
                setRemoveObjectMode={setRemoveObjectMode}
                setRemoveObjectActionsVisible={setRemoveObjectActionsVisible}
                handleOpenSelectObjectsPopup={handleOpenSelectObjectsPopup}
                showAllPolygonsOnCanvas={showAllPolygonsOnCanvas}
                setShowAllPolygonsOnCanvas={setShowAllPolygonsOnCanvas}
                selectedPolygonIndex={selectedPolygonIndex}
                detectedObjectMultiSelectActive={detectedObjectMultiSelectActive}
                multiSelectedPolygonIndices={multiSelectedPolygonIndices}
                setAiNotification={setAiNotification}
                setShowPolygonsOverlay={setShowPolygonsOverlay}
                setShowHistoryPanel={setShowHistoryPanel}
                setShowMaterialsPanel={setShowMaterialsPanel}
                removeObjectActionsVisible={removeObjectActionsVisible}
                removeObjectLoading={removeObjectLoading}
                handleRemoveObjectClick={handleRemoveObjectClick}
                polygons={polygons}
                detectedObjectsFromAPI={detectedObjectsFromAPI}
                handleStartAddObjectFlow={handleStartAddObjectFlow}
                setPendingDetectedObjects={setPendingDetectedObjects}
                setObjectChoiceStep={setObjectChoiceStep}
                editingLayerIndex={editingLayerIndex}
                isEditorToolbarActive={isEditorToolbarActive}
                setEditingLayerIndex={setEditingLayerIndex}
                handleEditorConfirm={handleEditorConfirm}
                handleCancelAddObjectActions={handleCancelAddObjectActions}
                setDetectedObjectMultiSelectActive={setDetectedObjectMultiSelectActive}
                setMultiSelectedPolygonIndices={setMultiSelectedPolygonIndices}
                interiorTypesList={interiorTypesList}
                pendingDetectedObjects={pendingDetectedObjects}
                polygonsLoading={polygonsLoading}
                expandedGroupLabel={expandedGroupLabel}
                setExpandedGroupLabel={setExpandedGroupLabel}
                objectRenameIndex={objectRenameIndex}
                addingNewObjectFromPopup={addingNewObjectFromPopup}
                objectChoiceStep={objectChoiceStep}
                handleDetectedObjectRowClick={handleDetectedObjectRowClick}
                resetEditorState={resetEditorState}
                polygonAtEditStartRef={polygonAtEditStartRef}
                objectListOverflowMenuIndex={objectListOverflowMenuIndex}
                setObjectListOverflowMenuIndex={setObjectListOverflowMenuIndex}
                setPolygonBottomActionsOpen={setPolygonBottomActionsOpen}
                removeObjectRefinePending={removeObjectRefinePending}
                setObjectBeingLabeled={setObjectBeingLabeled}
                setObjectRenameIndex={setObjectRenameIndex}
                setNewObjectLabel={setNewObjectLabel}
                setNewObjectLabelDropdownOpen={setNewObjectLabelDropdownOpen}
                roomId={roomId}
                roomImageId={roomImageId}
                setError={setError}
                setPolygons={setPolygons}
                remapMultiSelectedIndicesAfterRemove={remapMultiSelectedIndicesAfterRemove}
                setSelectedPolygonIndex={setSelectedPolygonIndex}
                setDeletedObjectUndo={setDeletedObjectUndo}
                deletedObjectUndo={deletedObjectUndo}
                handleUndoDeletedObject={handleUndoDeletedObject}
                error={error}
                aiNotification={aiNotification}
                isImageSelectionActive={uploadModelMaskResults.length > 0}
                removeObjectResultActionsVisible={
                  swapped &&
                  lastMaterialTransferResultKey &&
                  operationTypeForApply === 'remove_object'
                }
                removeResultActionsDisabled={
                  materialTransferLoading ||
                  removeObjectLoading ||
                  changeObjectAngleLoading ||
                  isEditorToolbarActive
                }
                handleRemoveResultRegenerate={handleRegenerateAiResult}
                handleRemoveResultRefine={handleRemoveObjectResultRefine}
                handleRemoveResultApply={() => void handleMaterialResultApply()}
                handleRemoveResultDownload={() => void handleMaterialResultDownload()}
                handleRemoveResultCancel={handleMaterialResultCancel}
                handleScanFromPopup={handleScanFromPopup}
                isGenerateMaskTaskActive={isGenerateMaskTaskActive}
              />
            ) : showPromptPanel ? (
              <PromptPanel
                prompt={prompt}
                onPromptChange={setPrompt}
                onSubmitPrompt={handlePromptChatSubmit}
                onApplyPromptResult={handlePromptResultApply}
                onCancelPromptResult={handlePromptResultCancel}
                canSubmit={roomImageId != null}
                onGenerate={handleExecute}
                loading={loading}
                hasImage={Boolean(image)}
                executionTime={executionTime}
              />
            ) : showRenderPanel ? (
              <RenderPanel
                renderResolution={renderResolution}
                onRenderResolutionChange={setRenderResolution}
                lightingPreset={renderLightingPreset}
                onLightingPresetChange={setRenderLightingPreset}
                onCameraChange={handleRenderCameraChange}
                libraryItems={renderLibraryHistory}
                libraryLoading={renderLibraryLoading}
                libraryError={renderLibraryError}
                onLibraryItemSelect={handleSavedRenderSelect}
                onLibraryItemDelete={handleRenderDeleteRequest}
                deletingLibraryItemId={deletingRenderHistoryId}
                onLibraryTabActive={refreshRenderLibraryImageUrls}
              />
            ) : showFlythroughPanel ? (
              <FlythroughVideoPanel
                libraryItems={renderLibraryHistory}
                libraryLoading={renderLibraryLoading}
                libraryError={renderLibraryError}
                onLoadLibrary={loadRenderHistory}
                roiMode={flythroughRoiMode}
                onRoiModeChange={handleFlythroughRoiModeChange}
                roi={flythroughRoi}
                onRoiChange={handleFlythroughRoiChange}
                cameraPreset={flythroughCameraPreset}
                onCameraPresetChange={setFlythroughCameraPreset}
                motionMode={flythroughMotionMode}
                onMotionModeChange={handleFlythroughMotionModeChange}
                cameraAngleSequence={flythroughCameraAngleSequence}
                onCameraAngleSequenceChange={handleFlythroughCameraAngleSequenceChange}
                draftCameraAngle={flythroughDraftCameraAngle}
                onDraftCameraAngleChange={setFlythroughDraftCameraAngle}
                motionPromptFields={flythroughMotionPromptFields}
                onMotionPromptFieldChange={handleFlythroughMotionPromptFieldChange}
                durationSeconds={flythroughDurationSeconds}
                onDurationSecondsChange={setFlythroughDurationSeconds}
                renderResolution={flythroughRenderResolution}
                onRenderResolutionChange={setFlythroughRenderResolution}
                frameRate={flythroughFrameRate}
                onFrameRateChange={setFlythroughFrameRate}
                selectedRenderIds={flythroughSelectedRenderIds}
                onSelectedRenderIdsChange={setFlythroughSelectedRenderIds}
                libraryFlythroughs={flythroughLibraryHistory}
                libraryFlythroughsLoading={flythroughLibraryLoading}
                libraryFlythroughsError={flythroughLibraryError}
                onLoadFlythroughLibrary={loadFlythroughHistory}
                onLibraryFlythroughSelect={handleFlythroughVideoSelect}
                onLibraryFlythroughDownload={handleLibraryFlythroughDownload}
                onLibraryFlythroughDelete={handleFlythroughDeleteRequest}
                deletingLibraryFlythroughId={deletingFlythroughId}
                downloadingLibraryFlythroughId={downloadingFlythroughId}
              />
            ) : null}
            </div>
          </aside>
          {hasActiveToolPanel ? (
          <button
            type="button"
            onClick={() => setRightPanelCollapsed((v) => !v)}
            className="flex w-6 h-12 shrink-0 flex-col items-center justify-center self-center rounded-r-lg transition-colors bg-white/35 box-border backdrop-blur-xl backdrop-saturate-150"
            title={rightPanelCollapsed ? 'Show side panel' : 'Hide side panel'}
            aria-expanded={!rightPanelCollapsed}
            aria-label={rightPanelCollapsed ? 'Show side panel' : 'Hide side panel'}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`shrink-0 transition-transform duration-300 ease-out ${rightPanelCollapsed ? 'rotate-180' : ''}`}
              aria-hidden
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          ) : null}
        </div>

            <div className="flex-1 flex flex-col min-h-0 relative">
              <div
                onContextMenu={(e) => e.preventDefault()}
                className={`flex-1 min-h-[200px] flex items-center justify-center relative ${
                  showCompareSlider ||
                  (editingLayerIndex != null &&
                    (editorState.zoom !== 1 || editorState.pan.x !== 0 || editorState.pan.y !== 0))
                    ? 'overflow-hidden'
                    : 'overflow-auto'
                }`}
              >
              {image || preview ? (
                swapped ? (
                  showCompareSlider && preview && image?.src ? (
                    <div
                      ref={compareCanvasRef}
                      className="relative mx-auto block w-fit min-h-0 min-w-0 max-h-full max-w-full cursor-ew-resize select-none touch-none overflow-hidden"
                      style={
                        compareViewLayoutSize.cw > 0 && compareViewLayoutSize.ch > 0
                          ? {
                              width: compareViewLayoutSize.cw,
                              height: compareViewLayoutSize.ch,
                            }
                          : undefined
                      }
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setIsCompareDragging(true);
                        updateCompareSliderFromClientX(e.clientX);
                        const onMove = (moveEvent) => {
                          updateCompareSliderFromClientX(moveEvent.clientX);
                        };
                        const onUp = () => {
                          setIsCompareDragging(false);
                          window.removeEventListener('mousemove', onMove);
                          window.removeEventListener('mouseup', onUp);
                        };
                        window.addEventListener('mousemove', onMove);
                        window.addEventListener('mouseup', onUp);
                      }}
                      onTouchStart={(e) => {
                        const touch = e.touches?.[0];
                        if (!touch) return;
                        setIsCompareDragging(true);
                        updateCompareSliderFromClientX(touch.clientX);
                      }}
                      onTouchMove={(e) => {
                        const touch = e.touches?.[0];
                        if (!touch) return;
                        updateCompareSliderFromClientX(touch.clientX);
                      }}
                      onTouchEnd={() => setIsCompareDragging(false)}
                      onTouchCancel={() => setIsCompareDragging(false)}
                    >
                      <div
                        className="pointer-events-none absolute rounded overflow-hidden"
                        style={{
                          left: compareFitRect.rw > 0 ? compareFitRect.ox : 0,
                          top: compareFitRect.rh > 0 ? compareFitRect.oy : 0,
                          width:
                            compareFitRect.rw > 0 ? compareFitRect.rw : '100%',
                          height:
                            compareFitRect.rh > 0 ? compareFitRect.rh : '100%',
                        }}
                      >
                        <img
                          ref={compareBaseImageRef}
                          src={image.src}
                          alt="Before"
                          className="pointer-events-auto absolute inset-0 h-full w-full object-contain rounded"
                          draggable={false}
                          crossOrigin="anonymous"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            setBasedImageLoadError(true);
                            setShowCompareSlider(false);
                          }}
                        />
                        <img
                          src={preview}
                          alt="After"
                          className="pointer-events-auto absolute inset-0 h-full w-full rounded object-contain"
                          draggable={false}
                          crossOrigin="anonymous"
                          style={{
                            clipPath: `inset(0 0 0 ${compareSliderPosition}%)`,
                          }}
                          onLoad={() => setResultImageLoadError(false)}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            setResultImageLoadError(true);
                            setShowCompareSlider(false);
                          }}
                        />
                        <div
                          className="absolute w-0.5 bg-white shadow pointer-events-none"
                          style={{
                            top: 0,
                            left: `calc(${compareSliderPosition}% - 1px)`,
                            height: '100%',
                          }}
                        />
                        <div
                          className="absolute h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white bg-black/50 text-white flex items-center justify-center pointer-events-none"
                          style={{
                            top: '50%',
                            left: `${compareSliderPosition}%`,
                          }}
                          aria-hidden
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M8 6L2 12l6 6" />
                            <path d="M16 6l6 6-6 6" />
                          </svg>
                        </div>
                        {compareSliderPosition <= 50 ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCompareSliderPosition(0);
                            }}
                            className="pointer-events-auto absolute top-2 left-2 inline-flex items-center gap-1 rounded-full border border-white bg-white px-2 py-1 text-[11px] text-gray-900 transition-colors"
                            title="Show more Before"
                            aria-label="Show more Before"
                          >
                            <span className="h-1.5 w-1.5 rounded-full bg-current" />
                            Before
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCompareSliderPosition(100);
                            }}
                            className="pointer-events-auto absolute top-2 right-2 inline-flex items-center gap-1 rounded-full border border-white bg-white px-2 py-1 text-[11px] text-gray-900 transition-colors"
                            title="Show more After"
                            aria-label="Show more After"
                          >
                            After
                            <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          </button>
                        )}
                      </div>
                    </div>
                  ) : resultImageLoadError ? (
                    <div className="flex flex-col items-center justify-center text-gray-500 p-8 text-center max-w-sm">
                      <svg className="w-14 h-14 mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14" />
                      </svg>
                      <p className="text-sm font-medium">Result image could not be loaded</p>
                      <p className="text-xs mt-1 text-gray-400">You can still use Cancel to return to the previous image.</p>
                    </div>
                  ) : resultImageElement ? (
                    <ImageWithPolygons
                      image={resultImageElement}
                      referenceImagePixelSize={roomImageDimensions}
                      polygons={null}
                      isLoading={false}
                      editingLayerIndex={null}
                      selectedPolygonIndex={null}
                      showAllPolygonsOnCanvas={false}
                      onPolygonClick={undefined}
                      onPolygonBrushClick={undefined}
                      activeTool={null}
                      brushSize={editorState.brushSize}
                      zoom={editorState.zoom}
                      pan={editorState.pan}
                      strokes={[]}
                      onStrokesChange={() => {}}
                      getPolygonLabel={(item, index) => getLabelForInterior(item, interiorTypesList, index)}
                    />
                  ) : (
                    <img
                      src={preview}
                      alt="Photorealistic result"
                  className="max-h-full max-w-full object-contain rounded"
                  draggable={false}
                      crossOrigin="anonymous"
                      onLoad={() => setResultImageLoadError(false)}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        setResultImageLoadError(true);
                      }}
                  onContextMenu={(e) => e.preventDefault()}
                />
                  )
                ) : (
                  <div
                    ref={mainCanvasContainerRef}
                    className={`relative mx-auto block w-fit max-h-full max-w-full min-h-0 min-w-0 ${
                      canvasCuratedDropActive ? 'ring-2 ring-primary-300 ring-inset rounded' : ''
                    }`}
                    onDragOver={handleCanvasCuratedDragOver}
                    onDragLeave={handleCanvasCuratedDragLeave}
                    onDrop={handleCanvasCuratedDrop}
                    onPointerDown={handleCanvasContainerPointerDown}
                  >
                    <ImageWithPolygons
                      image={activeCanvasImage}
                      referenceImagePixelSize={canvasReferenceImagePixelSize}
                      polygons={showPolygonsOverlay ? polygons : null}
                      isLoading={false}
                      editingLayerIndex={editingLayerIndex}
                      selectedPolygonIndex={selectedPolygonIndex}
                      showAllPolygonsOnCanvas={showAllPolygonsOnCanvas}
                      visiblePolygonIndices={visiblePolygonIndicesForCanvas}
                      polygonOverlayOpacity={polygonOverlayOpacity}
                      onPolygonClick={(index) => handleDetectedObjectRowClick(index, polygons)}
                      onPolygonBrushClick={
                        !swapped && !removeObjectMode
                          ? (index, anchor) => {
                              handleCloseMaterialsPanel();
                              activateDetectedObjectRow(index, polygons);
                              setPolygonBottomActionsAnchor(
                                anchor &&
                                  typeof anchor.leftPct === 'number' &&
                                  typeof anchor.topPct === 'number'
                                  ? { leftPct: anchor.leftPct, topPct: anchor.topPct }
                                  : null
                              );
                              setPolygonBottomActionsOpen(true);
                            }
                          : undefined
                      }
                      onPolygonRemoveClick={!swapped && removeObjectMode ? handleRemoveObjectClick : undefined}
                      polygonActionType={removeObjectMode ? 'remove' : 'brush'}
                      activeTool={editorState.activeTool}
                      magicMode={editorState.magicMode}
                      onMagicClick={handleMagicWandClick}
                      clickPoints={editorState.clickPoints}
                      brushSize={editorState.brushSize}
                      zoom={editorState.zoom}
                      pan={editorState.pan}
                      strokes={editorState.strokes}
                      onStrokesChange={handleStrokesChange}
                      onEditingWheel={handleEditorCanvasWheel}
                      getPolygonLabel={(item, index) => getLabelForInterior(item, interiorTypesList, index)}
                      showBottomActions={
                        hasActiveCanvasPolygons &&
                        !isEditorToolbarActive &&
                        selectedPolygonIndex != null &&
                        !showMaterialsPanel &&
                        objectChoiceStep !== 'editNewObjectLabel' &&
                        polygonBottomActionsOpen
                      }
                      bottomActionsAnchor={polygonBottomActionsAnchor}
                      onPolygonOverlayOpacityChange={setPolygonOverlayOpacity}
                      onApplyFinish={() => showMaterialsForInterior()}
                      onRefine={() => {
                        const i = selectedPolygonIndex;
                        const item = polygons?.[i];
                        if (!item) return;
                        polygonAtEditStartRef.current = { ...item };
                        setEditingLayerIndex(i);
                        resetEditorState();
                      }}
                      onRenameObject={() => {
                        const i = selectedPolygonIndex;
                        const item = polygons?.[i];
                        if (i == null || !item) return;
                        if (editingLayerIndex != null) {
                          setEditingLayerIndex(null);
                          resetEditorState();
                        }
                        setObjectBeingLabeled(item);
                        setObjectRenameIndex(i);
                        setNewObjectLabel(getLabelForInterior(item, interiorTypesList, i) || '');
                        setNewObjectLabelDropdownOpen(false);
                        setObjectChoiceStep('editNewObjectLabel');
                      }}
                      onRemoveFromList={() => {
                        const i = selectedPolygonIndex;
                        if (i == null) return;
                        setRemoveObjectMode(true);
                        setRemoveObjectActionsVisible(true);
                        setShowPolygonsOverlay(true);
                        setShowHistoryPanel(false);
                        setShowMaterialsPanel(false);
                        setPolygonBottomActionsOpen(false);
                        void handleRemoveObjectClick(i);
                      }}
                      onCloseBottomActions={
                        detectedObjectMultiSelectActive
                          ? () => setPolygonBottomActionsOpen(false)
                          : handleCancelPolygonSelection
                      }
                      isMultiSelectActive={detectedObjectMultiSelectActive}
                      hideSelectionActionButtons={uploadModelMaskResults.length > 0}
                      hideEditingPolygonOverlay={removeObjectRefinePending}
                    />
                    {showPlacedCuratedModelsOverlay ? (
                      <PlacedCuratedModelsOverlay
                        containerRef={mainCanvasContainerRef}
                        logicalWidth={canvasLogicalSize.width}
                        logicalHeight={canvasLogicalSize.height}
                        placements={curatedPlacementsForOverlay}
                        selectedPlacementId={selectedPlacedCuratedId}
                        onSelectPlacement={setSelectedPlacedCuratedId}
                        onMovePlacement={handleMovePlacedCurated}
                        onResizePlacement={handleResizePlacedCurated}
                        onRemovePlacement={handleRemovePlacedCurated}
                        onOpenTransformModelsPanel={handleOpenTransformModelsPanel}
                        disabled={!canPlaceCuratedOnCanvas}
                        maxPlacementWidth={canvasLogicalSize.width * 0.6}
                      />
                    ) : null}
                    <FlythroughRoiCropOverlay
                      visible={
                        showFlythroughPanel &&
                        flythroughRoiMode === 'custom' &&
                        Boolean(image) &&
                        !swapped
                      }
                      containerRef={mainCanvasContainerRef}
                      logicalWidth={canvasLogicalSize.width}
                      logicalHeight={canvasLogicalSize.height}
                      roi={flythroughRoi}
                      onRoiChange={handleFlythroughRoiChange}
                    />
                  </div>
                )
              ) : roomId != null && imageRestoreLoading ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-gray-500">
                  <svg
                    className="animate-spin h-10 w-10 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm font-medium">Loading image...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 text-gray-400 text-sm">
                  <p>No image found for this room.</p>
                  {error && (
                    <p className="text-xs text-red-500 text-center max-w-xs">{error}</p>
                  )}
                  {noImageUploadError && (
                    <p className="text-xs text-red-500">{noImageUploadError}</p>
                  )}
                  <div className="flex flex-col items-center gap-2">
                    {roomId != null && (
                      <button
                        type="button"
                        disabled={imageRestoreLoading}
                        onClick={() => {
                          setImage(null);
                          setPhotorealisticImage(null);
                          setRoomImageDimensions(null);
                          setError(null);
                          setImageRestoreRetryKey((k) => k + 1);
                        }}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-100 disabled:opacity-60"
                      >
                        {imageRestoreLoading ? 'Reloading…' : 'Reload room image'}
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={noImageUploadLoading}
                      onClick={() => noImageFileInputRef.current?.click()}
                      className="px-4 py-2 rounded-lg bg-gray-800 text-white text-xs font-medium hover:bg-gray-700 disabled:opacity-60"
                    >
                      {noImageUploadLoading ? 'Uploading…' : 'Upload image'}
                    </button>
                    <input
                      ref={noImageFileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleNoImageFileSelect(file);
                          e.target.value = '';
                        }
                      }}
                    />
                    <p className="text-[11px] text-gray-400">
                      Supported: JPG, PNG, PDF · Max 8MB
                    </p>
                  </div>
                </div>
              )}
              <div className="absolute bottom-3 left-0 z-10 max-w-[12rem]">
                <PhotorealisticHelpfulTipsPanel
                  context={{
                    hasActivePolygonOnCanvas: hasActiveCanvasPolygons,
                    isMagicWandActive: editorState.activeTool === 'magicwand',
                    isEraserActive: editorState.activeTool === 'eraser',
                    isBrushActive:
                      editorState.activeTool === 'brush' && hasExplicitBrushToolSelection,
                    isMoveToolActive:
                      editingLayerIndex != null && editorState.activeTool === 'move',
                    hasTriggeredZoomToolbarTips:
                      editingLayerIndex != null && hasTriggeredZoomToolbarTips,
                    zoomToolbarTipsEpoch,
                    panToolbarTipsEpoch,
                    showCuratedSavePromptTip,
                    curatedSavePromptTipEpoch,
                  }}
                />
              </div>
              <div className="pointer-events-none absolute bottom-3 right-3 z-20">
                <div className="pointer-events-auto flex flex-col gap-2 hidden">
                  {swapped &&
                    lastMaterialTransferResultKey &&
                    !pendingMaterialSelection &&
                    !(showMaterialsPanel && !materialsPanelCollapsed) && (
                    <>
                      {!(
                        operationTypeForApply === 'material_transfer' &&
                        showMaterialsGridDuringAiResult
                      ) && (
                        <>
                      <button
                        type="button"
                        onClick={handleMaterialResultApply}
                        className="px-3 py-2 rounded-lg bg-gray-800 text-sm font-medium text-white hover:bg-gray-700 shadow-sm"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Apply
                      </button>
                      <button
                        type="button"
                        onClick={handleRegenerateAiResult}
                        disabled={
                          materialTransferLoading || removeObjectLoading || changeObjectAngleLoading
                        }
                        className="px-3 py-2 rounded-lg bg-gray-800 text-sm font-medium text-white hover:bg-gray-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                          <path d="M3 3v5h5" />
                          <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                          <path d="M16 16h5v5" />
                        </svg>
                        Regenerate
                      </button>
                        </>
                      )}
                      {operationTypeForApply === 'material_transfer' && (
                        <button
                          type="button"
                          onClick={
                            showMaterialsGridDuringAiResult
                              ? () => {
                                  setShowMaterialsGridDuringAiResult(false);
                                  setMaterialsSearchQuery('');
                                }
                              : handleChangeMaterialFromResult
                          }
                          disabled={materialTransferLoading || removeObjectLoading}
                          className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gray-800 text-sm font-medium text-white hover:bg-gray-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <rect x="3" y="3" width="7" height="7" />
                            <rect x="14" y="3" width="7" height="7" />
                            <rect x="14" y="14" width="7" height="7" />
                            <rect x="3" y="14" width="7" height="7" />
                          </svg>
                          {showMaterialsGridDuringAiResult
                            ? 'Hide material list'
                            : 'Change Material'}
                        </button>
                      )}
                      {!(
                        operationTypeForApply === 'material_transfer' &&
                        showMaterialsGridDuringAiResult
                      ) && (
                        <>
                      <button
                        type="button"
                        onClick={handleMaterialResultDownload}
                        className="px-3 py-2 rounded-lg bg-gray-800 text-sm font-medium text-white hover:bg-gray-700 shadow-sm"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download
                      </button>
                      <button
                        type="button"
                        onClick={handleMaterialResultCancel}
                        className="px-3 py-2 rounded-lg bg-gray-800 text-sm font-medium text-white hover:bg-gray-700 shadow-sm"
                      >
                        Cancel
                      </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-2">
              {aiNotification && (
                <div
                  className="flex items-start gap-2 px-3 py-1.5 rounded-lg bg-white/90 shadow border border-gray-200 text-xs text-gray-700 max-w-[min(90vw,22rem)]"
                  title={
                    (aiNotification.message && aiNotification.message.trim()) ||
                    aiNotification.status
                  }
                >
                  <span
                    className={`mt-0.5 inline-flex h-2 w-2 shrink-0 rounded-full ${
                      aiNotification.status === 'Success'
                        ? 'bg-emerald-500'
                        : aiNotification.status === 'Failed'
                        ? 'bg-red-500'
                        : 'bg-amber-400'
                    }`}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="w-4 h-4 shrink-0 text-gray-600 mt-0.5"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="M12 2a2 2 0 0 1 2 2v.278A6.002 6.002 0 0 1 18 10v3.764l1.447 2.894A1 1 0 0 1 18.553 18H5.447a1 1 0 0 1-.894-1.342L6 13.764V10a6.002 6.002 0 0 1 4-5.722V4a2 2 0 0 1 2-2Zm0 20a3 3 0 0 1-2.816-2h5.632A3 3 0 0 1 12 22Z"
                    />
                  </svg>
                  <span className="font-medium leading-snug break-words text-left flex-1 min-w-0">
                    {(aiNotification.message && aiNotification.message.trim()) ||
                      aiNotification.status}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAiNotification(null);
                    }}
                    className="shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors -mr-0.5 -mt-0.5"
                    aria-label="Dismiss notification"
                    title="Dismiss"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                </div>
              )}
              {preview && !isCompareDragging && (
                <div
                    className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-[11rem] backdrop-blur-xl backdrop-saturate-150 border-white/50 bg-white/35 rounded-lg border border-gray-200 shadow-lg overflow-hidden flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <div className="flex-1 flex items-center justify-center p-2 min-h-0 relative ">
                    {swapped && basedImageLoadError ? (
                      <div className="flex flex-col items-center justify-center text-gray-400 text-sm p-4 text-center">
                        <svg className="w-10 h-10 mb-2 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14" />
                        </svg>
                        <span>Based image</span>
                        <span className="text-xs mt-1">Preview unavailable</span>
                      </div>
                    ) : (
                    <img
                      src={swapped ? image?.src : preview}
                      alt={swapped ? 'Based' : 'Photorealistic result'}
                      className="max-w-full max-h-full object-contain rounded"
                      draggable={false}
                        crossOrigin="anonymous"
                        onLoad={() => swapped && setBasedImageLoadError(false)}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          if (swapped) setBasedImageLoadError(true);
                        }}
                      onClick={(e) => e.stopPropagation()}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    )}
                    <div className="absolute bottom-2 right-2 flex items-center gap-2">
                      {preview && image?.src && !basedImageLoadError && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCompareSlider((v) => !v);
                          }}
                          className={`p-2 rounded-full shadow-md transition-colors ${
                            showCompareSlider
                              ? 'bg-white text-gray-800 hover:bg-gray-100'
                              : 'bg-gray-800 text-white hover:bg-gray-700'
                          }`}
                          title={showCompareSlider ? 'Hide compare slider' : 'Compare slider'}
                          aria-label={showCompareSlider ? 'Hide compare slider' : 'Compare slider'}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <rect x="3" y="4" width="18" height="16" rx="2" />
                            <line x1="12" y1="4" x2="12" y2="20" />
                            <polyline points="10 9 8 12 10 15" />
                            <polyline points="14 9 16 12 14 15" />
                          </svg>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          setShowCompareSlider(false);
                          handleSwap(e);
                        }}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-full shadow-md transition-colors"
                        title="Swap based and result"
                        aria-label="Swap based and result"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M7 16V4M7 4L3 8M7 4L11 8" />
                          <path d="M17 8v12M17 20l4-4M17 20l-4-4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
            {!imageRestoreLoading &&
              (scanTimeoutOrError ||
                scanNoDetections ||
                polygonsLoading ||
                samStatus) && (
              <div
                className={`absolute inset-0 z-20 flex items-center justify-center pointer-events-none ${
                  samStatus ? 'backdrop-blur-xl backdrop-saturate-150 bg-white/35' : ''
                }`}
              >
                {scanNoDetections ? (
                  <div
                    className="pointer-events-auto flex flex-col items-center justify-center gap-4 bg-black/50 rounded p-6 max-w-sm text-center"
                    aria-label="No detections"
                  >
                    <p className="text-white text-sm">
                      No interior regions were detected. Try Scan again, or start by selecting an area manually on the
                      image.
                    </p>
                    <div className="flex flex-col sm:flex-row flex-wrap items-stretch justify-center gap-2 w-full max-w-xs">
                      <button
                        type="button"
                        onClick={handleScanFromPopup}
                        disabled={polygonsLoading || isGenerateMaskTaskActive}
                        className="px-4 py-2 rounded-lg bg-white text-gray-800 font-medium hover:bg-gray-100 shadow transition-colors"
                      >
                        Scan
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleScanFailureStartManualArea()}
                        className="px-4 py-2 rounded-lg border border-white/80 bg-white/10 text-white font-medium hover:bg-white/20 shadow transition-colors"
                      >
                        Select area manually
                      </button>
                    </div>
                  </div>
                ) : scanTimeoutOrError ? (
                  <div
                    className="pointer-events-auto flex flex-col items-center justify-center gap-4 rounded p-6 max-w-sm text-center rounded-[26px] p-8 transition-colors backdrop-blur-xl backdrop-saturate-150 border-white/50 bg-white/35 hover:border-white/70"
                    aria-label="Scan failed"
                  >
                    <p className="text-sm text-primary-300">
                      Something went wrong while scanning the image Try Scan again, or start by selecting an area manually on the image.
                    </p>
                    <div className="flex flex-col sm:flex-row flex-wrap items-stretch justify-center gap-2 w-full max-w-xs">
                      <button
                        type="button"
                        onClick={handleScanFromPopup}
                        disabled={polygonsLoading || isGenerateMaskTaskActive}
                        className="px-4 py-2 rounded-full bg-white font-medium transition-colors border border-gray-200 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300"
                      >
                        Scan
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleScanFailureStartManualArea()}
                        className="px-4 py-2 rounded-full bg-white font-medium transition-colors border border-gray-200 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300"
                      >
                        Select Area Manually
                      </button>
                    </div>
                  </div>
                ) : samStatus ? (
                  <div
                    className="pointer-events-auto flex flex-col items-center justify-center bg-black/40 rounded p-6 text-white"
                    aria-label="Preparing image"
                  >
                    <svg
                      className="animate-spin h-8 w-8 mb-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="text-sm font-medium">
                      {samStatus === SAM_STATUS.LOADING_SESSION
                        ? 'Loading AI model...'
                        : samStatus === SAM_STATUS.PREPARING
                          ? 'Preparing selection...'
                          : samStatus === SAM_STATUS.ENCODING
                            ? 'Analyzing image...'
                            : samStatus === SAM_STATUS.DECODING
                              ? 'Refining mask...'
                              : 'Processing...'}
                    </span>
                  </div>
                ) : null}
              </div>
            )}
            {pendingCuratedPlacement && !swapped && (
              <div className="pointer-events-none absolute inset-x-0 bottom-6 z-20 flex justify-center px-2">
                <div className="pointer-events-auto flex flex-row items-center gap-2 rounded-full border border-white/50 bg-white/90 p-1 shadow-[0_5px_20px_rgba(0,0,0,0.18)] backdrop-blur-xl">
                  <button
                    type="button"
                    onClick={handleCancelCuratedPlacement}
                    className="flex min-w-[5.5rem] flex-row items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-1 text-sm font-medium text-primary-300 transition-colors hover:border-primary-300 hover:bg-primary-500 hover:text-primary-300"
                  >
                    <svg class="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                      <path d="M8.5625 8.5625L4.5625 4.5625M8.5625 4.5625L4.5625 8.5625M6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53844 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58655 1.01922 8.8586C0.717693 8.13065 0.562499 7.35043 0.562499 6.5625C0.562499 5.77457 0.717693 4.99435 1.01922 4.2664C1.32075 3.53845 1.76271 2.87701 2.31986 2.31986C2.87701 1.76271 3.53844 1.32075 4.2664 1.01922C4.99435 0.717694 5.77457 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625C12.5625 8.1538 11.9304 9.67992 10.8051 10.8051C9.67992 11.9304 8.1538 12.5625 6.5625 12.5625Z" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setCuratedPlacementConfirmOpen(true)}
                    className="flex min-w-[5.5rem] flex-row items-center justify-center gap-2 rounded-full border border-gray-800 bg-gray-800 px-4 py-1 text-sm font-medium text-white transition-colors hover:bg-gray-700"
                  >
                    <svg class="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                      <path d="M12.5625 6.5625C12.5625 7.35043 12.4073 8.13065 12.1058 8.8586C11.8043 9.58656 11.3623 10.248 10.8051 10.8051C10.248 11.3623 9.58656 11.8043 8.8586 12.1058C8.13065 12.4073 7.35043 12.5625 6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53845 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58656 1.01922 8.8586C0.717695 8.13065 0.5625 7.35043 0.5625 6.5625C0.5625 4.9712 1.19464 3.44508 2.31986 2.31986C3.44508 1.19464 4.9712 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625Z" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"></path><path d="M4.34033 6.98445L5.34367 8.13178C5.40969 8.20734 5.49192 8.26701 5.58422 8.30636C5.67652 8.34571 5.77651 8.36372 5.87674 8.35904C5.97697 8.35436 6.07485 8.32712 6.16308 8.27934C6.25132 8.23157 6.32763 8.16449 6.38633 8.08312L8.78433 4.76245" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                    Apply
                  </button>
                </div>
              </div>
            )}
            {editingLayerIndex != null && !swapped && (
              <>
                {editorState.activeTool === 'magicwand' &&
                  editorState.clickPoints.length > 0 &&
                  !samStatus &&
                  !scanTimeoutOrError && (
                    <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-20">
                      <button
                        type="button"
                        onClick={handleMagicWandApply}
                        className="px-3 py-1.5 rounded-full bg-emerald-500 text-xs sm:text-sm font-medium text-white hover:bg-emerald-600 shadow-sm"
                      >
                        Apply Mask ({editorState.clickPoints.length}/{MAX_SAM_PROMPT_POINTS} point
                        {editorState.clickPoints.length > 1 ? 's' : ''})
                      </button>
                    </div>
                  )}
                <div className="pointer-events-none absolute inset-x-0 bottom-6 z-20 flex flex-col items-center gap-2 px-2 pb-3">
                  <div className="pointer-events-auto flex max-w-full flex-wrap items-center justify-center gap-3">
                    <EditorActionToolbar
                      activeTool={editorState.activeTool}
                      onToolChange={(t) => {
                        setHasTriggeredZoomToolbarTips(false);
                        setEditorState((s) => ({ ...s, activeTool: t }));
                        setHasExplicitBrushToolSelection(t === 'brush');
                        if (editingLayerIndex != null && t === 'move') {
                          setPanToolbarTipsEpoch((n) => n + 1);
                        }
                      }}
                      magicMode={editorState.magicMode}
                      onMagicModeChange={(m) => setEditorState((s) => ({ ...s, magicMode: m }))}
                      brushSize={editorState.brushSize}
                      onBrushSizeChange={(v) => setEditorState((s) => ({ ...s, brushSize: v }))}
                      onZoomIn={handleZoomIn}
                      onZoomOut={handleZoomOut}
                      onUndo={handleUndo}
                      onRedo={handleRedo}
                      canUndo={
                        editorState.strokes.length > 0 ||
                        (editorState.polygonUndoStack?.length ?? 0) > 0
                      }
                      canRedo={
                        editorState.undoStack.length > 0 ||
                        (editorState.polygonRedoStack?.length ?? 0) > 0
                      }
                    />
                    <div className="flex shrink-0 items-center gap-2 p-2 rounded-full border border-white/15 bg-[rgba(253, 253, 253, 0.1)] shadow-[0_5px_20px_rgba(0,0,0,0.18)] backdrop-blur-xl backdrop-saturate-150 border border-white">
                      <button
                        type="button"
                        onClick={handleEditorCancel}
                        className="flex p-2 items-center justify-center rounded-full border border-white/20 bg-white/95 text-gray-800 shadow-sm hover:bg-gray-700 hover:text-white"
                        aria-label="Cancel"
                        title="Cancel"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={handleEditorConfirm}
                        className="flex p-2 items-center justify-center rounded-full bg-white/95 text-gray-800 shadow-sm hover:bg-gray-700 hover:text-white"
                        aria-label="Confirm selection"
                        title="Confirm selection"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  {samError && (
                    <div className="pointer-events-auto z-30 max-w-md rounded-lg bg-red-600/90 px-3 py-2 text-center text-xs text-white shadow-md">
                      {samError}
                    </div>
                  )}
                </div>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <SavedRenderHistoryStrip
              visible={showRenderPanel || showExportImagePanel}
              items={recentSavedRenderHistory}
              loading={localRecentRendersLoading}
              renderingJobs={pendingExportRenderJobs}
              error={null}
              onSelectItem={handleSavedRenderSelect}
              onCompare={handleSavedRenderCompare}
              compareActive={savedRenderCompareActive}
              compareSelectedIds={savedRenderCompareSelection}
              onCompareToggleItem={handleCompareRenderToggle}
              onSaveItem={handleSaveLocalRenderToHistory}
              onDeleteItem={handleRenderDeleteRequest}
              savingItemId={savingRenderHistoryItemId}
              serverRenderHistory={renderLibraryHistory}
              saveLimitReached={renderHistorySaveLimitReached}
              deletingItemId={deletingRenderHistoryId}
            />
            <SavedFlythroughHistoryStrip
              visible={showFlythroughPanel}
              items={localRecentFlythroughs}
              loading={localRecentFlythroughsLoading}
              renderingJobs={pendingFlythroughVideoJobs}
              onSelectItem={handleFlythroughVideoSelect}
              onSaveItem={handleSaveFlythroughToHistory}
              onDeleteItem={handleFlythroughDeleteRequest}
              savingItemId={savingFlythroughHistoryItemId}
              serverFlythroughHistory={flythroughLibraryHistory}
              deletingItemId={deletingFlythroughId}
            />
            <RenderPreviewModal
              open={Boolean(flythroughVideoLightboxSrc)}
              src={flythroughVideoLightboxSrc}
              onClose={() => setFlythroughVideoLightboxSrc('')}
              ariaLabel="Flythrough video"
              alt="Flythrough video"
              mediaType="video"
            />
            <RenderPreviewModal
              open={Boolean(savedRenderLightboxUrl) && !savedRenderCompareActive}
              src={savedRenderLightboxUrl}
              onClose={() => {
                setSavedRenderLightboxUrl('');
                setSavedRenderLightboxItemId(null);
              }}
              ariaLabel="Saved render"
              alt="Saved render full size"
            />
            <RenderCompareModal
              open={savedRenderCompareModalOpen && Boolean(savedRenderComparePair)}
              leftSrc={savedRenderComparePair?.leftSrc ?? ''}
              rightSrc={savedRenderComparePair?.rightSrc ?? ''}
              leftLabel={savedRenderComparePair?.leftLabel ?? 'Render 1'}
              rightLabel={savedRenderComparePair?.rightLabel ?? 'Render 2'}
              onClose={() => setSavedRenderCompareModalOpen(false)}
            />
          </div>
          {/* Materials: strip + collapse tab to the right of the canvas (same pattern as main right panel) */}
          <MaterialsPanel
            showMaterialsPanel={showMaterialsPanel}
            materialsPanelCollapsed={materialsPanelCollapsed}
            setMaterialsPanelCollapsed={setMaterialsPanelCollapsed}
          >
                {!showMaterialModifyPanel && (
                  <div className="flex shrink-0 items-center justify-between gap-2">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 min-w-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <rect x="3" y="3" width="7" height="7" />
                        <rect x="14" y="3" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" />
                        <rect x="14" y="14" width="7" height="7" />
                      </svg>
                      Materials
                    </h3>
                    <button
                      type="button"
                      onClick={handleCloseMaterialsPanel}
                      className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    >
                      Close
                    </button>
                  </div>
                )}
                <div className="relative flex-1 min-h-0">
                <div ref={materialsPanelListScrollRef} className="h-full min-h-0 overflow-y-auto pb-24">
                  {showMaterialModifyPanel && pendingMaterialSelection ? (
                    <div className="flex h-full min-h-0 flex-col items-center gap-4 py-2">
                      <h4 className="w-full text-left text-xl lao-mn text-gray-800">Modify Materials</h4>
                      <div className="w-40 h-40 rounded-full border border-primary-300 bg-white flex items-center justify-center overflow-hidden p-3">
                        {pendingMaterialSelection.imageKey ? (
                          <img
                            src={pendingMaterialSelection.imageKey}
                            alt={pendingMaterialSelection.materialType || 'Material'}
                            className="w-full h-full max-w-none max-h-none rounded-full object-cover"
                            style={{
                              transform: `rotate(${materialModifyRotation}deg) scale(${materialModifyScale / 100}) scaleX(${materialModifyFlipHorizontal ? -1 : 1}) scaleY(${materialModifyFlipVertical ? -1 : 1})`,
                              transformOrigin: 'center center',
                            }}
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">No image</span>
                        )}
                      </div>
                      <div className="w-full space-y-3">
                        <div className="relative h-12 overflow-hidden rounded-full border border-primary-100 bg-white">
                          <div
                            className="absolute inset-y-0 left-0 bg-[#E8F0FF]"
                            style={{
                              width: `${(materialModifyRotation / 360) * 100}%`,
                            }}
                          />
                          <div
                            className="absolute inset-y-0 w-[6px] -translate-x-1/2 rounded-full bg-gray-800 shadow-sm"
                            style={{
                              left: `clamp(3px, ${(materialModifyRotation / 360) * 100}%, calc(100% - 3px))`,
                            }}
                          />
                          <div className="relative z-[1] flex h-full items-center justify-between px-6 text-[12px] font-semibold text-[#20263A]">
                            <span>Rotation</span>
                            <span>{materialModifyRotation}°</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="360"
                            value={materialModifyRotation}
                            onChange={(e) => setMaterialModifyRotation(Number(e.target.value))}
                            className="absolute inset-0 z-[2] h-full w-full cursor-pointer opacity-0"
                            aria-label="Rotation"
                          />
                        </div>
                        <div className="relative h-12 overflow-hidden rounded-full border border-primary-100 bg-white">
                          <div
                            className="absolute inset-y-0 left-0 bg-[#E8F0FF]"
                            style={{
                              width: `${(materialModifyScale / 200) * 100}%`,
                            }}
                          />
                          <div
                            className="absolute inset-y-0 w-[6px] -translate-x-1/2 rounded-full bg-gray-800 shadow-sm"
                            style={{
                              left: `clamp(3px, ${(materialModifyScale / 200) * 100}%, calc(100% - 3px))`,
                            }}
                          />
                          <div className="relative z-[1] flex h-full items-center justify-between px-6 text-[12px] font-semibold text-[#20263A]">
                            <span>Scale</span>
                            <span>{(materialModifyScale / 100).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="200"
                            value={materialModifyScale}
                            onChange={(e) => setMaterialModifyScale(Number(e.target.value))}
                            className="absolute inset-0 z-[2] h-full w-full cursor-pointer opacity-0"
                            aria-label="Scale"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2 hidden">
                          <button
                            type="button"
                            onClick={() => setMaterialModifyFlipVertical((v) => !v)}
                            className={`flex flex-row justify-center items-center gap-2 rounded-full px-3 py-2 text-xs font-medium transition-colors ${
                              materialModifyFlipVertical
                                ? 'bg-gray-800 text-white'
                                : 'bg-white text-primary-300 hover:bg-gray-50'
                            }`}
                          >
                            <svg className="w-4 h-4" viewBox="0 0 17 13" fill="currentColor" aria-hidden="true">
                              <path d="M15.7692 12.026H9.76916C9.57024 12.026 9.37948 11.947 9.23883 11.8063C9.09817 11.6657 9.01916 11.4749 9.01916 11.276V0.753495C9.01843 0.587988 9.07247 0.426889 9.17286 0.295301C9.27325 0.163712 9.41434 0.0690256 9.57416 0.025995C9.91166 -0.064005 10.2567 0.0859948 10.4217 0.378495L16.4367 10.901C16.5717 11.1335 16.5717 11.4185 16.4367 11.651C16.3017 11.8835 16.0542 12.026 15.7692 12.026ZM0.769156 12.026C0.484156 12.026 0.236656 11.8835 0.101656 11.651C0.0350866 11.5372 0 11.4078 0 11.276C0 11.1442 0.0350866 11.0148 0.101656 10.901L6.11666 0.378495C6.28166 0.0859948 6.62666 -0.064005 6.96416 0.025995C7.12397 0.0690256 7.26507 0.163712 7.36546 0.295301C7.46584 0.426889 7.51988 0.587988 7.51916 0.753495V11.276C7.51916 11.4749 7.44014 11.6657 7.29949 11.8063C7.15883 11.947 6.96807 12.026 6.76916 12.026H0.769156ZM6.01916 10.526V3.58099L2.05166 10.526H6.01916Z" />
                            </svg>
                            Vertical
                          </button>
                          <button
                            type="button"
                            onClick={() => setMaterialModifyFlipHorizontal((v) => !v)}
                            className={`flex flex-row justify-center items-center gap-2 rounded-full px-3 py-2 text-xs font-medium transition-colors ${
                              materialModifyFlipHorizontal
                                ? 'bg-gray-800 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <svg className="w-4 h-4" viewBox="0 0 13 17" fill="currentColor" aria-hidden="true">
                              <path d="M0.000371244 15.7692L0.000371506 9.76916C0.000371515 9.57024 0.0793882 9.37948 0.220041 9.23883C0.360694 9.09817 0.551459 9.01916 0.750372 9.01916L11.2729 9.01916C11.4384 9.01843 11.5995 9.07247 11.7311 9.17286C11.8627 9.27324 11.9573 9.41434 12.0004 9.57416C12.0904 9.91166 11.9404 10.2567 11.6479 10.4217L1.12537 16.4367C0.892871 16.5717 0.607871 16.5717 0.375371 16.4367C0.142871 16.3017 0.000371231 16.0542 0.000371244 15.7692ZM0.000371899 0.769155C0.000371912 0.484155 0.142872 0.236655 0.375372 0.101655C0.489133 0.0350861 0.618565 -4.98651e-07 0.750372 -4.92889e-07C0.882179 -4.87128e-07 1.01161 0.0350861 1.12537 0.101655L11.6479 6.11666C11.9404 6.28166 12.0904 6.62666 12.0004 6.96416C11.9573 7.12397 11.8627 7.26507 11.7311 7.36546C11.5995 7.46584 11.4384 7.51988 11.2729 7.51916L0.750372 7.51916C0.551459 7.51916 0.360694 7.44014 0.220041 7.29949C0.0793883 7.15883 0.000371628 6.96807 0.000371637 6.76916L0.000371899 0.769155ZM1.50037 6.01916L8.44537 6.01916L1.50037 2.05166L1.50037 6.01916Z" />
                            </svg>
                            Horizontal
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : materialsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <span className="text-gray-500 text-sm">Loading materials…</span>
                    </div>
                  ) : materialsError ? (
                    <p className="text-red-600 text-sm py-2 whitespace-pre-wrap">
                      {materialsError}
                    </p>
                  ) : materialsForInterior.length === 0 ? (
                    <p className="text-gray-500 text-sm py-2">
                      No materials found
                      {materialsInteriorTypeId != null
                        ? ` for interior type ${materialsInteriorTypeId}.`
                        : ' that apply to every selected object.'}
                    </p>
                  ) : operationTypeForApply === 'material_transfer' &&
                    swapped &&
                    lastMaterialTransferResultKey &&
                    !showMaterialsGridDuringAiResult &&
                    !pendingMaterialSelection ? (
                    <div className="py-6 px-2 text-center text-sm text-gray-600 leading-relaxed">
                      Result preview is on the canvas. Use{' '}
                      <span className="font-medium text-gray-800">Change Material</span> below to
                      browse finishes again.
                    </div>
                  ) : (
                    <div className="space-y-3 py-2">
                      <div className="sticky top-0 z-10">
                        <input
                          type="text"
                          value={materialsSearchQuery}
                          onChange={(e) => setMaterialsSearchQuery(e.target.value)}
                          placeholder="Search materials"
                          className="w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                          aria-label="Search materials"
                        />
                      </div>
                      {filteredMaterialsForInterior.length === 0 ? (
                        <p className="text-gray-500 text-sm py-2">
                          No materials match "{materialsSearchQuery.trim()}".
                        </p>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          {filteredMaterialsForInterior.map((mat) => (
                            <div
                              key={mat.id}
                              className={`border rounded-lg overflow-hidden bg-white shadow-sm flex flex-col cursor-pointer transition ${
                                pendingMaterialSelection?.id === mat.id
                                  ? 'border-primary-400 ring-1 ring-primary-300 shadow-md'
                                  : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
                              }`}
                              onClick={() => setPendingMaterialSelection(mat)}
                            >
                              <div className="h-24 bg-gray-100 flex items-center justify-center overflow-hidden">
                                {mat.imageKey ? (
                                  <img
                                    src={mat.imageKey}
                                    alt={mat.materialType || 'Material'}
                                    className="w-full h-full object-cover"
                                    crossOrigin="anonymous"
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      setMaterialsForInterior((prev) =>
                                        Array.isArray(prev)
                                          ? prev.map((m) =>
                                              m.id === mat.id ? { ...m, imageKey: null } : m
                                            )
                                          : prev
                                      );
                                    }}
                                  />
                                ) : (
                                  <span className="text-xs text-gray-400">No image</span>
                                )}
                              </div>
                              <div className="p-2 space-y-0.5">
                                <p className="text-sm font-medium text-gray-900 truncate capitalize">
                                  {mat.materialType || `Material #${mat.id}`}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {!materialsLoading && pendingMaterialSelection && !showMaterialModifyPanel && (
                  <div className="absolute bottom-0 left-0 right-0 z-10 px-2 py-2">
                    <div className="flex items-center justify-between gap-2 text-xs text-gray-600 mb-2">
                      <span className="truncate hidden">
                        Selected: {pendingMaterialSelection.materialType || `Material #${pendingMaterialSelection.id}`}
                      </span>
                    </div>
                    <div className="flex flex-col justify-center items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setMaterialModifyGeneratedOnce(false);
                          setShowMaterialModifyPanel(true);
                        }}
                        className="flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                          <path d="M5.46225 6.67585C5.33541 6.8027 5.23482 6.95331 5.16623 7.11906C5.09764 7.28482 5.06239 7.46246 5.0625 7.64185V9.56335H6.996C7.35825 9.56335 7.70625 9.41935 7.96275 9.16285L13.6627 3.45985C13.7899 3.33306 13.8907 3.18243 13.9595 3.01661C14.0283 2.85078 14.0637 2.67301 14.0637 2.49348C14.0637 2.31394 14.0283 2.13617 13.9595 1.97035C13.8907 1.80452 13.7899 1.6539 13.6627 1.5271L13.0995 0.963851C12.9727 0.836634 12.822 0.735696 12.6561 0.666824C12.4902 0.597952 12.3124 0.5625 12.1328 0.5625C11.9531 0.5625 11.7753 0.597952 11.6094 0.666824C11.4435 0.735696 11.2928 0.836634 11.166 0.963851L5.46225 6.67585Z" stroke="currentColor" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14.0625 7.31323C14.0625 10.4955 14.0625 12.0862 13.074 13.0747C12.0855 14.0632 10.494 14.0632 7.3125 14.0632C4.131 14.0632 2.5395 14.0632 1.551 13.0747C0.5625 12.0862 0.5625 10.4947 0.5625 7.31323C0.5625 4.13173 0.5625 2.54023 1.551 1.55173C2.5395 0.563232 4.131 0.563232 7.3125 0.563232" stroke="currentColor" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Modify Materials / Finishes
                      </button>
                      <div className="flex flex-row gap-2 w-full">
                        <button
                          type="button"
                          onClick={() => {
                            const selected = pendingMaterialSelection;
                            if (selected) void handleApplyMaterialToPolygon(selected);
                          }}
                          disabled={materialTransferLoading}
                          className="flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                            <path d="M12.5625 6.5625C12.5625 7.35043 12.4073 8.13065 12.1058 8.8586C11.8043 9.58656 11.3623 10.248 10.8051 10.8051C10.248 11.3623 9.58656 11.8043 8.8586 12.1058C8.13065 12.4073 7.35043 12.5625 6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53845 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58656 1.01922 8.8586C0.717695 8.13065 0.5625 7.35043 0.5625 6.5625C0.5625 4.9712 1.19464 3.44508 2.31986 2.31986C3.44508 1.19464 4.9712 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625Z" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M4.34033 6.98445L5.34367 8.13178C5.40969 8.20734 5.49192 8.26701 5.58422 8.30636C5.67652 8.34571 5.77651 8.36372 5.87674 8.35904C5.97697 8.35436 6.07485 8.32712 6.16308 8.27934C6.25132 8.23157 6.32763 8.16449 6.38633 8.08312L8.78433 4.76245" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {materialTransferLoading
                            ? 'Starting…'
                            : materialTransferLastFailed
                              ? 'Regenerate'
                              : 'Confirm'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setPendingMaterialSelection(null);
                            if (
                              operationTypeForApply === 'material_transfer' &&
                              swapped &&
                              lastMaterialTransferResultKey
                            ) {
                              setShowMaterialsGridDuringAiResult(false);
                            }
                          }}
                          className="flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                            <path d="M8.5625 8.5625L4.5625 4.5625M8.5625 4.5625L4.5625 8.5625M6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53844 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58655 1.01922 8.8586C0.717693 8.13065 0.562499 7.35043 0.562499 6.5625C0.562499 5.77457 0.717693 4.99435 1.01922 4.2664C1.32075 3.53845 1.76271 2.87701 2.31986 2.31986C2.87701 1.76271 3.53844 1.32075 4.2664 1.01922C4.99435 0.717694 5.77457 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625C12.5625 8.1538 11.9304 9.67992 10.8051 10.8051C9.67992 11.9304 8.1538 12.5625 6.5625 12.5625Z" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {swapped &&
                  lastMaterialTransferResultKey &&
                  !materialsPanelCollapsed &&
                  !pendingMaterialSelection && (
                  <div className="absolute bottom-0 left-0 right-0 z-[11] px-2 py-2">
                    <div className="flex flex-col justify-center items-center gap-2">
                      {!(
                        operationTypeForApply === 'material_transfer' &&
                        showMaterialsGridDuringAiResult
                      ) && (
                        <>
                      <button
                        type="button"
                        onClick={() => void handleMaterialResultApply()}
                        className="flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors"
                      >
                        <svg
                          className="w-4 h-4 shrink-0"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Apply
                      </button>
                      <button
                        type="button"
                        onClick={handleRegenerateAiResult}
                        disabled={materialTransferLoading || removeObjectLoading}
                        className="flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg
                          className="w-4 h-4 shrink-0"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                          <path d="M3 3v5h5" />
                          <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                          <path d="M16 16h5v5" />
                        </svg>
                        Regenerate
                      </button>
                        </>
                      )}
                      {operationTypeForApply === 'material_transfer' && (
                        <button
                          type="button"
                          onClick={
                            showMaterialsGridDuringAiResult
                              ? () => {
                                  setShowMaterialsGridDuringAiResult(false);
                                  setMaterialsSearchQuery('');
                                }
                              : handleChangeMaterialFromResult
                          }
                          disabled={materialTransferLoading || removeObjectLoading}
                          className="flex flex-row justify-center items-center gap-2 w-full rounded-full bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-200 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg
                            className="w-4 h-4 shrink-0"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <rect x="3" y="3" width="7" height="7" />
                            <rect x="14" y="3" width="7" height="7" />
                            <rect x="14" y="14" width="7" height="7" />
                            <rect x="3" y="14" width="7" height="7" />
                          </svg>
                          {showMaterialsGridDuringAiResult
                            ? 'Hide material list'
                            : 'Change Material'}
                        </button>
                      )}
                      {!(
                        operationTypeForApply === 'material_transfer' &&
                        showMaterialsGridDuringAiResult
                      ) && (
                      <div className="flex flex-row gap-2 w-full">
                        <button
                          type="button"
                          onClick={() => void handleMaterialResultDownload()}
                          className="flex flex-row justify-center items-center gap-2 w-full rounded-full bg-white px-4 py-2 text-sm font-medium text-primary-300 hover:bg-primary-200 hover:text-white transition-colors"
                        >
                          <svg
                            className="w-4 h-4 shrink-0"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          Download
                        </button>
                        <button
                          type="button"
                          onClick={handleMaterialResultCancel}
                          className="flex flex-row justify-center items-center gap-2 w-full rounded-full bg-white px-4 py-2 text-sm font-medium text-primary-300 hover:bg-primary-200 hover:text-white transition-colors"
                        >
                          <svg
                            className="w-4 h-4 shrink-0"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden
                          >
                            <path d="M18 6 6 18M6 6l12 12" />
                          </svg>
                          Cancel
                        </button>
                      </div>
                      )}
                    </div>
                  </div>
                )}
                {!materialsLoading && pendingMaterialSelection && showMaterialModifyPanel && (
                  <div className="absolute bottom-0 left-0 right-0 z-10 px-2 py-2">
                    <div className="flex flex-col gap-2 w-full">
                      {materialModifyGeneratedOnce && (
                        <button
                          type="button"
                          onClick={() => {
                            const selected = pendingMaterialSelection;
                            if (selected) {
                              void handleApplyMaterialToPolygon(selected, {
                                rotation: materialModifyRotation,
                                scale: materialModifyScale / 100,
                              }, {
                                keepModifyPanelOpen: true,
                              });
                            }
                          }}
                          disabled={materialTransferLoading}
                          className="flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                            <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M3 3v5h5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16 16h5v5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {materialTransferLoading ? 'Adjusting…' : 'Adjust'}
                        </button>
                      )}
                      <div className="flex flex-row gap-2 w-full">
                      <button
                        type="button"
                        onClick={() => {
                          const selected = pendingMaterialSelection;
                          if (!materialModifyGeneratedOnce && selected) {
                            void handleApplyMaterialToPolygon(selected, {
                              rotation: materialModifyRotation,
                              scale: materialModifyScale / 100,
                            }, {
                              keepModifyPanelOpen: true,
                            });
                            setMaterialModifyGeneratedOnce(true);
                            return;
                          }
                          void handleMaterialResultApply();
                        }}
                        disabled={materialTransferLoading}
                        className="flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg
                          className="w-4 h-4 shrink-0"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {materialModifyGeneratedOnce ? 'Apply' : 'Generate'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMaterialModifyGeneratedOnce(false);
                          setShowMaterialModifyPanel(false);
                          setPendingMaterialSelection(null);
                          if (
                            operationTypeForApply === 'material_transfer' &&
                            swapped &&
                            lastMaterialTransferResultKey
                          ) {
                            setShowMaterialsGridDuringAiResult(true);
                          }
                        }}
                        className="flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                          <path d="M8.5625 8.5625L4.5625 4.5625M8.5625 4.5625L4.5625 8.5625M6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53844 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58655 1.01922 8.8586C0.717693 8.13065 0.562499 7.35043 0.562499 6.5625C0.562499 5.77457 0.717693 4.99435 1.01922 4.2664C1.32075 3.53845 1.76271 2.87701 2.31986 2.31986C2.87701 1.76271 3.53844 1.32075 4.2664 1.01922C4.99435 0.717694 5.77457 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625C12.5625 8.1538 11.9304 9.67992 10.8051 10.8051C9.67992 11.9304 8.1538 12.5625 6.5625 12.5625Z" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Cancel
                      </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
          </MaterialsPanel>
          <TransformModelsPanel
            showTransformModelsPanel={showTransformModelsPanel}
            transformModelsPanelCollapsed={transformModelsPanelCollapsed}
            setTransformModelsPanelCollapsed={setTransformModelsPanelCollapsed}
            onClose={() => setShowTransformModelsPanel(false)}
            onCancelPreview={handleTransformModelsCancelPreview}
            onGenerate={handleTransformModelsGenerate}
            onApply={handleTransformModelsApply}
            applyDisabled={!transformModelsOutputUrl || !selectedPlacedCuratedId}
            applyLoading={transformModelsPlaceLoading}
            generateDisabled={
              !selectedCuratedPlacement ||
              !Number.isFinite(Number(selectedCuratedPlacement.roomObjectId)) ||
              Number(selectedCuratedPlacement.roomObjectId) <= 0
            }
            generateLoading={changeObjectAngleLoading}
            outputModelSrc={transformModelsOutputUrl}
            outputModelName={selectedCuratedPlacement?.name}
          />
          <FlythroughExportVideoPanel
            showFlythroughExportVideoPanel={showFlythroughPanel}
            flythroughExportVideoPanelCollapsed={flythroughExportVideoPanelCollapsed}
            setFlythroughExportVideoPanelCollapsed={setFlythroughExportVideoPanelCollapsed}
            metadata={flythroughExportMetadata}
            previewVideoSrc={flythroughVideoPreviewSrc}
            thumbnailSrc={flythroughExportThumbnailSrc}
            generatedVideoSrc={flythroughGeneratedVideoSrc}
            generatedThumbnailSrc={flythroughGeneratedThumbnailSrc}
            onSaveToLibrary={handleFlythroughExportSaveToLibrary}
            saveToLibraryLoading={savingFlythroughHistoryItemId === 'export-generated'}
            saveToLibraryDisabled={
              !flythroughGeneratedVideoS3Key ||
              !flythroughGeneratedSavedSettings ||
              flythroughGeneratedAlreadySaved
            }
            onDeleteGenerated={handleFlythroughDeleteGenerated}
            onDownloadVideo={handleFlythroughDownloadGeneratedVideo}
            downloadVideoLoading={downloadingGeneratedFlythrough}
            onPreviewVideo={handleFlythroughPreviewVideo}
            onPreviewThumbnail={() => {}}
            onGenerateVideo={handleFlythroughGenerateVideo}
            onCancel={handleFlythroughCancelTasks}
            generateVideoLoading={flythroughGenerateVideoLoading}
            generateStatusLabel={flythroughGenerateStatusLabel}
            generateVideoAtCapacity={flythroughGenerateAtCapacity}
            previewVideoLoading={flythroughPreviewVideoLoading}
            previewStatusLabel={flythroughPreviewStatusLabel}
            previewVideoDisabled={flythroughPreviewDisabled}
            previewVideoDisabledTitle={FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_PREVIEW_DISABLED_TITLE}
          />
          <ExportImagePanel
            showExportImagePanel={showExportImagePanel}
            exportImagePanelCollapsed={exportImagePanelCollapsed}
            setExportImagePanelCollapsed={setExportImagePanelCollapsed}
            canvasImageSrc={exportCanvasImageSrc}
            renderSettingsMetadata={exportRenderSettingsMetadata}
            previewSrc={exportPreviewUrl}
            onGeneratePreview={handleExportGeneratePreview}
            onDiscardPreview={handleExportPreviewDiscard}
            onRender={handleExportRender}
            onCancel={handleExportImageCancel}
            generatePreviewLoading={exportPreviewLoading}
            previewStatusLabel={exportPreviewStatusLabel}
            renderLoading={exportRenderLoading}
            renderAtCapacity={exportRenderAtCapacity}
            pendingRenderCount={pendingExportRenderCount}
          />
          <ImageSelectionPanel
            imageSelectionPanelCollapsed={imageSelectionPanelCollapsed}
            setImageSelectionPanelCollapsed={setImageSelectionPanelCollapsed}
            visible={uploadModelMaskResults.length > 0}
            items={uploadModelMaskResults}
            selectedRoomObjectId={selectedUploadModelMaskRoomObjectId}
            onSelect={applyUploadModelMaskSelection}
            onCancel={handleCancelUploadModelMaskSelection}
            onSaveAsObject={handleSaveSelectedPolygonAsObject}
            saveAsObjectLoading={saveSelectedObjectLoading}
          />
          {curatedCompositeLoading && (
            <div
              className="absolute inset-y-0 left-[6rem] right-0 z-40 flex items-center justify-center backdrop-blur-xl backdrop-saturate-150 bg-white/35"
              aria-label="Combining and saving image"
              role="status"
              aria-live="polite"
            >
              <div className="pointer-events-auto flex flex-col items-center justify-center px-6 py-5 text-primary-300">
                <svg
                  className="animate-spin h-9 w-9 mb-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm font-medium">Combining and saving image...</span>
              </div>
            </div>
          )}
          {polygonsLoading && (
            <div
              className="absolute inset-y-0 left-[6rem] right-0 z-40 flex items-center justify-center backdrop-blur-xl backdrop-saturate-150 bg-white/35"
              aria-label={workspaceOverlayMessage ?? 'Preparing image'}
              role="status"
              aria-live="polite"
            >
              <div className="pointer-events-auto flex flex-col items-center justify-center px-6 py-5 text-primary-300">
                <svg
                  className="animate-spin h-9 w-9 mb-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm font-medium">
                  {workspaceOverlayMessage ?? 'Preparing image...'}
                </span>
              </div>
            </div>
          )}
          {!imageRestoreLoading &&
            (materialTransferLoading || removeObjectLoading || changeObjectAngleLoading) && (
            <div
              className="absolute inset-y-0 left-[6rem] right-0 z-40 flex items-center justify-center backdrop-blur-xl backdrop-saturate-150 bg-white/35"
              aria-label="Generating image"
              role="status"
              aria-live="polite"
            >
              <div className="pointer-events-auto flex flex-col items-center justify-center gap-3 px-6 py-5 text-primary-300">
                <svg
                  className="animate-spin h-9 w-9"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm font-medium">Generating image...</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelGeneratingImageOverlay();
                  }}
                  className="px-4 py-2 rounded-full border border-gray-300 bg-white text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div >
      </div >

      {showSessionRecoveryModal && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 backdrop-blur-xl backdrop-saturate-150 border-white/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="session-recovery-title"
        >
          <div className="rounded-xl shadow-xl w-full max-w-md backdrop-blur-xl backdrop-saturate-150 border-white/80 bg-white">
            <div className="p-4 border-b border-gray-200">
              <h2 id="session-recovery-title" className="text-lg font-semibold text-gray-800">
                Recover previous session?
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                We found an auto-saved draft for this room image. Restore it or discard it.
              </p>
            </div>
            <div className="p-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleDiscardDraft}
                className="px-4 py-2 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleRestoreDraft}
                className="px-4 py-2 rounded-full bg-gray-800 text-white hover:bg-gray-700 transition-colors"
              >
                Restore session
              </button>
            </div>
          </div>
        </div>
      )}

      {flythroughGenerationFailedMode ? (
        <FlythroughGenerationFailedModal
          onClose={handleFlythroughGenerationFailedClose}
          onRetry={handleFlythroughGenerationFailedRetry}
          retryLoading={flythroughPreviewVideoLoading || flythroughGenerateVideoLoading}
        />
      ) : null}

      {flythroughDeleteConfirmItem ? (
        <DeleteRenderConfirmModal
          onClose={handleFlythroughDeleteConfirmClose}
          onConfirm={() => void handleDeleteFlythroughLocal()}
          confirmLoading={deletingFlythroughId != null}
        />
      ) : null}

      {renderDeleteConfirmItem ? (
        <DeleteRenderConfirmModal
          onClose={handleRenderDeleteConfirmClose}
          onConfirm={() => void handleDeleteRenderHistory()}
          confirmLoading={deletingRenderHistoryId != null}
        />
      ) : null}

      {removeObjectConfirmOpen && (
        <RemoveObjectConfirmModal
          onClose={handleRemoveObjectConfirmModalClose}
          onConfirm={handleRemoveObjectConfirmModalConfirm}
          confirmLoading={removeObjectLoading}
        />
      )}

      {curatedPlacementConfirmOpen && (
        <CuratedPlacementConfirmModal
          onClose={() => setCuratedPlacementConfirmOpen(false)}
          onConfirm={() => {
            setCuratedPlacementConfirmOpen(false);
            handleConfirmCuratedPlacement();
          }}
        />
      )}

      {modelLibraryModalOpen && (
        <ModelLibraryModal
          key={modelLibraryModalKey}
          roomId={roomId}
          initialStep={modelLibraryInitialStep}
          initialDetectedSelectionIds={modelLibraryDetectedSelectionIds}
          initialLibrarySelectionIds={curatedLibrarySelectionIds}
          onConfirmLibrarySelection={handleConfirmLibraryCuratedSelection}
          onClose={() => {
            setModelLibraryModalOpen(false);
            setModelLibraryDetectedSelectionIds(null);
          }}
          closeDetectedModelsCancelAsClose={uploadModelMaskResults.length > 0}
          excludeLibraryRoomObjectIds={librarySessionHiddenRoomObjectIds}
          items={studioModels}
          setItems={setStudioModels}
          onUploadStandardMaskLoading={handleUploadStandardMaskLoading}
          onUploadStandardMaskComplete={handleUploadStandardMaskComplete}
          onUploadStandardMaskError={handleUploadStandardMaskError}
        />
      )}

      {/* Object choice modal: Auto-detect (show all) or Choose object (checkbox list + Confirm) */}
      {
        editingLayerIndex == null &&
        (pendingDetectedObjects != null ||
          objectChoiceStep === 'editNewObjectLabel' ||
          objectChoiceStep === 'reviewLowConfidence') &&
        !addingNewObjectFromPopup &&
        image && (
          <ObjectChoiceModal
            onClose={handleCloseSelectObjectsPopup}
            title={
              <>
                {objectChoiceStep === 'initial' && 'Show detected objects'}
                {objectChoiceStep === 'choose' && 'What object do you want to focus on?'}
                {objectChoiceStep === 'editNewObjectLabel' &&
                  (objectRenameIndex != null ? 'Rename object label' : 'Edit label for new object')}
                {objectChoiceStep === 'reviewLowConfidence' && 'Review low confidence regions'}
              </>
            }
          >
              <div className="flex-1 min-h-0 p-4 pb-10">
                {objectChoiceStep === 'editNewObjectLabel' ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      {objectRenameIndex != null ? 'Rename this object label.' : 'Give your new object a label.'}
                    </p>
                    <label htmlFor="new-object-label" className="block text-sm font-medium text-gray-700">
                      Label
                    </label>
                    <div className="relative">
                      <input
                        id="new-object-label"
                        type="text"
                        value={newObjectLabel}
                        onChange={(e) => setNewObjectLabel(e.target.value)}
                        onFocus={() => setNewObjectLabelDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setNewObjectLabelDropdownOpen(false), 150)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400"
                        placeholder="Search or type label..."
                        autoComplete="off"
                      />
                      {newObjectLabelDropdownOpen && interiorTypesLoading ? (
                        <div className="absolute left-0 right-0 top-full mt-1 py-2 px-2 bg-white border border-gray-200 rounded-lg shadow-lg z-20 text-sm text-gray-500">
                          Loading types...
    </div>
                      ) : newObjectLabelDropdownOpen && interiorTypesList.length > 0 ? (
                        <ul
                          className="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1"
                          role="listbox"
                        >
                          {interiorTypesList
                            .filter((t) =>
                              String(t.interiorType)
                                .toLowerCase()
                                .includes((newObjectLabel || '').toLowerCase())
                            )
                            .map((t) => (
                              <li
                                key={t.id}
                                role="option"
                                className="px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 cursor-pointer truncate"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setNewObjectLabel(t.interiorType);
                                  setNewObjectLabelDropdownOpen(false);
                                }}
                              >
                                {t.interiorType}
                              </li>
                            ))}
                          {interiorTypesList.filter((t) =>
                            String(t.interiorType)
                              .toLowerCase()
                              .includes((newObjectLabel || '').toLowerCase())
                          ).length === 0 && (
                              <li className="px-3 py-2 text-sm text-gray-500">No matches</li>
                            )}
                        </ul>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!objectBeingLabeled) return;
                        const raw = newObjectLabel.trim() || objectBeingLabeled.label || '';
                        const label = raw.trim();
                        let interiorTypeId =
                          objectBeingLabeled.interior_type_id ?? objectBeingLabeled.interiorTypeId ?? null;
                        if (label && interiorTypesList.length > 0) {
                          const match = interiorTypesList.find(
                            (t) =>
                              String(t.interiorType).toLowerCase() === label.toLowerCase()
                          );
                          if (match) interiorTypeId = match.id;
                        }
                        let nextObj = {
                          ...objectBeingLabeled,
                          label,
                          interior_type_id: interiorTypeId ?? objectBeingLabeled.interior_type_id ?? 0,
                        };

                        if (objectRenameIndex != null) {
                          const renameIndex = objectRenameIndex;
                          const baseForRename =
                            Array.isArray(polygons) && polygons.length > 0
                              ? polygons
                              : objectBeingLabeled
                                ? [objectBeingLabeled]
                                : [];
                          const toPersist = baseForRename.map((p, idx) =>
                            idx === renameIndex ? { ...p, ...nextObj } : p
                          );
                          setPolygons((prev) =>
                            Array.isArray(prev)
                              ? prev.map((p, idx) => (idx === renameIndex ? { ...p, ...nextObj } : p))
                              : prev
                          );
                          const isLowConfidenceIsolatedRename =
                            reviewLowConfidenceEditIndex != null &&
                            Array.isArray(reviewLowConfidenceSource);
                          if (roomId != null && roomImageId != null) {
                            try {
                              const saved = await persistInteriors(toPersist);
                              if (Array.isArray(saved) && saved.length > 0) {
                                if (isLowConfidenceIsolatedRename) {
                                  const prevOne = toPersist[renameIndex] ?? toPersist[0];
                                  const sid = prevOne?.id ?? prevOne?.interior_id;
                                  let fromSaved =
                                    sid != null
                                      ? saved.find(
                                          (s) => Number(s.id ?? s.interior_id) === Number(sid)
                                        )
                                      : null;
                                  if (fromSaved == null && saved[reviewLowConfidenceEditIndex] != null) {
                                    fromSaved = saved[reviewLowConfidenceEditIndex];
                                  }
                                  const singleUpdated =
                                    fromSaved != null
                                      ? {
                                          ...prevOne,
                                          ...fromSaved,
                                          label: nextObj.label,
                                          interior_type_id:
                                            nextObj.interior_type_id ??
                                            fromSaved.interior_type_id ??
                                            prevOne.interior_type_id,
                                        }
                                      : { ...prevOne, ...nextObj };
                                  const updatedSource = reviewLowConfidenceSource.map((p, i) =>
                                    i === reviewLowConfidenceEditIndex
                                      ? { ...p, ...singleUpdated }
                                      : p
                                  );
                                  setPolygons([singleUpdated]);
                                  setDetectedObjectsFromAPI(updatedSource);
                                  setReviewLowConfidenceSource(updatedSource);
                                  setInteriorsFromExisting(true);
                                  setInteriorsSavedOnce(true);
                                  setObjectChoiceStep('reviewLowConfidence');
                                  setEditingLayerIndex(0);
                                  setSelectedPolygonIndex(0);
                                  resetEditorState();
                                } else {
                                  setPolygons(saved);
                                  setDetectedObjectsFromAPI(saved);
                                  setInteriorsFromExisting(true);
                                  setInteriorsSavedOnce(true);
                                  setObjectChoiceStep('initial');
                                }
                              } else if (isLowConfidenceIsolatedRename) {
                                const prevOne = toPersist[renameIndex] ?? toPersist[0];
                                const updatedSource = reviewLowConfidenceSource.map((p, i) =>
                                  i === reviewLowConfidenceEditIndex
                                    ? { ...p, ...prevOne, ...nextObj }
                                    : p
                                );
                                setPolygons([{ ...prevOne, ...nextObj }]);
                                setReviewLowConfidenceSource(updatedSource);
                                setObjectChoiceStep('reviewLowConfidence');
                                setEditingLayerIndex(0);
                                setSelectedPolygonIndex(0);
                                resetEditorState();
                              } else {
                                setObjectChoiceStep('initial');
                              }
                            } catch (err) {
                              console.error('Failed to rename object from label popup:', err);
                              setError(err?.message ?? 'Failed to rename object');
                            }
                          } else if (isLowConfidenceIsolatedRename) {
                            const prevOne = toPersist[renameIndex] ?? toPersist[0];
                            const updatedSource = reviewLowConfidenceSource.map((p, i) =>
                              i === reviewLowConfidenceEditIndex ? { ...p, ...prevOne, ...nextObj } : p
                            );
                            setPolygons([{ ...prevOne, ...nextObj }]);
                            setReviewLowConfidenceSource(updatedSource);
                            setObjectChoiceStep('reviewLowConfidence');
                            setEditingLayerIndex(0);
                            setSelectedPolygonIndex(0);
                            resetEditorState();
                          } else {
                            setObjectChoiceStep('initial');
                          }
                          setPendingDetectedObjects(null);
                          setObjectBeingLabeled(null);
                          setObjectRenameIndex(null);
                          setNewObjectLabel('');
                          return;
                        }

                        let refreshedInteriors = null;
                        let newObjectServerId = null;
                        const previousSelection = selectedObjectBeforeAddRef.current;
                        if (roomId != null && roomImageId != null) {
                          setPolygonsLoading(true);
                          try {
                            const saved = await persistInteriors([nextObj]);
                            if (Array.isArray(saved) && saved.length > 0) {
                              refreshedInteriors = saved;
                              const fallbackByLabelType = saved.find((s) => {
                                const sType = Number(s?.interior_type_id ?? s?.interiorTypeId ?? -1);
                                const nType = Number(nextObj?.interior_type_id ?? nextObj?.interiorTypeId ?? -2);
                                if (sType !== nType) return false;
                                return (
                                  String(getLabelForInterior(s, interiorTypesList, 0)).trim().toLowerCase() ===
                                  String(label).trim().toLowerCase()
                                );
                              });
                              const existingIdsBeforeAdd = existingObjectIdsBeforeAddRef.current;
                              const newSavedCandidates = Array.isArray(saved)
                                ? saved.filter((s) => {
                                    const sid = s?.id ?? s?.interior_id;
                                    if (sid == null) return false;
                                    return !existingIdsBeforeAdd?.has(Number(sid));
                                  })
                                : [];
                              const candidateByShape =
                                newSavedCandidates.find((s) => {
                                  const sType = Number(s?.interior_type_id ?? s?.interiorTypeId ?? -1);
                                  const nType = Number(nextObj?.interior_type_id ?? nextObj?.interiorTypeId ?? -2);
                                  if (sType !== nType) return false;
                                  const sb = s?.bbox;
                                  const nb = nextObj?.bbox;
                                  return (
                                    Array.isArray(nb) &&
                                    Array.isArray(sb) &&
                                    nb.length === sb.length &&
                                    nb.every((v, i) => Number(v) === Number(sb[i]))
                                  );
                                }) ??
                                null;
                              const newCandidate =
                                candidateByShape ??
                                (newSavedCandidates.length === 1
                                  ? newSavedCandidates[0]
                                  : newSavedCandidates.length > 1
                                    ? newSavedCandidates[newSavedCandidates.length - 1]
                                    : null);
                              const savedId =
                                (newCandidate?.id ?? newCandidate?.interior_id) ??
                                getInteriorIdFromSaved(nextObj, saved) ??
                                fallbackByLabelType?.id ??
                                fallbackByLabelType?.interior_id ??
                                null;
                              if (savedId != null) {
                                newObjectServerId = Number(savedId);
                                nextObj = {
                                  ...nextObj,
                                  id: savedId,
                                  interior_id: savedId,
                                };
                              }
                              setDetectedObjectsFromAPI(saved);
                              setInteriorsFromExisting(true);
                              setInteriorsSavedOnce(true);
                            }
                          } catch (err) {
                            console.error('Failed to save new object from label popup:', err);
                            setError(err?.message ?? 'Failed to save new object');
                          } finally {
                            setPolygonsLoading(false);
                          }
                        }
                        let nextSelectedIndex = 0;
                        let nextCurrentPolygons = Array.isArray(polygons) ? [...polygons] : [];
                        if (Array.isArray(refreshedInteriors) && refreshedInteriors.length > 0) {
                          const baseList = Array.isArray(polygons) ? polygons : [];
                          nextCurrentPolygons = baseList.map((p, idx) => {
                            if (idx === 0) {
                              const draft = { ...p, ...nextObj };
                              const resolvedId =
                                newObjectServerId ??
                                draft?.id ??
                                draft?.interior_id ??
                                null;
                              if (resolvedId == null) return draft;
                              return { ...draft, id: resolvedId, interior_id: resolvedId };
                            }
                            // Keep existing rows stable; do not rematch ids heuristically here.
                            const existingId = p?.id ?? p?.interior_id ?? null;
                            if (existingId == null) return p;
                            return { ...p, id: existingId, interior_id: existingId };
                          });
                          if (nextCurrentPolygons.length > 1) {
                            // During add flow the draft object sits at index 0 for editing.
                            // After save, move it to the end so order matches server/object list.
                            const [newDraft, ...existing] = nextCurrentPolygons;
                            nextCurrentPolygons = [...existing, newDraft];
                          }
                          setPolygons(nextCurrentPolygons);
                          const previousSelectedFromRefresh =
                            previousSelection?.id != null
                              ? refreshedInteriors.find(
                                  (s) => Number(s?.id ?? s?.interior_id) === Number(previousSelection.id)
                                )
                              : refreshedInteriors.find((s) => {
                                  const sType = Number(s?.interior_type_id ?? s?.interiorTypeId ?? -1);
                                  const typeMatch = sType === Number(previousSelection?.typeId ?? -2);
                                  const sb = s?.bbox;
                                  const bboxMatch =
                                    Array.isArray(previousSelection?.bbox) &&
                                    Array.isArray(sb) &&
                                    previousSelection.bbox.length === sb.length &&
                                    previousSelection.bbox.every((v, i) => Number(v) === Number(sb[i]));
                                  const labelMatch =
                                    String(getLabelForInterior(s, interiorTypesList, 0)).trim().toLowerCase() ===
                                    String(previousSelection?.label ?? '').trim().toLowerCase();
                                  return typeMatch && (bboxMatch || labelMatch);
                                });
                          const fallbackFromRefresh = refreshedInteriors.find((s) => {
                            const sType = Number(s?.interior_type_id ?? s?.interiorTypeId ?? -1);
                            const nType = Number(nextObj?.interior_type_id ?? nextObj?.interiorTypeId ?? -2);
                            if (sType !== nType) return false;
                            return (
                              String(getLabelForInterior(s, interiorTypesList, 0)).trim().toLowerCase() ===
                              String(label).trim().toLowerCase()
                            );
                          });
                          const preferredSelected =
                            previousSelectedFromRefresh ??
                            (newObjectServerId != null
                              ? refreshedInteriors.find(
                                  (s) => Number(s?.id ?? s?.interior_id) === Number(newObjectServerId)
                                )
                              : null) ??
                            fallbackFromRefresh ??
                            null;
                          if (preferredSelected != null && Array.isArray(polygons) && polygons.length > 0) {
                            const preferredId = Number(preferredSelected?.id ?? preferredSelected?.interior_id ?? -1);
                            const idxInCurrentList = nextCurrentPolygons.findIndex(
                              (p) => Number(p?.id ?? p?.interior_id ?? -2) === preferredId
                            );
                            nextSelectedIndex = idxInCurrentList >= 0 ? idxInCurrentList : 0;
                          } else {
                            nextSelectedIndex = 0;
                          }
                        } else {
                          setPolygons((prev) => {
                            if (!Array.isArray(prev) || prev.length === 0) return prev;
                            // New object from add flow is prepended at index 0.
                            const merged = prev.map((p, idx) => (idx === 0 ? { ...p, ...nextObj } : p));
                            if (merged.length <= 1) return merged;
                            const [newDraft, ...existing] = merged;
                            return [...existing, newDraft];
                          });
                        }
                        setSelectedPolygonIndex(nextSelectedIndex);
                        setShowAllPolygonsOnCanvas(false);
                        setDetectedObjectMultiSelectActive(false);
                        setMultiSelectedPolygonIndices(new Set());
                        if (Array.isArray(refreshedInteriors) && refreshedInteriors.length > 0) {
                          const newDetectedIdx = refreshedInteriors.findIndex((item) => {
                            const itemId = item?.id ?? item?.interior_id;
                            if (newObjectServerId != null && itemId != null) {
                              return Number(itemId) === Number(newObjectServerId);
                            }
                            const iType = Number(item?.interior_type_id ?? item?.interiorTypeId ?? -1);
                            const nType = Number(nextObj?.interior_type_id ?? nextObj?.interiorTypeId ?? -2);
                            if (iType !== nType) return false;
                            return (
                              String(getLabelForInterior(item, interiorTypesList, 0)).trim().toLowerCase() ===
                              String(label).trim().toLowerCase()
                            );
                          });
                          if (newDetectedIdx >= 0) {
                            setSelectedObjectIndices((prev) => new Set([...prev, newDetectedIdx]));
                          }
                        } else {
                          const nextDetected = Array.isArray(detectedObjectsFromAPI)
                            ? [...detectedObjectsFromAPI, nextObj]
                            : [nextObj];
                          setDetectedObjectsFromAPI(nextDetected);
                          setSelectedObjectIndices((prev) => new Set([...prev, nextDetected.length - 1]));
                        }
                        setSelectedUserDefinedIndices(new Set());
                        setObjectChoiceStep(addObjectLabelReturnStepRef.current);
                        setObjectBeingLabeled(null);
                        setObjectRenameIndex(null);
                        setNewObjectLabel('');
                        selectedObjectBeforeAddRef.current = null;
                        existingObjectIdsBeforeAddRef.current = null;
                      }}
                      className="w-full py-3 px-4 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-700 transition-colors"
                    >
                      Confirm
                    </button>
                  </div>
                ) : objectChoiceStep === 'initial' ? (
                  <div className="space-y-3">
                    <p className="text-sm text-primary-300 text-center">
                      {pendingDetectedObjects.length} object{pendingDetectedObjects.length !== 1 ? 's' : ''} detected. How would you like to display them?
                    </p>
                    {/*
                    {!hideLowConfidenceBanner && hasLowConfidenceInPopupList && (
                      <div
                        className="rounded-lg border border-primary-200 p-3 space-y-2 flex flex-col items-center justify-center border-white/50 bg-white/50 hover:border-white/70"
                        role="status"
                        aria-live="polite"
                      >
                        <p className="text-sm font-medium text-amber-950">
                          The system detected a low confidence region
                          {interiorsListForLowConfidencePopup.filter(isLowConfidenceInterior).length > 1
                            ? 's'
                            : ''}
                          .
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={handleLowConfidenceIgnore}
                            title="Ignore the warning"
                            aria-label="Ignore the warning"
                            className="px-3 py-0.5 rounded-full text-sm font-medium bg-white text-primary-300 hover:bg-primary-400 hover:text-white transition-colors"
                          >
                            Ignore
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLowConfidenceReview('initial')}
                            title="Review and update"
                            aria-label="Review and update"
                            className="px-3 py-0.5 rounded-full text-sm font-medium bg-primary-200 text-primary-300 hover:bg-primary-400 hover:text-white transition-colors"
                          >
                            Review
                          </button>
                        </div>
                      </div>
                    )}*/}
                    <div className="flex flex-col gap-2 px-8">
                      <button
                        type="button"
                        onClick={async () => {
                          if (hasLowConfidenceInPopupList) {
                            handleLowConfidenceIgnore();
                          }
                          setPolygons(pendingDetectedObjects);
                          setShowPolygonsOverlay(true);
                          setPendingDetectedObjects(null);
                          selectObjectsSnapshotRef.current = null;
                          setObjectChoiceStep('initial');
                          setSelectedObjectIndices(new Set(detectedObjectsFromAPI.map((_, i) => i)));
                          setSelectedUserDefinedIndices(new Set(userDefinedObjects.map((_, i) => i)));
                          if (
                            pendingDetectedObjects.length > 0 &&
                            roomId != null &&
                            roomImageId != null &&
                            !interiorsSavedOnce &&
                            !interiorsFromExisting
                          ) {
                            try {
                              const saved = await persistInteriors(pendingDetectedObjects);
                              if (Array.isArray(saved) && saved.length > 0) {
                                setPolygons(saved);
                                setDetectedObjectsFromAPI(saved);
                                setInteriorsFromExisting(true);
                                setInteriorsSavedOnce(true);
                              }
                            } catch (err) {
                              console.error('Failed to save interiors (auto-detect):', err);
                              setError(err?.message ?? 'Failed to save objects');
                            }
                          }
                        }}
                        className="w-full py-3 px-4 rounded-full bg-primary-400 text-white font-medium hover:bg-primary-200 transition-colors"
                      >
                        Auto-detect — show all objects
                      </button>
                      <button
                        type="button"
                        onClick={openChooseObjectPopup}
                        className="w-full py-3 px-4 rounded-full bg-white text-primary-300 font-medium hover:bg-primary-200 hover:text-white transition-colors"
                      >
                        Choose object — select which to show
                      </button>
                    </div>
                  </div>
                ) : objectChoiceStep === 'reviewLowConfidence' ? (
                  <div className="space-y-4">
                    <p className="text-sm text-primary-300 text-center">
                      These regions were detected with lower model confidence. Regions you marked as ignored will show a
                      warning on the selection list.
                    </p>
                    <div className="p-6 bg-white/30 rounded-xl">
                      <ul className="max-h-56 overflow-y-auto space-y-2 p-1">
                        {interiorsListForLowConfidencePopup
                          .map((item, i) => ({ item, i }))
                          .filter(({ item }) => isLowConfidenceInterior(item))
                          .map(({ item, i }) => (
                            <li
                              key={getInteriorStableKey(item, i)}
                              className="rounded-full bg-white px-3 py-2 text-sm font-medium text-primary-300"
                            >
                              <div className="flex items-center gap-2">
                                <span className="truncate flex-1 min-w-0">
                                  {getLabelForInterior(item, interiorTypesList, i)}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => startLowConfidenceEdit(i, interiorsListForLowConfidencePopup)}
                                  className="shrink-0 p-1.5 rounded-full text-amber-900 hover:bg-primary-200 hover:text-white transition-colors"
                                  title={`Edit ${getLabelForInterior(item, interiorTypesList, i)}`}
                                  aria-label={`Edit ${getLabelForInterior(item, interiorTypesList, i)}`}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                                    <path d="M12 20h9" />
                                    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
                                  </svg>
                                </button>
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>
                    <div className="flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => setObjectChoiceStep(lowConfidenceReviewReturnStepRef.current)}
                        className="py-2 px-8 rounded-full bg-white text-gray-800 font-medium hover:bg-primary-200 hover:text-white transition-colors flex items-center gap-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M13 15l-4-3 4-3" />
                          <path d="M22 12a10 10 0 11-20 0 10 10 0 0120 0z" />
                        </svg>
                        Back
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {!hideLowConfidenceBanner && hasLowConfidenceInPopupList && (
                      <div
                        className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2"
                        role="status"
                        aria-live="polite"
                      >
                        <p className="text-sm font-medium text-amber-950">
                          The system detected a low confidence region
                          {interiorsListForLowConfidencePopup.filter(isLowConfidenceInterior).length > 1
                            ? 's'
                            : ''}
                          .
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={handleLowConfidenceIgnore}
                            title="Ignore the warning"
                            aria-label="Ignore the warning"
                            className="px-3 py-1.5 rounded-lg text-sm font-medium border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 transition-colors"
                          >
                            Ignore
                          </button>
                          <button
                            type="button"
                            onClick={() => handleLowConfidenceReview('choose')}
                            title="Review and update"
                            aria-label="Review and update"
                            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-800 text-white hover:bg-amber-900 transition-colors"
                          >
                            Review
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between items-center gap-2">
                    <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteDetectionMode(false);
                            setObjectChoiceStep('initial');
                          }}
                          className="text-sm text-gray-600 hover:text-gray-800"
                        >
                          ← Back
                        </button>
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setDeleteDetectionMode((v) => !v)}
                          className={`text-sm ml-auto ${deleteDetectionMode ? 'text-red-700' : 'text-gray-600'} hover:text-red-700`}
                          title="Delete selected detections"
                        >
                          {deleteDetectionMode ? 'Cancel delete' : 'Delete detection'}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedObjectIndices(
                              selectedObjectIndices.size === detectedObjectsFromAPI.length
                                ? new Set()
                                : new Set(detectedObjectsFromAPI.map((_, i) => i))
                            )
                          }
                          className="text-sm text-gray-600 hover:text-gray-800 ml-auto"
                        >
                          {selectedObjectIndices.size === detectedObjectsFromAPI.length ? 'Deselect all' : 'Select all'}
                        </button>
                     
                      <button
                        type="button"
                        onClick={async () => {
                          if (roomId == null || roomImageId == null) {
                            const source = [...detectedObjectsFromAPI, ...userDefinedObjects];
                            setPendingDetectedObjects(source);
                            {
                              const { detectedSet, userSet } = buildPreselectedSetsFromPolygons(
                                polygons,
                                detectedObjectsFromAPI,
                                userDefinedObjects
                              );
                              setSelectedObjectIndices(detectedSet);
                              setSelectedUserDefinedIndices(userSet);
                            }
                            return;
                          }
                          try {
                            const refreshed = await getInteriors(roomId, roomImageId);
                            const list = Array.isArray(refreshed) ? refreshed : [];
                            setDetectedObjectsFromAPI(list);
                            setInteriorsFromExisting(list.length > 0);
                            setUserDefinedObjects([]);
                            setPendingDetectedObjects(list);
                            {
                              const { detectedSet, userSet } = buildPreselectedSetsFromPolygons(
                                polygons,
                                list,
                                []
                              );
                              setSelectedObjectIndices(detectedSet);
                              setSelectedUserDefinedIndices(userSet);
                            }
                          } catch (err) {
                            console.error('Failed to refresh interiors list:', err);
                            const fallback = [...detectedObjectsFromAPI, ...userDefinedObjects];
                            setPendingDetectedObjects(fallback);
                            {
                              const { detectedSet, userSet } = buildPreselectedSetsFromPolygons(
                                polygons,
                                detectedObjectsFromAPI,
                                userDefinedObjects
                              );
                              setSelectedObjectIndices(detectedSet);
                              setSelectedUserDefinedIndices(userSet);
                            }
                          }
                        }}
                        className="text-sm text-gray-500 hover:text-gray-800 underline"
                      >
                        Refresh list
                      </button>
                      </div>
                    </div>
                    <div className=" py-6 px-2 bg-white/30 rounded-xl">
                      <ul className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-1">
                        {detectedObjectsFromAPI.length === 0 && userDefinedObjects.length === 0 ? (
                          <li className="col-span-2 rounded-md p-3 text-sm text-gray-700 text-center">
                            <p className="font-medium text-gray-900">No detected objects yet.</p>
                            <p className="mt-1 text-gray-600">
                              Run AI detection to generate interior regions for this room.
                            </p>
                            <button
                              type="button"
                              onClick={handleScanFromPopup}
                              disabled={polygonsLoading || isGenerateMaskTaskActive}
                              className="mt-3 w-full py-2 px-3 rounded-full bg-primary-400 text-white font-medium hover:bg-primary-200 disabled:opacity-50 transition-colors"
                            >
                              {polygonsLoading ? 'Scanning…' : 'Re-Scan'}
                            </button>
                          </li>
                        ) : (
                          groupPopupListByLabel(detectedObjectsFromAPI, interiorTypesList, 'detected').map((grp, gIdx) => {
                          const allSelected = grp.indices.every((i) => selectedObjectIndices.has(i));
                          const showLowConfBadge = grp.indices.some((i) => {
                            const item = detectedObjectsFromAPI[i];
                            return (
                              item &&
                              ignoredLowConfidenceKeys.has(getInteriorStableKey(item, i))
                            );
                          });
                          return (
                            <li key={`det-${gIdx}-${grp.label}`} className="flex items-center gap-2 py-1.5 px-2 rounded-full">
                              {/*
                              <div className="flex w-5 shrink-0 items-center justify-center" aria-hidden={!showLowConfBadge}>
                                
                                {showLowConfBadge ? (
                                  <button
                                    type="button"
                                    onClick={() => handleLowConfidenceReview('choose')}
                                    className="inline-flex text-primary-200"
                                    title="Review all low confidence regions"
                                    aria-label="Open review list for low confidence regions"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      aria-hidden
                                    >
                                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                      <line x1="12" y1="9" x2="12" y2="13" />
                                      <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                  </button>
                                ) : null}
                              </div>*/}
                              <input
                                type="checkbox"
                                id={`obj-grp-${gIdx}`}
                                checked={allSelected}
                                onChange={() => {
                                  setSelectedObjectIndices((prev) => {
                                    const next = new Set(prev);
                                    if (allSelected) grp.indices.forEach((i) => next.delete(i));
                                    else grp.indices.forEach((i) => next.add(i));
                                    return next;
                                  });
                                }}
                                className="pr-object-checkbox"
                              />
                              <label htmlFor={`obj-grp-${gIdx}`} className="text-sm text-gray-800 cursor-pointer flex-1 truncate min-w-0">
                                {grp.label}{grp.indices.length > 1 ? ` (${grp.indices.length})` : ''}
                              </label>
                            </li>
                          );
                          })
                        )}
                      </ul>
                    </div>
                    {deletedObjectUndo && (
                      <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-200 shrink-0 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="truncate">
                            {deletedObjectUndo.batchItems
                              ? `${deletedObjectUndo.batchItems.length} detections were removed.`
                              : `${deletedObjectUndo.label} was removed.`}
                          </span>
                          <button
                            type="button"
                            className="text-sm font-medium text-gray-900 hover:text-black underline underline-offset-2 disabled:opacity-60 disabled:no-underline"
                            onClick={handleUndoDeletedObject}
                            disabled={deletedObjectUndo.restoring}
                          >
                            {deletedObjectUndo.restoring ? 'Undoing...' : 'Undo'}
                          </button>
                        </div>
                          <button
                            type="button"
                            className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors"
                            aria-label="Close notification"
                            title="Close notification"
                            onClick={() => setDeletedObjectUndo(null)}
                            disabled={deletedObjectUndo.restoring}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-2">
                      {!deleteDetectionMode && (
                        <button
                          type="button"
                          onClick={() => {
                            const selected = detectedObjectsFromAPI.filter((_, i) => selectedObjectIndices.has(i));
                            handleStartAddObjectFlow(selected);
                          }}
                          className="w-full py-2.5 px-4 rounded-full bg-white text-gray-700 font-medium hover:bg-primary-200 hover:text-white transition-colors flex items-center justify-center gap-2"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9.5625 0.5625L1.7625 0.562499 C1.3425 0.562499 1.1325 0.562499 0.972 0.644249 C0.830884 0.716153 0.716153 0.830884 0.644249 0.971999 C0.562499 1.1325 0.5625 1.3425 0.5625 1.761 L0.5625 9.5625 M6.5625 8.8125L8.8125 8.8125 M8.8125 8.8125L11.0625 8.8125 M8.8125 8.8125L8.8125 6.5625 M8.8125 8.8125L8.8125 11.0625 M5.9625 3.5625L11.6625 3.5625 C12.5025 3.5625 12.9225 3.5625 13.2435 3.726 C13.5262 3.87 13.755 4.09875 13.899 4.3815 C14.0625 4.7025 14.0625 5.1225 14.0625 5.9625 L14.0625 11.6625 C14.0625 12.5025 14.0625 12.9225 13.899 13.2435 C13.7552 13.5257 13.5257 13.7552 13.2435 13.899 C12.9225 14.0625 12.5025 14.0625 11.6625 14.0625 L5.9625 14.0625 C5.1225 14.0625 4.7025 14.0625 4.3815 13.899 C4.09927 13.7552 3.86981 13.5257 3.726 13.2435 C3.5625 12.9225 3.5625 12.504 3.5625 11.6655 L3.5625 5.96025 C3.5625 5.12175 3.5625 4.70175 3.726 4.3815 C3.86981 4.09927 4.09927 3.86981 4.3815 3.726 C4.7025 3.5625 5.1225 3.5625 5.9625 3.5625Z" />
                          </svg>
                          Add New
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={selectedObjectIndices.size === 0}
                        onClick={async () => {
                          if (deleteDetectionMode) {
                            await handleDeleteSelectedDetectionsFromPopup();
                            return;
                          }
                          const selectedDetected = detectedObjectsFromAPI.filter((_, i) =>
                            selectedObjectIndices.has(i)
                          );
                          const combined = [...selectedDetected];
                          setPolygons(combined);
                          setShowPolygonsOverlay(true);
                          setPendingDetectedObjects(null);
                          selectObjectsSnapshotRef.current = null;
                          setObjectChoiceStep('initial');
                          const toPersist = [...detectedObjectsFromAPI, ...userDefinedObjects];
                          const hasNewUserObjects = userDefinedObjects.length > 0;
                          if (
                            toPersist.length > 0 &&
                            roomId != null &&
                            roomImageId != null &&
                            !(!interiorsFromExisting && interiorsSavedOnce && !hasNewUserObjects)
                          ) {
                            try {
                              const saved = await persistInteriors(toPersist);
                              if (Array.isArray(saved) && saved.length > 0) {
                                setDetectedObjectsFromAPI(saved);
                                setInteriorsFromExisting(true);
                                setUserDefinedObjects([]);
                                setSelectedUserDefinedIndices(new Set());
                                const withIds = combined.map((p) => ({
                                  ...p,
                                  id: p.id ?? getInteriorIdFromSaved(p, saved),
                                  interior_id: p.interior_id ?? p.id ?? getInteriorIdFromSaved(p, saved),
                                }));
                                setPolygons(withIds);
                                if (!interiorsFromExisting) {
                                  setInteriorsSavedOnce(true);
                                }
                              }
                            } catch (err) {
                              console.error('Failed to save interiors on confirm:', err);
                              setError(err?.message ?? 'Failed to save objects');
                            }
                          }
                        }}
                        className="w-full py-2.5 px-4 rounded-full bg-primary-400 text-white font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9.5625 0.5625L1.7625 0.562499 C1.3425 0.562499 1.1325 0.562499 0.972 0.644249 C0.830884 0.716153 0.716153 0.830884 0.644249 0.971999 C0.562499 1.1325 0.5625 1.3425 0.5625 1.761 L0.5625 9.5625 M6.5625 8.8125L8.8125 8.8125 M8.8125 8.8125L11.0625 8.8125 M8.8125 8.8125L8.8125 6.5625 M8.8125 8.8125L8.8125 11.0625 M5.9625 3.5625L11.6625 3.5625 C12.5025 3.5625 12.9225 3.5625 13.2435 3.726 C13.5262 3.87 13.755 4.09875 13.899 4.3815 C14.0625 4.7025 14.0625 5.1225 14.0625 5.9625 L14.0625 11.6625 C14.0625 12.5025 14.0625 12.9225 13.899 13.2435 C13.7552 13.5257 13.5257 13.7552 13.2435 13.899 C12.9225 14.0625 12.5025 14.0625 11.6625 14.0625 L5.9625 14.0625 C5.1225 14.0625 4.7025 14.0625 4.3815 13.899 C4.09927 13.7552 3.86981 13.5257 3.726 13.2435 C3.5625 12.9225 3.5625 12.504 3.5625 11.6655 L3.5625 5.96025 C3.5625 5.12175 3.5625 4.70175 3.726 4.3815 C3.86981 4.09927 4.09927 3.86981 4.3815 3.726 C4.7025 3.5625 5.1225 3.5625 5.9625 3.5625Z" />
                        </svg>
                        {deleteDetectionMode
                          ? `Confirm Delete (${selectedObjectIndices.size} selected)`
                          : `Confirm (${selectedObjectIndices.size} selected)`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
          </ObjectChoiceModal>
        )}
      </div>
    </div>
  );
};

export default Photorealistic;
